import { useState, useEffect, useRef } from 'react';
import { NodeInfoPanel } from './NodeInfoPanel';
import { useReactFlow, useNodes, useEdges, type Node } from 'reactflow';
import {
    Wand2, ArrowRight,
    Sparkles, BarChart, Settings2,
    Trash2, Plus, HelpCircle, GitMerge, Search, Info,
    LayoutGrid, LayoutList, Loader2, ArrowDown, ArrowUp, CopyMinus, Eye, Cpu, Check,
    MapPin, Coins, Calendar, Mail, BrainCircuit, Zap, Box, Trophy, Server,
    Bot, Terminal, Save, Clock, FileText, Sigma, Database, Edit, Upload, Play, FolderOpen // Added Play icon
} from 'lucide-react';
import { apiGet, apiPost, apiFetch } from '@/api';
import { RemoteStorageConfig } from './RemoteStorageConfig';
import { SourceNodeConfig } from './SourceNodeConfig';
import { FileSourceNodeConfig } from './FileSourceNodeConfig';
import { FileObjectNodeConfig } from './FileObjectNodeConfig';
import { RelaySourceConfig } from './RelaySourceConfig';
import { HubSpotNodeConfig } from './HubSpotNodeConfig';
import { RelaySinkConfig } from './RelaySinkConfig';
import { FileBrowserModal } from './FileBrowserModal';
import { HybridFileHandlerConfig } from './HybridFileHandlerConfig';

// API_BASE_URL removed â€“ all fetches must use the apiFetch() utility from '@/api'
// which resolves the correct base URL from VITE_API_BASE_URL at build time.

// DataSourceType enum from backend (integer â†’ label)
const DS_TYPE_LABELS: Record<number, string> = { 0: 'PostgreSQL', 1: 'MySQL', 2: 'SQLServer', 3: 'Oracle', 4: 'Snowflake', 5: 'BigQuery', 6: 'Redshift', 7: 'AzureSQL', 8: 'Synapse', 9: 'CSV', 10: 'Excel', 11: 'JSON', 12: 'Parquet', 13: 'DuckDB', 14: 'RestAPI', 15: 'MCP', 16: 'AzureBlob', 17: 'Salesforce' };
const DS_DB_TYPES = new Set([0, 1, 2, 3, 4, 5, 6, 7, 8, 13]); // database connection types
const isDatabaseConnection = (c: any) => {
    const t = typeof c.type === 'string' ? parseInt(c.type, 10) : c.type;
    return DS_DB_TYPES.has(t) || c.category === 'Database' || ['PostgreSQL', 'MySQL', 'SQLServer', 'Oracle'].includes(c.type);
};
const dsTypeLabel = (c: any) => {
    const t = typeof c.type === 'string' ? parseInt(c.type, 10) : c.type;
    return DS_TYPE_LABELS[t] || c.type || 'Unknown';
};
// @ts-ignore
import DataModelEditorModal from '@/modules/datamodels/DataModelEditorModal';
import { DataPreviewModal } from '@/components/common/DataPreviewModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/context/useAuth';
import { SqlEditor } from '@/components/ui/SqlEditor';
import { MappingReviewModal } from './MappingReviewModal';
import { DataProfileReport } from './nodes/DataProfileReport';
import { ApiRequestPanel } from './ApiRequestPanel';
import { SchemaForm } from './SchemaForm';
import { ScheduleNodeConfig } from './nodes/ScheduleNodeConfig';
import { WebhookPanel } from './nodes/WebhookPanel';
import { JoinConfigModal } from './JoinConfigModal';
import { SmartImputeConfig } from './nodes/SmartImputeConfig';
import { VisualConfig } from './nodes/VisualConfig';
import { TrainModelConfig } from './nodes/TrainModelConfig';
import { ScorePredictConfig } from './nodes/ScorePredictConfig';
import { TrainedModelConfig } from './nodes/TrainedModelConfig';
import { EvaluateModelConfig as EvaluateModelConfigNode } from './nodes/EvaluateModelConfig';

import { AutoTypeConfig } from './nodes/AutoTypeConfig';
import { ParetoConfig } from './nodes/ParetoConfig';

import { FetchDocumentConfig } from './nodes/FetchDocumentConfig';
import { EdiParserConfig } from './nodes/EdiParserConfig';
import { AiInsightConfig } from './nodes/AiInsightConfig';
import { AIProviderConfig } from './nodes/AIProviderConfig';
import { ServiceConnectionSelect } from './WorkflowAutomationConfigs';
import { SortNodeConfig } from './nodes/SortNodeConfig';
import { WindowNodeConfig } from './nodes/WindowNodeConfig';
import { DeduplicateConfig } from './nodes/DeduplicateNodeConfig';
import { UnionConfig } from './nodes/UnionNodeConfig';
import { AggregateNodeConfig } from './nodes/AggregateNodeConfig';
import { PivotNodeConfig } from './nodes/PivotNodeConfig';
import { UnpivotNodeConfig } from './nodes/UnpivotNodeConfig';
import { ColumnNodeConfig } from './nodes/ColumnNodeConfig';
import { MultiJoinNodeConfig } from './nodes/MultiJoinNodeConfig';
import {
    FileListenerConfig, DocumentParserConfig, DuplicateDetectorConfig, PythonScriptConfig, LoopConfig,
    DocumentGeneratorConfig, ContentMergerConfig, AISummarizerConfig, LLMPromptConfig, DatabaseLookupConfig, TemplateRendererConfig,
    ApprovalConfig, AIGenerateButton, FileOutputConfig, ConditionalRouterConfig, ConditionalSplitConfig, DataMapperConfig,
    SetVariableConfig, GetVariableConfig
} from './WorkflowAutomationConfigs';
// @ts-ignore
import UserInputNodePanel from './UserInputNodePanel';


const FUNCTION_CATS = [
    { name: 'Conditional', funcs: ['IF(cond, t, f)', 'CASE WHEN x THEN y END', 'COALESCE(a, b)', 'NULLIF(a, b)'] },
    { name: 'Conversion', funcs: ['CAST(x AS TYPE)', 'TRY_CAST(x AS TYPE)', 'TO_TIMESTAMP(d)', 'STRFTIME(d, fmt)', 'STRING(x)', 'TINYINT(x)', 'DOUBLE(x)'] },
    { name: 'DateTime', funcs: ['CURRENT_DATE', 'NOW()', 'DATE_TRUNC(\'part\', d)', 'DATE_PART(\'part\', d)', 'AGE(d)', 'DATE_ADD(d, INTERVAL x part)', 'DATE_DIFF(\'part\', a, b)'] },
    { name: 'Finance', funcs: ['(rate*pv)/(1-(1+rate)^(-n)) -- PMT', 'fv / (1 + rate)^n -- PV', 'pv * (1 + rate)^n -- FV'] },
    { name: 'Math', funcs: ['ABS(x)', 'ROUND(x, d)', 'CEIL(x)', 'FLOOR(x)', 'SQRT(x)', 'POWER(x, y)', 'EXP(x)', 'LN(x)', 'LOG(x)', 'MOD(a, b)'] },
    { name: 'Math: Bitwise', funcs: ['x & y', 'x | y', 'x # y (XOR)', 'x << n', 'x >> n', '~x', 'BIT_COUNT(x)'] },
    { name: 'Min/Max', funcs: ['LEAST(a, b, c)', 'GREATEST(a, b, c)'] },
    { name: 'Operators', funcs: ['+', '-', '*', '/', '%', '=', '<>', '>', '<', '>=', '<=', 'AND', 'OR', 'NOT', 'IN', 'IS NULL'] },
    { name: 'String', funcs: ['UPPER(s)', 'LOWER(s)', 'TRIM(s)', 'LENGTH(s)', 'SUBSTRING(s, i, len)', 'REPLACE(s, a, b)', 'REGEXP_REPLACE(s, p, r)', 'LEFT(s, n)', 'RIGHT(s, n)', 'REVERSE(s)', 'CONCAT(a, b)'] },
    { name: 'Magic (Advanced)', funcs: ['SMART_DATE(s) -- Try parse formats', 'MASK_EMAIL(s) -- a****@domain', 'GET_DOMAIN(url)', 'EXTRACT_DIGITS(s)'] }
];

const WINDOW_FUNCTIONS = [
    { value: 'ROW_NUMBER', label: 'Row Number' },
    { value: 'RANK', label: 'Rank' },
    { value: 'DENSE_RANK', label: 'Dense Rank' },
    { value: 'PERCENT_RANK', label: 'Percent Rank' },
    { value: 'NTILE', label: 'N-Tile (Buckets)' },
    { value: 'LEAD', label: 'Lead (Next Row)' },
    { value: 'LAG', label: 'Lag (Prior Row)' },
    { value: 'SUM', label: 'Running Sum' },
    { value: 'AVG', label: 'Running Average' },
    { value: 'MIN', label: 'Running Min' },
    { value: 'MAX', label: 'Running Max' },
    { value: 'FIRST_VALUE', label: 'First Value' },
    { value: 'LAST_VALUE', label: 'Last Value' }
];

const getToken = () => {
    try {
        const s = sessionStorage.getItem('access_token');
        if (s) return s;
        const t = localStorage.getItem('token') || localStorage.getItem('authToken');
        if (t) return t;
        const st = localStorage.getItem('auth-storage');
        if (st) return JSON.parse(st).state?.token || null;
    } catch { }
    return null;
};

const getStoredToken = getToken;
const getSelectedToken = () => getToken();

// --- Salesforce Helpers ---

const SalesforceObjectSelector = ({ dataSourceId, value, onChange }: any) => {
    const [objects, setObjects] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!dataSourceId) return;
        const fetchObjects = async () => {
            setLoading(true);
            try {
                const data = await apiFetch(`/datasources/${dataSourceId}/salesforce/objects`, null, { method: 'GET' });
                setObjects(Array.isArray(data) ? data : []);
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        fetchObjects();
    }, [dataSourceId]);

    return (
        <Select value={value || ''} onValueChange={onChange}>
            <SelectTrigger className="w-full text-sm">
                <SelectValue placeholder={loading ? "Loading..." : "Select Object"} />
            </SelectTrigger>
            <SelectContent className="max-h-[200px] bg-white z-50 shadow-lg border-gray-200">
                {objects.map(obj => (
                    <SelectItem key={obj} value={obj}>{obj}</SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
};

// ── Save as ETL Table — Data Model selector sub-component ───────────────────
const ETLModelOutputConfig = ({ data, updateData }: { data: any; updateData: (k: string, v: any) => void }) => {
    const [etlModels, setEtlModels] = useState<any[]>([]);
    const [etlMode, setEtlMode] = useState<'existing' | 'new'>(data.dataModelId ? 'existing' : 'new');
    const [etlLoading, setEtlLoading] = useState(false);
    const [newName, setNewName] = useState((!data.dataModelId && data.dataModelName) ? data.dataModelName : '');
    const [newDesc, setNewDesc] = useState(data.description || '');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    // Local copies of the selection so the dropdown shows the chosen value immediately
    // (parent `data` prop may not re-render synchronously after updateData)
    const [selectedId, setSelectedId] = useState<string>(data.dataModelId || '');
    const [selectedName, setSelectedName] = useState<string>(data.dataModelName || '');

    // ── Fetch ETL Flow models whenever we enter "existing" mode ──────────────
    const loadEtlModels = () => {
        setEtlLoading(true);
        apiFetch('/datamodels/?source_type=ETL_FLOW', null)
            .then((res: any) => setEtlModels(Array.isArray(res) ? res : []))
            .catch(() => setEtlModels([]))
            .finally(() => setEtlLoading(false));
    };

    useEffect(() => {
        if (etlMode !== 'existing') return;
        loadEtlModels();
    }, [etlMode]);

    // ── Save button handler — pre-registers the model immediately ─────────────
    const handleSaveNew = async () => {
        const name = newName.trim();
        if (!name) { setSaveError('Please enter a model name.'); return; }
        setSaving(true);
        setSaveError('');
        try {
            const created: any = await apiFetch('/datamodels/pre-register-etl', null, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, description: newDesc || undefined, data_layer: 'gold' }),
            });
            if (!created?.id) throw new Error('Unexpected response');

            // Commit to node data
            updateData('dataModelId', created.id);
            updateData('dataModelName', created.name);
            updateData('description', newDesc || undefined);
            setSelectedId(created.id);
            setSelectedName(created.name);

            // Switch to "existing" mode and show the newly created model selected
            setEtlModels(prev => {
                const already = prev.find((m: any) => m.id === created.id);
                return already ? prev.map((m: any) => m.id === created.id ? created : m) : [created, ...prev];
            });
            setEtlMode('existing');
        } catch (err: any) {
            const msg = err?.message || 'Failed to save. Please try again.';
            setSaveError(msg.includes('already exists') ? 'A model with this name already exists — pick it from "Add to Existing".' : msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-5">
            {/* Info Banner */}
            <div className="p-3 bg-sky-50 border border-sky-100 rounded-xl flex items-start gap-2.5">
                <Database className="h-4 w-4 text-sky-500 mt-0.5 shrink-0" />
                <div>
                    <p className="text-xs font-semibold text-sky-800">Save ETL Output as a Data Model</p>
                    <p className="text-[10px] text-sky-600 mt-0.5">
                        Results will be written as a <strong>Data Model</strong> table and
                        appear in the Data Models list.
                    </p>
                </div>
            </div>

            {/* Table Name (required) */}
            <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-1">
                    Table Name <span className="text-red-500">*</span>
                </label>
                <input
                    type="text"
                    className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-500/10 focus:outline-none transition-all font-mono"
                    placeholder="e.g. sales_summary"
                    value={data.tableName || ''}
                    onChange={e => updateData('tableName', e.target.value)}
                />
                <p className="text-[10px] text-gray-400">Becomes the physical table name (lowercase, no spaces).</p>
            </div>

            {/* Data Model — Existing / New toggle */}
            <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-1">
                    Data Model <span className="text-red-500">*</span>
                </label>

                {/* Toggle */}
                <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs font-semibold">
                    <button
                        type="button"
                        className={`flex-1 py-1.5 transition-colors ${etlMode === 'existing' ? 'bg-sky-500 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'
                            }`}
                        onClick={() => {
                            setSaveError('');
                            setEtlMode('existing');
                            if (!data.dataModelId) updateData('dataModelName', '');
                        }}
                    >
                        Add to Existing
                    </button>
                    <button
                        type="button"
                        className={`flex-1 py-1.5 transition-colors border-l border-gray-200 ${etlMode === 'new' ? 'bg-sky-500 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'
                            }`}
                        onClick={() => {
                            setEtlMode('new');
                            updateData('dataModelId', undefined);
                            setSaveError('');
                        }}
                    >
                        Create New
                    </button>
                </div>

                {/* ── Existing model dropdown ─────────────────────────────── */}
                {etlMode === 'existing' && (
                    <div className="space-y-1.5">
                        {etlLoading ? (
                            <div className="flex items-center gap-2 text-[11px] text-gray-400 py-2">
                                <span className="h-3 w-3 rounded-full border border-sky-400 border-t-transparent animate-spin inline-block" />
                                Loading ETL models…
                            </div>
                        ) : etlModels.length === 0 ? (
                            <div className="p-3 rounded-lg border border-dashed border-amber-300 bg-amber-50 text-[11px] text-amber-700">
                                No ETL Flow models found — switch to <strong>Create New</strong> to register one.
                            </div>
                        ) : (
                            <select
                                className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white focus:border-sky-400 focus:outline-none"
                                value={selectedId}
                                onChange={e => {
                                    const chosen = etlModels.find((m: any) => m.id === e.target.value);
                                    const id = chosen?.id ?? '';
                                    const name = chosen?.name ?? '';
                                    setSelectedId(id);
                                    setSelectedName(name);
                                    updateData('dataModelId', id || undefined);
                                    updateData('dataModelName', name);
                                }}
                            >
                                <option value="">— Select ETL model —</option>
                                {etlModels.map((m: any) => (
                                    <option key={m.id} value={m.id}>
                                        {m.name}
                                        {m.tables && m.tables.length > 0
                                            ? ` (${m.tables.length} table${m.tables.length !== 1 ? 's' : ''})`
                                            : ' (0 tables)'}
                                        {m.last_refresh_rows != null && m.last_refresh_rows > 0
                                            ? ` · ${m.last_refresh_rows.toLocaleString()} rows`
                                            : ' · 0 rows'}
                                    </option>
                                ))}
                            </select>
                        )}
                        {selectedId && (
                            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-sky-50 border border-sky-100">
                                <Database className="h-3 w-3 text-sky-500 shrink-0" />
                                <span className="text-[11px] text-sky-700 font-medium truncate">
                                    {selectedName || selectedId}
                                </span>
                                <span className="ml-auto text-[10px] text-sky-400 shrink-0">Selected ✓</span>
                            </div>
                        )}
                    </div>
                )}

                {/* ── Create New — name input + Save button ──────────────── */}
                {etlMode === 'new' && (
                    <div className="space-y-2">
                        <input
                            type="text"
                            className={`w-full text-sm px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:outline-none transition-all ${saveError ? 'border-red-300 focus:border-red-400 focus:ring-red-500/10' : 'border-gray-200 focus:border-sky-400 focus:ring-sky-500/10'
                                }`}
                            placeholder="e.g. Sales Summary"
                            value={newName}
                            onChange={e => { setNewName(e.target.value); setSaveError(''); }}
                            onKeyDown={e => { if (e.key === 'Enter') handleSaveNew(); }}
                        />

                        {saveError && (
                            <p className="text-[10px] text-red-500 font-medium">{saveError}</p>
                        )}

                        {/* Save & Register button */}
                        <button
                            type="button"
                            disabled={saving || !newName.trim()}
                            onClick={handleSaveNew}
                            className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold transition-all ${saving || !newName.trim()
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-sky-500 hover:bg-sky-600 text-white shadow-sm hover:shadow-md active:scale-[0.98]'
                                }`}
                        >
                            {saving ? (
                                <>
                                    <span className="h-3 w-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
                                    Saving…
                                </>
                            ) : (
                                <>
                                    <Save className="h-3 w-3" />
                                    Save & Register Model
                                </>
                            )}
                        </button>

                        <p className="text-[10px] text-gray-400">
                            Creates the Data Model card immediately with 0 rows. The flow run populates the data.
                        </p>

                        {/* Unsaved warning — shown when user typed a name but hasn't registered yet */}
                        {newName.trim() && !selectedId && (
                            <div className="flex items-start gap-1.5 p-2 rounded-lg bg-amber-50 border border-amber-200">
                                <span className="text-amber-500 text-xs mt-0.5">⚠</span>
                                <p className="text-[10px] text-amber-700 font-medium">
                                    Click <strong>Save &amp; Register Model</strong> to register this model before running the flow.
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Description (both modes) */}
            <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Data Model Description <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                    rows={2}
                    className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-500/10 focus:outline-none transition-all resize-none"
                    placeholder="What does this output represent?"
                    value={etlMode === 'new' ? newDesc : (data.description || '')}
                    onChange={e => {
                        if (etlMode === 'new') setNewDesc(e.target.value);
                        else updateData('description', e.target.value);
                    }}
                />
                <p className="text-[10px] text-gray-400">This description appears on the Data Model card.</p>
            </div>

        </div>
    );
};

// Shared full email connection form used by both EmailTriggerConfig and EmailListenerTriggerConfig
const EmailConnectionForm = ({ data, onChange, nodeLabel }: { data: any; onChange: (k: string, v: any) => void; nodeLabel?: string }) => {
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

    const authMethod = data.authMethod || 'basic';
    const token = getSelectedToken();

    const testConnection = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const json = await apiFetch('/flows/ops/email-trigger/test', null, {
                method: 'POST',
                body: JSON.stringify({ ...data }),
            });
            setTestResult(json);
        } catch (e: any) {
            setTestResult({ success: false, message: e.message || 'Network error' });
        } finally {
            setTesting(false);
        }
    };

    return (
        <div className="space-y-5">
            {/* Auth Method */}
            <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Authentication Method</label>
                <div className="flex gap-2">
                    {[{ v: 'basic', label: 'IMAP / Basic' }, { v: 'microsoft_graph', label: 'Microsoft 365' }].map(opt => (
                        <button
                            key={opt.v}
                            onClick={() => onChange('authMethod', opt.v)}
                            className={`flex-1 py-1.5 text-xs font-semibold rounded-lg border transition-all ${authMethod === opt.v
                                ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                                : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'
                                }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* IMAP fields */}
            {authMethod === 'basic' && (
                <div className="space-y-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">IMAP Connection</p>
                    <div className="grid grid-cols-3 gap-2">
                        <div className="col-span-2 space-y-1">
                            <label className="text-xs text-gray-600">IMAP Host</label>
                            <input
                                type="text"
                                className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-md bg-white focus:border-indigo-400 focus:outline-none"
                                value={data.host || ''}
                                onChange={e => onChange('host', e.target.value)}
                                placeholder="imap.gmail.com"
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs text-gray-600">Port</label>
                            <input
                                type="number"
                                className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-md bg-white focus:border-indigo-400 focus:outline-none"
                                value={data.port || 993}
                                onChange={e => onChange('port', parseInt(e.target.value) || 993)}
                            />
                        </div>
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs text-gray-600">Username / Email</label>
                        <input
                            type="text"
                            className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-md bg-white focus:border-indigo-400 focus:outline-none"
                            value={data.username || ''}
                            onChange={e => onChange('username', e.target.value)}
                            placeholder="user@company.com"
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs text-gray-600">Password / App Password</label>
                        <input
                            type="password"
                            className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-md bg-white focus:border-indigo-400 focus:outline-none"
                            value={data.password || ''}
                            onChange={e => onChange('password', e.target.value)}
                            placeholder="â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢"
                        />
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={data.useSSL !== false}
                            onChange={e => onChange('useSSL', e.target.checked)}
                            className="rounded"
                        />
                        <span className="text-xs text-gray-600">Use SSL/TLS</span>
                    </label>
                </div>
            )}

            {/* Microsoft Graph fields */}
            {authMethod === 'microsoft_graph' && (
                <div className="space-y-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-[10px] text-blue-600 font-medium uppercase tracking-wider mb-2">Microsoft 365 / Entra ID</p>

                    <ServiceConnectionSelect
                        service="ms_graph"
                        value={data.connectionId}
                        onChange={(v) => onChange('connectionId', v)}
                        label="Microsoft Graph Connection"
                    />

                    {(!data.connectionId || data.connectionId === 'new') && (
                        <>
                            <div className="space-y-1 mt-2">
                                <label className="text-xs text-gray-600">Tenant ID</label>
                                <input
                                    type="text"
                                    className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-md bg-white focus:border-indigo-400 focus:outline-none font-mono"
                                    value={data.msGraphTenantId || ''}
                                    onChange={e => onChange('msGraphTenantId', e.target.value)}
                                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs text-gray-600">Client ID (Application ID)</label>
                                <input
                                    type="text"
                                    className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-md bg-white focus:border-indigo-400 focus:outline-none font-mono"
                                    value={data.msGraphClientId || ''}
                                    onChange={e => onChange('msGraphClientId', e.target.value)}
                                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs text-gray-600">Client Secret</label>
                                <input
                                    type="password"
                                    className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-md bg-white focus:border-indigo-400 focus:outline-none"
                                    value={data.msGraphClientSecret || ''}
                                    onChange={e => onChange('msGraphClientSecret', e.target.value)}
                                    placeholder="â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢"
                                />
                            </div>
                        </>
                    )}

                    <div className="space-y-1 mt-2 pt-2 border-t border-blue-100">
                        <label className="text-xs text-gray-600">User / Mailbox Email</label>
                        <input
                            type="text"
                            className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-md bg-white focus:border-indigo-400 focus:outline-none"
                            value={data.msGraphUserEmail || ''}
                            onChange={e => onChange('msGraphUserEmail', e.target.value)}
                            placeholder="user@company.com"
                        />
                    </div>
                </div>
            )}

            {/* Folder & Filters */}
            <div className="space-y-3">
                <div className="space-y-1">
                    <label className="text-xs text-gray-600">Folder / Mailbox</label>
                    <input
                        type="text"
                        className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-md bg-white focus:border-indigo-400 focus:outline-none"
                        value={data.folder || (authMethod === 'basic' ? 'INBOX' : 'inbox')}
                        onChange={e => onChange('folder', e.target.value)}
                        placeholder={authMethod === 'basic' ? 'INBOX' : 'inbox'}
                    />
                </div>
                <div className="space-y-1">
                    <label className="text-xs text-gray-600">Max Emails Per Run</label>
                    <input
                        type="number"
                        min="1"
                        max="500"
                        className="w-24 text-xs px-2 py-1.5 border border-gray-200 rounded-md bg-white focus:border-indigo-400 focus:outline-none"
                        value={data.maxEmails || 10}
                        onChange={e => onChange('maxEmails', parseInt(e.target.value) || 10)}
                    />
                </div>
                <div className="space-y-1">
                    <label className="text-xs text-gray-600">Subject Filter <span className="text-gray-400 font-normal">(optional)</span></label>
                    <input
                        type="text"
                        className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-md bg-white focus:border-indigo-400 focus:outline-none"
                        value={data.subjectFilter || ''}
                        onChange={e => onChange('subjectFilter', e.target.value)}
                        placeholder="Contains text filterâ€¦"
                    />
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={data.onlyUnread !== false}
                        onChange={e => onChange('onlyUnread', e.target.checked)}
                        className="rounded"
                    />
                    <span className="text-xs text-gray-600">Only Unread Emails</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={!!data.downloadAttachments}
                        onChange={e => onChange('downloadAttachments', e.target.checked)}
                        className="rounded"
                    />
                    <span className="text-xs text-gray-600">Download Attachments</span>
                </label>
            </div>

            {/* Test Connection */}
            <div className="pt-2 border-t border-gray-100 space-y-2">
                <button
                    onClick={testConnection}
                    disabled={testing}
                    className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium disabled:opacity-50 transition-colors"
                >
                    {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
                    {testing ? 'Testingâ€¦' : 'Test Connection'}
                </button>
                {testResult && (
                    <div className={`text-xs p-2 rounded-lg border ${testResult.success
                        ? 'bg-green-50 border-green-200 text-green-800'
                        : 'bg-red-50 border-red-200 text-red-700'}`}>
                        {testResult.message}
                    </div>
                )}
            </div>

            {/* Output Schema reference */}
            <div className="pt-2 border-t border-gray-100 space-y-2">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Output Schema</label>
                <div className="bg-gray-50 rounded-lg border border-gray-200 divide-y divide-gray-200">
                    {['messageId:VARCHAR', 'subject:VARCHAR', 'sender:VARCHAR', 'body_text:VARCHAR', 'receivedAt:VARCHAR', 'has_attachments:BOOLEAN', 'attachments:VARCHAR'].map(col => {
                        const [name, type] = col.split(':');
                        return (
                            <div key={name} className="flex justify-between items-center px-2 py-1.5 text-xs">
                                <code className="font-mono text-pink-600 bg-pink-50 px-1 py-0.5 rounded">{name}</code>
                                <span className="text-gray-400">{type}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const EmailTriggerConfig = ({ data, onChange }: any) => (
    <EmailConnectionForm data={data} onChange={onChange} nodeLabel="Email Trigger" />
);


const EmailListenerTriggerConfig = ({ data, onChange }: any) => {
    return (
        <div className="space-y-4">
            <div className="p-3 bg-blue-50 text-blue-800 text-xs rounded-lg border border-blue-100 flex items-start gap-2">
                <Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                <div>
                    <span className="font-semibold block mb-1">System Trigger</span>
                    This node acts as the entry point for workflows triggered by the <strong>System Email Listener</strong>. Polling and deduplication are managed securely outside the canvas. Ensure your Listener in the `Automations` tab is pointing to this flow.
                </div>
            </div>

            <div>
                <Label className="text-sm font-semibold mb-2 block">Connection Settings</Label>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs text-gray-600 mb-1 block">Monitored Mailbox Address</Label>
                        <Input
                            value={data.monitored_mailbox || ''}
                            onChange={(e) => onChange('monitored_mailbox', e.target.value)}
                            placeholder="e.g. support@company.com"
                            className="bg-white text-gray-800 text-xs"
                        />
                        <p className="text-[10px] text-gray-500 mt-1">This workflow will only trigger for emails sent to this specific address. Ensure this account is connected in Tenant Integrations.</p>
                    </div>
                    <div>
                        <Label className="text-xs text-gray-600 mb-1 block">Number of Emails to Fetch (Preview Mode)</Label>
                        <Input
                            type="number"
                            min="1"
                            max="50"
                            value={data.fetch_limit || 1}
                            onChange={(e) => onChange('fetch_limit', parseInt(e.target.value) || 1)}
                            className="bg-white text-gray-800 text-xs w-24"
                        />
                        <p className="text-[10px] text-gray-500 mt-1">Only used during interactive Studio runs to pull a batch of real emails for testing.</p>
                    </div>
                </div>
            </div>

            <div>
                <Label className="text-sm font-semibold mb-2 block">Output Data Schema</Label>
                <p className="text-xs text-gray-500 mb-3">
                    When executed, this node outputs the incoming email payload as a data table with the following columns:
                </p>

                <div className="bg-gray-50 rounded-lg border border-gray-200 divide-y divide-gray-200">
                    <div className="flex justify-between items-center p-2 text-xs">
                        <code className="font-mono text-pink-600 bg-pink-50 px-1 py-0.5 rounded">messageId</code>
                        <span className="text-gray-500">VARCHAR</span>
                    </div>
                    <div className="flex justify-between items-center p-2 text-xs">
                        <code className="font-mono text-pink-600 bg-pink-50 px-1 py-0.5 rounded">subject</code>
                        <span className="text-gray-500">VARCHAR</span>
                    </div>
                    <div className="flex justify-between items-center p-2 text-xs">
                        <code className="font-mono text-pink-600 bg-pink-50 px-1 py-0.5 rounded">sender</code>
                        <span className="text-gray-500">VARCHAR</span>
                    </div>
                    <div className="flex justify-between items-center p-2 text-xs">
                        <code className="font-mono text-pink-600 bg-pink-50 px-1 py-0.5 rounded">body_text</code>
                        <span className="text-gray-500">VARCHAR</span>
                    </div>
                    <div className="flex justify-between items-center p-2 text-xs">
                        <code className="font-mono text-pink-600 bg-pink-50 px-1 py-0.5 rounded">receivedAt</code>
                        <span className="text-gray-500">VARCHAR</span>
                    </div>
                    <div className="flex justify-between items-center p-2 text-xs">
                        <code className="font-mono text-pink-600 bg-pink-50 px-1 py-0.5 rounded">cc_addresses</code>
                        <span className="text-gray-500">JSON String</span>
                    </div>
                    <div className="flex justify-between items-center p-2 text-xs">
                        <code className="font-mono text-pink-600 bg-pink-50 px-1 py-0.5 rounded">has_attachments</code>
                        <span className="text-gray-500">BOOLEAN</span>
                    </div>
                    <div className="flex justify-between items-center p-2 text-xs">
                        <code className="font-mono text-pink-600 bg-pink-50 px-1 py-0.5 rounded">attachments</code>
                        <span className="text-gray-500">JSON String</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const EmailAttributeFilterConfig = ({ data, onChange }: any) => {
    return (
        <div className="space-y-4">
            <div className="p-3 bg-orange-50 text-orange-800 text-xs rounded-lg border border-orange-100 flex items-start gap-2">
                <Info className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />
                <div>
                    <span className="font-semibold block mb-1">Attribute Filtering</span>
                    This node drops the entire email event if it doesn't match the condition. Enter a valid SQL WHERE condition testing the incoming columns (subject, sender, body_text, has_attachments).
                </div>
            </div>

            <div>
                <Label className="text-xs font-semibold text-gray-700 uppercase">SQL Condition</Label>
                <div className="mt-1">
                    <Input
                        value={data.condition_sql || ''}
                        onChange={(e) => onChange('condition_sql', e.target.value)}
                        placeholder="e.g. subject ILIKE '%Urgent%' AND has_attachments = true"
                        className="font-mono text-xs"
                    />
                </div>
                <p className="text-[10px] text-gray-500 mt-1">Uses DuckDB SQL syntax. Available cols: subject, sender, body_text, has_attachments.</p>
            </div>
        </div>
    );
};

const EmailPatternFilterConfig = ({ data, onChange }: any) => {
    const patterns = data.patterns || [];

    const addPattern = () => {
        onChange('patterns', [...patterns, { targetColumn: '', sourceField: 'body_text', regex: '' }]);
    };

    const updatePattern = (index: number, key: string, value: string) => {
        const newPatterns = [...patterns];
        newPatterns[index][key] = value;
        onChange('patterns', newPatterns);
    };

    const removePattern = (index: number) => {
        const newPatterns = patterns.filter((_: any, i: number) => i !== index);
        onChange('patterns', newPatterns);
    };

    return (
        <div className="space-y-4">
            <div className="p-3 bg-pink-50 text-pink-800 text-xs rounded-lg border border-pink-100 flex items-start gap-2">
                <Info className="h-4 w-4 text-pink-500 mt-0.5 shrink-0" />
                <div>
                    <span className="font-semibold block mb-1">Regex Extraction</span>
                    Use Regular Expressions to find matches in the email subject or body. Captured matches are saved as new columns.
                </div>
            </div>

            <div>
                <Label className="text-xs font-semibold text-gray-700 uppercase mb-2 block">Extraction Rules</Label>
                <div className="space-y-3">
                    {patterns.map((p: any, i: number) => (
                        <div key={i} className="p-3 bg-gray-50 border border-gray-200 rounded-lg relative">
                            <button
                                onClick={() => removePattern(i)}
                                className="absolute top-2 right-2 text-gray-400 hover:text-red-500"
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>
                            <div className="grid gap-3 mr-6">
                                <div>
                                    <Label className="text-[10px] text-gray-500 uppercase">Target Column Name</Label>
                                    <Input
                                        value={p.targetColumn || ''}
                                        onChange={(e) => updatePattern(i, 'targetColumn', e.target.value)}
                                        placeholder="e.g. claim_id"
                                        className="h-7 text-xs font-mono"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <Label className="text-[10px] text-gray-500 uppercase">Source Field</Label>
                                        <Select value={p.sourceField || 'body_text'} onValueChange={(v: string) => updatePattern(i, 'sourceField', v)}>
                                            <SelectTrigger className="h-7 text-xs">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="body_text">Body Text</SelectItem>
                                                <SelectItem value="subject">Subject</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="text-[10px] text-gray-500 uppercase">Regex Pattern</Label>
                                        <Input
                                            value={p.regex || ''}
                                            onChange={(e) => updatePattern(i, 'regex', e.target.value)}
                                            placeholder="\w+-\d+"
                                            className="h-7 text-xs font-mono"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <button
                    onClick={addPattern}
                    className="mt-3 flex items-center justify-center w-full py-2 border border-dashed border-gray-300 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                    <Plus className="h-4 w-4 mr-1" /> Add Extraction Rule
                </button>
            </div>
        </div>
    );
};

const AttachmentFilterConfig = ({ data, onChange }: any) => {
    return (
        <div className="space-y-4">
            <div className="p-3 bg-slate-50 text-slate-800 text-xs rounded-lg border border-slate-200 flex items-start gap-2">
                <Info className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
                <div>
                    <span className="font-semibold block mb-1">Attachment Filtering</span>
                    This node drops the entire email event if it doesn't contain an attachment matching the allowed extensions.
                </div>
            </div>

            <div>
                <Label className="text-xs font-semibold text-gray-700 uppercase">Allowed Extensions</Label>
                <div className="mt-1">
                    <Input
                        value={data.allowed_extensions || ''}
                        onChange={(e) => onChange('allowed_extensions', e.target.value)}
                        placeholder="e.g. pdf, docx, csv"
                        className="font-mono text-xs"
                    />
                </div>
                <p className="text-[10px] text-gray-500 mt-1">Comma-separated list of file extensions. Leave empty to allow any attachment.</p>
            </div>
        </div>
    );
};

const AttachmentProcessorConfig = ({ data, onChange }: any) => {
    return (
        <div className="space-y-4">
            <div className="p-3 bg-slate-50 text-slate-800 text-xs rounded-lg border border-slate-200 flex items-start gap-2">
                <Info className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
                <div>
                    <span className="font-semibold block mb-1">Attachment Extractor</span>
                    Extracts file attachments from the incoming email data and saves them to processing storage.
                    Outputs a table with `file_name` and `file_path` for downstream Document Parsers.
                </div>
            </div>
            {/* No specific configuration needed, purely an operational node */}
        </div>
    );
};

const EmailActionConfig = ({ data = {}, onChange, inputColumns = [], incomingNodes = [] }: any) => {
    return (
        <div className="space-y-4">
            <div>
                <Label>Provider Configuration</Label>
                <Select value={data.provider || 'default'} onValueChange={(v: any) => onChange('provider', v)}>
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                        <SelectItem value="default">Default Provider (Tenant Integrations)</SelectItem>
                        <SelectItem value="graph">Custom Microsoft Graph</SelectItem>
                        <SelectItem value="sendgrid">Custom SendGrid</SelectItem>
                        <SelectItem value="smtp">Custom SMTP</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {data.provider === 'default' && (
                <div className="p-3 bg-gray-50 text-gray-600 text-[10px] rounded-lg border border-gray-100 mb-2">
                    Using the global default email configuration set in your Tenant Integrations settings.
                </div>
            )}

            {data.provider === 'sendgrid' && (
                <div>
                    <Label className="text-xs">API Key</Label>
                    <Input
                        type="password"
                        value={data.api_key || ''}
                        onChange={(e: any) => onChange('api_key', e.target.value)}
                        placeholder="SG.xxxxxxxx..."
                    />
                </div>
            )}

            {data.provider === 'smtp' && (
                <div className="space-y-4 border p-3 rounded bg-gray-50">
                    <Label className="text-xs font-semibold text-gray-600">Custom SMTP Credentials</Label>
                    <ServiceConnectionSelect
                        service="smtp"
                        value={data.connectionId}
                        onChange={(v) => onChange('connectionId', v)}
                        label="SMTP Connection"
                    />
                    {(!data.connectionId || data.connectionId === 'new') && (
                        <div className="space-y-2 mt-2">
                            <Input placeholder="Host" value={data.smtp_host || ''} onChange={(e: any) => onChange('smtp_host', e.target.value)} />
                            <Input placeholder="Port" value={data.smtp_port || '587'} onChange={(e: any) => onChange('smtp_port', e.target.value)} />
                            <Input placeholder="Username" value={data.smtp_user || ''} onChange={(e: any) => onChange('smtp_user', e.target.value)} />
                            <Input type="password" placeholder="Password" value={data.smtp_pass || ''} onChange={(e: any) => onChange('smtp_pass', e.target.value)} />
                        </div>
                    )}
                </div>
            )}

            {data.provider === 'graph' && (
                <div className="space-y-4 border p-3 rounded bg-gray-50">
                    <Label className="text-xs font-semibold text-gray-600">Custom Graph Credentials</Label>
                    <ServiceConnectionSelect
                        service="ms_graph"
                        value={data.connectionId}
                        onChange={(v) => onChange('connectionId', v)}
                        label="Microsoft Graph Connection"
                    />
                    {(!data.connectionId || data.connectionId === 'new') && (
                        <div className="space-y-2 mt-2">
                            <Input placeholder="Tenant ID" value={data.graph_tenant_id || ''} onChange={(e: any) => onChange('graph_tenant_id', e.target.value)} />
                            <Input placeholder="Client ID" value={data.graph_client_id || ''} onChange={(e: any) => onChange('graph_client_id', e.target.value)} />
                            <Input type="password" placeholder="Client Secret" value={data.graph_client_secret || ''} onChange={(e: any) => onChange('graph_client_secret', e.target.value)} />
                        </div>
                    )}
                </div>
            )}

            <div className="h-px bg-gray-200 my-2" />

            {data.provider !== 'default' && (
                <div className="space-y-1">
                    <Label>From Address <span className="text-gray-400 font-normal">(Optional Override)</span></Label>
                    <Input value={data.from_address || ''} onChange={(e: any) => onChange('from_address', e.target.value)} placeholder="Leave blank to use verified Tenant Default" />
                    <p className="text-[10px] text-gray-400 leading-tight">If populated, this will aggressively override your outbound email settings.</p>
                </div>
            )}

            <div className="mt-2">
                <Label>To Address</Label>
                <Input value={data.to_address || ''} onChange={(e: any) => onChange('to_address', e.target.value)} placeholder="team@example.com" />
            </div>
            <div>
                <Label>Subject</Label>
                <Input value={data.subject || ''} onChange={(e: any) => onChange('subject', e.target.value)} placeholder="Daily Summary" />
            </div>
            <div>
                <Label>Attachment Column (Optional)</Label>
                <Input value={data.attachment_column || ''} onChange={(e: any) => onChange('attachment_column', e.target.value)} placeholder="e.g. file_path or attachments" />
                <p className="text-[10px] text-gray-500 mt-1">Column name from the input table containing the file path or JSON array of paths.</p>
            </div>
            <div>
                <Label>Body</Label>
                <div className="flex items-center gap-2 mb-2">
                    <input
                        type="checkbox"
                        checked={data.ai_enhanced || false}
                        onChange={(e) => onChange('ai_enhanced', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-xs text-gray-700">Generate Content with AI</span>
                </div>

                {data.ai_enhanced ? (
                    <div className="space-y-2 animate-in fade-in">
                        <Label className="text-xs text-gray-500">AI Prompt</Label>
                        <Textarea
                            value={data.ai_prompt || ''}
                            onChange={(e: any) => onChange('ai_prompt', e.target.value)}
                            placeholder="e.g. Write a summary email based on the attached data..."
                            rows={4}
                        />
                        <p className="text-[10px] text-gray-400">The AI will use this prompt and a sample of your data to generate the email body.</p>
                    </div>
                ) : (
                    <Textarea value={data.body || ''} onChange={(e: any) => onChange('body', e.target.value)} placeholder="Here is the attached report..." rows={5} />
                )}
            </div>

            {inputColumns && inputColumns.length > 0 && (
                <div className="mt-2 p-3 bg-indigo-50/50 rounded-lg border border-indigo-100">
                    <Label className="text-xs text-indigo-800 font-semibold mb-2 flex items-center gap-1">
                        <Info className="h-3.5 w-3.5" /> Available Variables
                    </Label>
                    <p className="text-[10px] text-indigo-600/80 mb-2 leading-tight">
                        Use <code className="bg-indigo-100 px-1 rounded font-mono text-indigo-700">{'{column_name}'}</code> syntax in Subject and unstructured Body to inject upstream data.
                    </p>
                    <div className="flex flex-wrap gap-1">
                        {inputColumns.map((col: any) => (
                            <div key={col.name} className="px-1.5 py-0.5 bg-white border border-indigo-200 text-indigo-700 rounded text-[10px] font-mono shadow-sm cursor-help" title={`Data type: ${col.type}`}>
                                {'{'}{col.name}{'}'}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* DATA PAYLOAD OPTIONS */}
            <div className="pt-4 border-t border-gray-100">
                {incomingNodes && incomingNodes.length > 1 && (
                    <div className="mb-3 space-y-1">
                        <Label className="text-xs text-gray-700 font-semibold">Data Table Source (Optional)</Label>
                        <Select value={data.attachment_source_node || 'auto'} onValueChange={(v: any) => onChange('attachment_source_node', v === 'auto' ? '' : v)}>
                            <SelectTrigger className="h-8 text-xs bg-gray-50 border-gray-200">
                                <SelectValue placeholder="Auto-Detect (Smart Routing)" />
                            </SelectTrigger>
                            <SelectContent className="bg-white z-50 shadow-lg">
                                <SelectItem value="auto">Auto-Detect (Smart Routing)</SelectItem>
                                {incomingNodes.map((n: any) => (
                                    <SelectItem key={n.id} value={n.id}>{n.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-[10px] text-gray-400">Explicitly choose which incoming node to use for attachments/HTML tables below.</p>
                    </div>
                )}
            </div>

            <div className="flex items-center gap-2 mt-2">
                <input
                    type="checkbox"
                    checked={data.include_data_table || false}
                    onChange={(e) => onChange('include_data_table', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="text-xs">
                    <span className="font-medium text-gray-700">Embed Data as HTML Table</span>
                    <p className="text-[10px] text-gray-400">Injects a clean HTML table of the preview data directly into the email body.</p>
                </div>
            </div>

            <div className="flex flex-col gap-2 mt-2">
                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        checked={data.attach_data || false}
                        onChange={(e) => onChange('attach_data', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="text-xs">
                        <span className="font-medium text-gray-700">Attach Dataset as File</span>
                        <p className="text-[10px] text-gray-400">Exports the incoming data and attaches it to the email.</p>
                    </div>
                </div>
                {data.attach_data && (
                    <div className="pl-6 pt-1">
                        <Label className="text-[10px] text-gray-500 mb-1 block">Attachment Format</Label>
                        <Select value={data.attachment_format || 'csv'} onValueChange={(v: any) => onChange('attachment_format', v)}>
                            <SelectTrigger className="h-7 text-xs bg-white w-32">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white z-50">
                                <SelectItem value="csv">CSV File</SelectItem>
                                <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                                <SelectItem value="json">JSON Lines</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                )}
            </div>


            <div className="flex items-center gap-2 mt-2">
                <input
                    type="checkbox"
                    checked={data.skip_if_empty || false}
                    onChange={(e) => onChange('skip_if_empty', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="text-xs">
                    <span className="font-medium text-gray-700">Skip if Input is Empty</span>
                    <p className="text-[10px] text-gray-400">Prevents sending an email if 0 records are received from upstream.</p>
                </div>
            </div>
        </div>
    );
};

const SalesforceFieldSelector = ({ dataSourceId, objectName, value, onChange, placeholder }: any) => {
    const [fields, setFields] = useState<string[]>([]);

    useEffect(() => {
        if (!dataSourceId || !objectName) return;
        const fetchFields = async () => {
            try {
                const data = await apiFetch(`/datasources/${dataSourceId}/salesforce/objects/${objectName}/fields`, null, { method: 'GET' });
                if (Array.isArray(data)) setFields(data);
            } catch (e) { console.error(e); }
        };
        fetchFields();
    }, [dataSourceId, objectName]);

    return (
        <Select value={value || ''} onValueChange={onChange}>
            <SelectTrigger className="w-full text-xs h-7">
                <SelectValue placeholder={placeholder || "Select Field"} />
            </SelectTrigger>
            <SelectContent className="max-h-[200px] bg-white z-50 shadow-lg border-gray-200">
                {fields.map(f => (
                    <SelectItem key={f} value={f}>{f}</SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
};

const SalesforceMappingEditor = ({ dataSourceId, objectName, inputColumns, mappings, onChange }: any) => {
    const [fields, setFields] = useState<string[]>([]);

    useEffect(() => {
        if (!dataSourceId || !objectName) return;
        // Re-fetch fields here or lift state up. For isolation, fetching again is safer.
        const fetchFields = async () => {
            try {
                const data = await apiFetch(`/datasources/${dataSourceId}/salesforce/objects/${objectName}/fields`, null, { method: 'GET' });
                if (Array.isArray(data)) {
                    setFields(data);

                    // Auto-Map if mappings are empty
                    if (mappings.length === 0 && inputColumns.length > 0) {
                        const newMappings: any[] = [];
                        inputColumns.forEach((col: any) => {
                            // Case-insensitive match
                            const match = data.find((f: string) => f.toLowerCase() === col.name.toLowerCase());
                            if (match) {
                                newMappings.push({ source: col.name, target: match });
                            }
                        });
                        if (newMappings.length > 0) onChange(newMappings);
                    }
                }
            } catch (e) { console.error(e); }
        };
        fetchFields();
    }, [dataSourceId, objectName]); // Depend on objectName to refresh fields

    const handleMapChange = (source: string, target: string) => {
        const newMappings = mappings.filter((m: any) => m.source !== source);
        if (target) {
            newMappings.push({ source, target });
        }
        onChange(newMappings);
    };

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden text-xs">
            <div className="flex bg-gray-50 border-b border-gray-200 p-2 font-medium text-gray-500">
                <div className="flex-1">Input Column</div>
                <div className="w-4 flex justify-center"><ArrowRight className="h-3 w-3" /></div>
                <div className="flex-1 pl-2">Salesforce Field</div>
            </div>
            <div className="max-h-[200px] overflow-y-auto bg-white">
                {inputColumns.map((col: any) => {
                    const currentMap = mappings.find((m: any) => m.source === col.name);
                    return (
                        <div key={col.name} className="flex items-center p-1.5 border-b border-gray-50 hover:bg-gray-50">
                            <div className="flex-1 truncate font-medium text-gray-700" title={col.name}>{col.name}</div>
                            <div className="w-4" />
                            <div className="flex-1">
                                <Select
                                    value={currentMap?.target || '_IGNORE_'}
                                    onValueChange={(val) => handleMapChange(col.name, val === '_IGNORE_' ? '' : val)}
                                >
                                    <SelectTrigger className="w-full h-6 text-[10px] px-1">
                                        <SelectValue placeholder="Map Field..." />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[200px] bg-white z-50 shadow-lg border-gray-200">
                                        <SelectItem value="_IGNORE_">(Skip)</SelectItem>
                                        {fields.map(f => (
                                            <SelectItem key={f} value={f}>{f}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Generic Table Mapping Editor
const TableMappingEditor = ({ dataSourceId, tableName, inputColumns, mappings, onChange, fetchSchema }: any) => {
    const [columns, setColumns] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);

    useEffect(() => {
        if (!dataSourceId || !tableName) return;
        const fetchTableSchema = async () => {
            setLoading(true);
            try {
                let tableCols: any[] = [];
                if (fetchSchema) {
                    tableCols = await fetchSchema(dataSourceId, tableName);
                } else {
                    // Legacy Fallback (should not be hit if prop passed)
                    const schema = await apiFetch(`/datasources/${dataSourceId}/schema`, null, { method: 'GET' });
                    if (Array.isArray(schema)) {
                        const table = (schema as any[]).find((t: any) => t.name === tableName);
                        if (table && Array.isArray(table.columns)) {
                            tableCols = table.columns;
                        }
                    }
                }

                if (tableCols.length > 0) {
                    setColumns(tableCols);

                    // Default Auto-Map by Name if empty
                    if ((!mappings || mappings.length === 0) && inputColumns.length > 0) {
                        const newMappings: any[] = [];
                        inputColumns.forEach((col: any) => {
                            const match = tableCols.find((c: any) => c.name.toLowerCase() === col.name.toLowerCase());
                            if (match) newMappings.push({ source: col.name, target: match.name });
                        });
                        if (newMappings.length > 0) onChange(newMappings);
                    }
                }
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        fetchTableSchema();
    }, [dataSourceId, tableName, fetchSchema]);

    const handleMapChange = (source: string, target: string) => {
        const current = mappings || [];
        const newMappings = current.filter((m: any) => m.source !== source);
        if (target) {
            newMappings.push({ source, target });
        }
        onChange(newMappings);
    };

    const handleAutoMapAI = async () => {
        if (inputColumns.length === 0 || columns.length === 0) return;
        setAiLoading(true);
        try {
            const suggestions = await apiFetch('/ai/automap-columns', null, {
                method: 'POST',
                body: JSON.stringify({
                    SourceColumns: inputColumns.map((c: any) => c.name),
                    TargetColumns: columns.map((c: any) => c.name)
                })
            });

            if (Array.isArray(suggestions) && suggestions.length > 0) {
                onChange(suggestions);
                toast.success(`AI Auto-Mapped ${suggestions.length} columns!`);
            } else {
                toast.info("AI could not find better mappings.");
            }
        } catch (e) {
            console.error(e);
            toast.error("AI Auto-Map failed.");
        } finally {
            setAiLoading(false);
        }
    };

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden text-xs">
            <div className="flex justify-between items-center bg-gray-50 border-b border-gray-200 p-2">
                <div className="flex-1 font-medium text-gray-500">Input Column</div>
                <div className="w-4"></div>
                <div className="flex-1 pl-2 font-medium text-gray-500">Target Column</div>
                <button
                    onClick={handleAutoMapAI}
                    className="ml-2 text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded hover:bg-purple-200 flex items-center gap-1"
                    disabled={loading || aiLoading}
                    title="Use AI to automatically map columns"
                >
                    {aiLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3 text-purple-600" />}
                    <span className="text-purple-700 font-medium">Auto</span>
                </button>
            </div>
            <div className="max-h-[200px] overflow-y-auto bg-white">
                {inputColumns.map((col: any) => {
                    const currentMap = (mappings || []).find((m: any) => m.source === col.name);
                    return (
                        <div key={col.name} className="flex items-center p-1.5 border-b border-gray-50 hover:bg-gray-50">
                            <div className="flex-1 truncate font-medium text-gray-700" title={col.name}>
                                {col.name} <span className="text-[9px] text-gray-400">({col.type})</span>
                            </div>
                            <div className="w-4 flex justify-center"><ArrowRight className="h-3 w-3 text-gray-300" /></div>
                            <div className="flex-1">
                                <Select
                                    value={currentMap?.target || '_IGNORE_'}
                                    onValueChange={(val) => handleMapChange(col.name, val === '_IGNORE_' ? '' : val)}
                                >
                                    <SelectTrigger className="w-full h-6 text-[10px] px-1 border-gray-200">
                                        <SelectValue placeholder={loading ? "Loading..." : "Ignore"} />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[200px] bg-white z-50 shadow-lg border-gray-200">
                                        <SelectItem value="_IGNORE_">(Ignore Column)</SelectItem>
                                        {columns.map((c: any) => (
                                            <SelectItem key={c.name} value={c.name}>
                                                {c.name} <span className="text-[9px] text-gray-400">({c.dataType})</span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// --- Date Intelligence Config ---
const DateConfig = ({ node, onChangeData, inputColumns }: any) => {
    const config = node.data.dateConfig || {};
    const operation = config.operation || 'extract';

    const updateConfig = (key: string, value: any) => {
        const newConfig = { ...config, [key]: value };
        onChangeData('dateConfig', newConfig);

        // Update Label for visibility
        if (key === 'operation') {
            onChangeData('label', `Date: ${value}`);
        }
    };

    return (
        <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                <Calendar className="h-3 w-3 text-blue-500" />
                Date Logic
            </h3>

            {/* Operation */}
            <div className="space-y-1">
                <label className="text-xs font-medium text-gray-500">Operation</label>
                <Select value={operation} onValueChange={(v) => updateConfig('operation', v)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                        <SelectItem value="extract">Extract Part</SelectItem>
                        <SelectItem value="diff">Date Difference</SelectItem>
                        <SelectItem value="add">Add Interval</SelectItem>
                        <SelectItem value="subtract">Subtract Interval</SelectItem>
                        <SelectItem value="startof">Start Of (Truncate)</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Source Column */}
            <div className="space-y-1">
                <label className="text-xs font-medium text-gray-500">Date Column</label>
                <Select value={config.sourceColumn || ''} onValueChange={(v) => updateConfig('sourceColumn', v)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select Column" /></SelectTrigger>
                    <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                        {inputColumns.map((c: any) => (
                            <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Dynamic Controls based on Operation */}

            {/* EXTRACT / STARTOF -> Part Selector */}
            {(operation === 'extract' || operation === 'startof') && (
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500">Part / Unit</label>
                    <Select value={config.part || 'YEAR'} onValueChange={(v) => updateConfig('part', v)}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                            <SelectItem value="YEAR">Year</SelectItem>
                            <SelectItem value="QUARTER">Quarter</SelectItem>
                            <SelectItem value="MONTH">Month</SelectItem>
                            {operation === 'extract' && <SelectItem value="DAY">Day</SelectItem>}
                            {operation === 'extract' && <SelectItem value="DOW">Day of Week</SelectItem>}
                            {operation === 'startof' && <SelectItem value="WEEK">Week</SelectItem>}
                        </SelectContent>
                    </Select>
                </div>
            )}

            {/* DIFF -> Operand Column & Unit */}
            {operation === 'diff' && (
                <>
                    <div className="space-y-1">
                        <label className="text-xs font-medium text-gray-500">Second Date Column</label>
                        <Select value={config.operandColumn || ''} onValueChange={(v) => updateConfig('operandColumn', v)}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select Column" /></SelectTrigger>
                            <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                {inputColumns.map((c: any) => (
                                    <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-medium text-gray-500">Difference Unit</label>
                        <Select value={config.unit || 'day'} onValueChange={(v) => updateConfig('unit', v)}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                <SelectItem value="day">Days</SelectItem>
                                <SelectItem value="month">Months</SelectItem>
                                <SelectItem value="year">Years</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </>
            )}

            {/* ADD/SUBTRACT -> Value & Unit */}
            {(operation === 'add' || operation === 'subtract') && (
                <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                        <label className="text-xs font-medium text-gray-500">Value</label>
                        <Input
                            type="number"
                            className="h-8 text-xs"
                            value={config.value || 0}
                            onChange={(e) => updateConfig('value', parseFloat(e.target.value))}
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-medium text-gray-500">Unit</label>
                        <Select value={config.unit || 'DAY'} onValueChange={(v) => updateConfig('unit', v)}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                <SelectItem value="DAY">Days</SelectItem>
                                <SelectItem value="MONTH">Months</SelectItem>
                                <SelectItem value="YEAR">Years</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            )}

            {/* Output Name */}
            <div className="space-y-1 pt-2 border-t border-gray-100">
                <label className="text-xs font-medium text-gray-500">Output Column Name</label>
                <Input
                    className="h-8 text-xs font-mono"
                    placeholder="New Column"
                    value={config.targetColumn || ''}
                    onChange={(e) => updateConfig('targetColumn', e.target.value)}
                />
            </div>

        </div>
    );
};

const DataModelConfig = ({ node, onChangeData, onEditModel }: any) => {
    return (
        <div className="space-y-4">
            {/* <h3 className="font-semibold text-gray-900 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                <Database className="h-3 w-3 text-blue-500" />
                Data Model Configuration
            </h3> */}

            <div className="p-3 bg-blue-50 rounded border border-blue-100 mb-4">
                <div className="text-xs text-blue-800 mb-2">
                    Define the semantic layer, relationships, and measures for this data.
                </div>
                <Button
                    size="sm"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => onEditModel(node.data.modelId)}
                >
                    <Edit className="w-3 h-3 mr-2" />
                    {node.data.modelId ? "Edit Data Model" : "Create Data Model"}
                </Button>
            </div>

            {node.data.modelId && (
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500">Linked Model ID</label>
                    <div className="text-xs font-mono bg-gray-50 p-1.5 rounded border text-gray-600 truncate">
                        {node.data.modelId}
                    </div>
                </div>
            )}
        </div>
    );
};




// --- Geolocation Config ---
const GeoConfig = ({ node, onChangeData, inputColumns }: any) => {
    const config = node.data.geoConfig || {};
    // Only support 'distance' for now
    // const operation = config.operation || 'distance'; 

    const updateConfig = (key: string, value: any) => {
        const newConfig = { ...config, [key]: value };
        onChangeData('geoConfig', newConfig);
    };

    return (
        <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                <MapPin className="h-3 w-3 text-rose-500" />
                Geolocation (Haversine)
            </h3>

            <div className="p-2 bg-rose-50 border border-rose-100 rounded text-[10px] text-rose-700 mb-2">
                Calculates the distance between two coordinate points.
            </div>

            {/* Point 1 */}
            <div className="space-y-2 border-b border-gray-100 pb-2">
                <label className="text-xs font-semibold text-gray-600">Point 1 (Origin)</label>
                <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                        <label className="text-[10px] text-gray-500">Latitude Col</label>
                        <Select value={config.lat1Column || ''} onValueChange={(v) => updateConfig('lat1Column', v)}>
                            <SelectTrigger className="h-7 text-[10px]"><SelectValue placeholder="Select..." /></SelectTrigger>
                            <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                {inputColumns.map((c: any) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] text-gray-500">Longitude Col</label>
                        <Select value={config.lon1Column || ''} onValueChange={(v) => updateConfig('lon1Column', v)}>
                            <SelectTrigger className="h-7 text-[10px]"><SelectValue placeholder="Select..." /></SelectTrigger>
                            <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                {inputColumns.map((c: any) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            {/* Point 2 */}
            <div className="space-y-2 border-b border-gray-100 pb-2">
                <label className="text-xs font-semibold text-gray-600">Point 2 (Destination)</label>
                <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                        <label className="text-[10px] text-gray-500">Latitude Col</label>
                        <Select value={config.lat2Column || ''} onValueChange={(v) => updateConfig('lat2Column', v)}>
                            <SelectTrigger className="h-7 text-[10px]"><SelectValue placeholder="Select..." /></SelectTrigger>
                            <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                {inputColumns.map((c: any) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] text-gray-500">Longitude Col</label>
                        <Select value={config.lon2Column || ''} onValueChange={(v) => updateConfig('lon2Column', v)}>
                            <SelectTrigger className="h-7 text-[10px]"><SelectValue placeholder="Select..." /></SelectTrigger>
                            <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                {inputColumns.map((c: any) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            {/* Options */}
            <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500">Unit</label>
                    <Select value={config.unit || 'km'} onValueChange={(v) => updateConfig('unit', v)}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                            <SelectItem value="km">Kilometers</SelectItem>
                            <SelectItem value="miles">Miles</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500">Output Name</label>
                    <Input
                        className="h-8 text-xs font-mono"
                        value={config.targetColumn || 'Distance'}
                        onChange={(e) => updateConfig('targetColumn', e.target.value)}
                    />
                </div>
            </div>

        </div>
    );
};


// --- Currency Config ---
const CurrencyConfig = ({ node, onChangeData, inputColumns, getNodes, getEdges }: any) => {
    const config = node.data.currencyConfig || {};

    const updateConfig = (key: string, value: any) => {
        const newConfig = { ...config, [key]: value };
        onChangeData('currencyConfig', newConfig);
    };

    // Helper: Get Parents
    const parentNodes = getEdges().filter((e: any) => e.target === node.id).map((e: any) => {
        return getNodes().find((n: any) => n.id === e.source);
    }).filter((n: any) => n);

    // Columns specific to selected Data/Rates node
    const dataDisplayCols = config.dataNodeId
        ? inputColumns.filter((c: any) => c.sourceId === config.dataNodeId)
        : inputColumns;

    const ratesDisplayCols = config.ratesNodeId
        ? inputColumns.filter((c: any) => c.sourceId === config.ratesNodeId)
        : inputColumns;

    return (
        <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                <Coins className="h-3 w-3 text-amber-600" />
                Currency Conversion
            </h3>

            <div className="grid grid-cols-2 gap-2 text-[10px]">
                {/* Data Input Source */}
                <div className="space-y-1">
                    <label className="text-gray-500 font-semibold">Data Input</label>
                    <Select value={config.dataNodeId || ''} onValueChange={(v) => updateConfig('dataNodeId', v)}>
                        <SelectTrigger className="h-7"><SelectValue placeholder="Source" /></SelectTrigger>
                        <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                            {parentNodes.map((n: any) => <SelectItem key={n.id} value={n.id}>{n.data.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                {/* Rates Input Source */}
                <div className="space-y-1">
                    <label className="text-gray-500 font-semibold">Rates Input</label>
                    <Select value={config.ratesNodeId || ''} onValueChange={(v) => updateConfig('ratesNodeId', v)}>
                        <SelectTrigger className="h-7"><SelectValue placeholder="Source" /></SelectTrigger>
                        <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                            {parentNodes.map((n: any) => <SelectItem key={n.id} value={n.id}>{n.data.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Data Columns */}
            <div className="space-y-2 border-t border-gray-100 pt-2">
                <label className="text-xs font-semibold text-gray-600">Transaction Columns</label>
                <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                        <label className="text-[10px] text-gray-500">Amount</label>
                        <Select value={config.amountColumn || ''} onValueChange={(v) => updateConfig('amountColumn', v)}>
                            <SelectTrigger className="h-7 text-[10px]"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-white z-50 shadow-lg border-gray-200">{dataDisplayCols.map((c: any) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] text-gray-500">Currency</label>
                        <Select value={config.sourceCurrencyColumn || ''} onValueChange={(v) => updateConfig('sourceCurrencyColumn', v)}>
                            <SelectTrigger className="h-7 text-[10px]"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-white z-50 shadow-lg border-gray-200">{dataDisplayCols.map((c: any) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1 col-span-2">
                        <label className="text-[10px] text-gray-500">Date (Transaction)</label>
                        <Select value={config.dateColumn || ''} onValueChange={(v) => updateConfig('dateColumn', v)}>
                            <SelectTrigger className="h-7 text-[10px]"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-white z-50 shadow-lg border-gray-200">{dataDisplayCols.map((c: any) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            {/* Rates Columns */}
            <div className="space-y-2 border-t border-gray-100 pt-2">
                <label className="text-xs font-semibold text-gray-600">Exchange Rate Columns</label>
                <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                        <label className="text-[10px] text-gray-500">Currency</label>
                        <Select value={config.rateCurrencyColumn || ''} onValueChange={(v) => updateConfig('rateCurrencyColumn', v)}>
                            <SelectTrigger className="h-7 text-[10px]"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-white z-50 shadow-lg border-gray-200">{ratesDisplayCols.map((c: any) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] text-gray-500">Rate Value</label>
                        <Select value={config.rateValueColumn || ''} onValueChange={(v) => updateConfig('rateValueColumn', v)}>
                            <SelectTrigger className="h-7 text-[10px]"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-white z-50 shadow-lg border-gray-200">{ratesDisplayCols.map((c: any) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1 col-span-2">
                        <label className="text-[10px] text-gray-500">Date (Rate)</label>
                        <Select value={config.rateDateColumn || ''} onValueChange={(v) => updateConfig('rateDateColumn', v)}>
                            <SelectTrigger className="h-7 text-[10px]"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-white z-50 shadow-lg border-gray-200">{ratesDisplayCols.map((c: any) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            {/* Target */}
            <div className="space-y-2 border-t border-gray-100 pt-2">
                <label className="text-xs font-semibold text-gray-600">Target</label>
                <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                        <label className="text-[10px] text-gray-500">Convert To</label>
                        <Input className="h-7 text-xs" value={config.targetCurrency || 'USD'} onChange={(e) => updateConfig('targetCurrency', e.target.value)} />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] text-gray-500">Output Name</label>
                        <Input className="h-7 text-xs" value={config.targetColumnName || 'ConvertedAmount'} onChange={(e) => updateConfig('targetColumnName', e.target.value)} />
                    </div>
                </div>
            </div>

        </div>
    );
};


// --- Email Helpers ---
const EmailConfig = ({ node, onChangeData }: any) => {
    return (
        <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                <Mail className="h-3 w-3 text-blue-500" />
                Email Settings
            </h3>

            <div className="space-y-1">
                <label className="text-xs font-medium text-gray-500">Operation</label>
                <Select value={node.data.operation || 'fetch'} onValueChange={(val) => onChangeData('operation', val)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                        <SelectItem value="fetch">Fetch Unread Emails</SelectItem>
                        <SelectItem value="search">Search Emails</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="space-y-1">
                <label className="text-xs font-medium text-gray-500">Subject Filter (Contains)</label>
                <Input
                    value={node.data.subjectFilter || ''}
                    onChange={(e) => onChangeData('subjectFilter', e.target.value)}
                    className="h-8 text-xs font-mono"
                    placeholder="e.g. [Invoice], Alert"
                />
            </div>

            <div className="space-y-1">
                <label className="text-xs font-medium text-gray-500">Attachment Filter</label>
                <div className="flex gap-2">
                    <Input
                        value={node.data.attachmentFilter || ''}
                        onChange={(e) => onChangeData('attachmentFilter', e.target.value)}
                        className="h-8 text-xs font-mono flex-1"
                        placeholder="Filename contains..."
                    />
                    <Input
                        value={node.data.allowedExtensions || ''}
                        onChange={(e) => onChangeData('allowedExtensions', e.target.value)}
                        className="h-8 text-xs font-mono w-20"
                        placeholder=".csv,.xlsx"
                    />
                </div>
                <p className="text-[10px] text-gray-400">Leave empty to download all attached files.</p>
            </div>

            <div className="pt-2 border-t border-gray-100">
                <h4 className="text-[10px] font-semibold text-gray-500 uppercase mb-2">Connection</h4>
                <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                        <label className="text-[10px] text-gray-400">Host</label>
                        <Input value={node.data.host || ''} onChange={(e) => onChangeData('host', e.target.value)} className="h-7 text-xs" placeholder="imap.outlook.com" />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] text-gray-400">Port</label>
                        <Input value={node.data.port || ''} onChange={(e) => onChangeData('port', e.target.value)} className="h-7 text-xs" placeholder="993" />
                    </div>
                </div>

                <div className="space-y-1 mt-2">
                    <label className="text-[10px] text-gray-400">Authentication Method</label>
                    <select
                        className="w-full text-xs p-1 border rounded bg-white text-gray-900 border-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        value={node.data.authMethod || 'Basic'}
                        onChange={(e) => onChangeData('authMethod', e.target.value)}
                    >
                        <option value="Basic">Basic (User/Pass)</option>
                        <option value="OAuth2">OAuth2 (Access Token)</option>
                    </select>
                </div>

                <div className="space-y-1 mt-2">
                    <label className="text-[10px] text-gray-400">Username / Email</label>
                    <Input value={node.data.username || ''} onChange={(e) => onChangeData('username', e.target.value)} className="h-7 text-xs" />
                </div>

                {(!node.data.authMethod || node.data.authMethod === 'Basic') && (
                    <div className="space-y-1 mt-2">
                        <label className="text-[10px] text-gray-400">Password / App Password</label>
                        <Input type="password" value={node.data.password || ''} onChange={(e) => onChangeData('password', e.target.value)} className="h-7 text-xs" />
                    </div>
                )}

                {node.data.authMethod === 'OAuth2' && (
                    <div className="space-y-1 mt-2">
                        <label className="text-[10px] text-gray-400">Access Token (Raw)</label>
                        <Input
                            type="password"
                            value={node.data.accessToken || ''}
                            onChange={(e) => onChangeData('accessToken', e.target.value)}
                            className="h-7 text-xs"
                            placeholder="ya29.a0..."
                        />
                        <p className="text-[9px] text-gray-400">
                            Provide a valid OAuth2 Access Token.
                            You must generate this externally (e.g. Google OAuth Playground).
                        </p>
                    </div>
                )}

                <div className="flex items-center space-x-2 mt-2">
                    <input
                        type="checkbox"
                        checked={node.data.useSsl !== false} // Default true
                        onChange={(e) => onChangeData('useSsl', e.target.checked)}
                        className="rounded text-indigo-600 focus:ring-indigo-500 h-3 w-3"
                    />
                    <span className="text-[10px] text-gray-600">Use SSL/TLS</span>
                </div>
            </div>

            <div className="pt-2 border-t border-gray-100">
                <h4 className="text-[10px] font-semibold text-gray-500 uppercase mb-2">Filters</h4>
                <div className="space-y-1">
                    <label className="text-[10px] text-gray-400">Subject Contains</label>
                    <Input value={node.data.subjectFilter || ''} onChange={(e) => onChangeData('subjectFilter', e.target.value)} className="h-7 text-xs" />
                </div>
                <div className="space-y-1 mt-1">
                    <label className="text-[10px] text-gray-400">Attachment Name Contains</label>
                    <Input value={node.data.attachmentFilter || ''} onChange={(e) => onChangeData('attachmentFilter', e.target.value)} className="h-7 text-xs" />
                </div>
                <div className="space-y-1 mt-1">
                    <label className="text-[10px] text-gray-400">Allowed Extensions</label>
                    <Input value={node.data.allowedExtensions || ''} onChange={(e) => onChangeData('allowedExtensions', e.target.value)} className="h-7 text-xs" placeholder=".csv,.xlsx" />
                </div>
                <div className="space-y-1 mt-1">
                    <label className="text-[10px] text-gray-400">Max Emails</label>
                    <Input type="number" value={node.data.maxCount || 10} onChange={(e) => onChangeData('maxCount', parseInt(e.target.value))} className="h-7 text-xs" />
                </div>
            </div>
        </div>
    );
};

const FlowSelector = ({ value, onChange }: any) => {
    const [flows, setFlows] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchFlows = async () => {
            setLoading(true);
            try {
                const data = await apiFetch('/flows', null, { method: 'GET' });
                setFlows(Array.isArray(data) ? data : []);
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        fetchFlows();
    }, []);

    return (
        <Select value={value || ''} onValueChange={onChange}>
            <SelectTrigger className="w-full text-sm">
                <SelectValue placeholder={loading ? "Loading..." : "Select Targeted Flow"} />
            </SelectTrigger>
            <SelectContent className="max-h-[300px] bg-white z-50 shadow-lg border-gray-200">
                {flows.map(f => (
                    <SelectItem key={f.id} value={f.id}>
                        {f.name || f.id}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
};

const FlowRunnerConfig = ({ data, onChange }: any) => {
    return (
        <div className="space-y-4">
            <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Sub-Flow</label>
                <FlowSelector value={data.subFlowId} onChange={(v: string) => onChange('subFlowId', v)} />
                <p className="text-[10px] text-gray-400">Select the flow to be executed by this step.</p>
            </div>

            <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Execution Mode</label>
                <Select value={data.waitMode || 'wait'} onValueChange={(v) => onChange('waitMode', v)}>
                    <SelectTrigger className="w-full text-sm">
                        <SelectValue placeholder="Select execution mode" />
                    </SelectTrigger>
                    <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                        <SelectItem value="wait">Wait (Synchronous)</SelectItem>
                        <SelectItem value="fireAndForget">Fire & Forget (Async)</SelectItem>
                    </SelectContent>
                </Select>
                <p className="text-[10px] text-gray-400">
                    <strong>Wait:</strong> Parent waits for completion. <strong>Fire & Forget:</strong> Parent continues immediately.
                </p>
            </div>

            <div className="space-y-2 border-t pt-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Runtime Overrides (JSON)</label>
                <Textarea
                    className="font-mono text-[10px] min-h-[100px]"
                    value={data.runtimeOverrides || '{}'}
                    onChange={(e) => onChange('runtimeOverrides', e.target.value)}
                    placeholder='{ "ParamName": "Value" }'
                />
                <p className="text-[10px] text-gray-400">Optional: Pass parameters to the sub-flow.</p>
            </div>
        </div>
    );
};

const BarrierConfig = ({ data, onChange }: any) => {
    return (
        <div className="space-y-4">
            <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Wait for Node ID</label>
                <Input
                    className="text-xs"
                    value={data.waitStepId || ''}
                    onChange={(e) => onChange('waitStepId', e.target.value)}
                    placeholder="e.g. node_1, node_2"
                />
                <p className="text-[10px] text-gray-400">Optional: Specify node ID(s) to wait for. Defaults to all upstream.</p>
            </div>
        </div>
    );
};

const ExecuteSqlConfig = ({ selectedNode, updateData, flowVariables }: any) => {
    const [connList, setConnList] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        const fetchConnections = async () => {
            setLoading(true);
            try {
                const data = await apiFetch('/datasources', null, { method: 'GET' });
                if (!cancelled) setConnList(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error('[ExecuteSqlConfig] Failed to load connections', e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchConnections();
        return () => { cancelled = true; };
    }, []);

    return (
        <div className="space-y-6">
            <div className="space-y-3">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Target Database</label>
                <select className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 focus:bg-white"
                    value={selectedNode.data.dataSourceId || ''}
                    onChange={(e) => updateData('dataSourceId', e.target.value)}
                >
                    <option value="">{loading ? 'Loading connections...' : 'Select Connection...'}</option>
                    {(connList.some(isDatabaseConnection) ? connList.filter(isDatabaseConnection) : connList).map((conn: any) => (
                        <option key={conn.id} value={conn.id}>{conn.name} ({dsTypeLabel(conn)})</option>
                    ))}
                </select>
            </div>

            <div className="space-y-3">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">SQL Command</label>
                <SqlEditor className="h-48 border" value={selectedNode.data.sql || ''} onChange={(val: string) => updateData('sql', val)} columns={[]} />
                <p className="text-[10px] text-gray-400">Use @ParamName for parameters.</p>
            </div>

            <div className="space-y-3">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Parameter Mapping</label>
                <div className="space-y-2">
                    {(selectedNode.data.parameters || []).map((param: any, idx: number) => (
                        <div key={idx} className="flex gap-2 items-center">
                            <input className="text-xs border p-1 rounded w-1/3" placeholder="@Param" value={param.key || ''} onChange={e => {
                                const newParams = [...(selectedNode.data.parameters || [])];
                                newParams[idx].key = e.target.value;
                                updateData('parameters', newParams);
                            }} />
                            <select className="text-xs border p-1 rounded w-1/2" value={param.varName || ''} onChange={e => {
                                const newParams = [...(selectedNode.data.parameters || [])];
                                newParams[idx].varName = e.target.value;
                                updateData('parameters', newParams);
                            }}>
                                <option value="">Select Variable...</option>
                                {flowVariables.map((v: any) => <option key={v.name} value={v.name}>{v.name}</option>)}
                            </select>
                            <button onClick={() => {
                                const newParams = [...(selectedNode.data.parameters || [])];
                                newParams.splice(idx, 1);
                                updateData('parameters', newParams);
                            }}><Trash2 className="h-3 w-3 text-gray-400 hover:text-red-500" /></button>
                        </div>
                    ))}
                    <Button size="sm" variant="outline" className="w-full text-xs h-7" onClick={() => {
                        const newParams = [...(selectedNode.data.parameters || [])];
                        newParams.push({ key: '', varName: '' });
                        updateData('parameters', newParams);
                    }}>+ Add Parameter</Button>
                </div>
            </div>

            <div className="space-y-3">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Result (Optional)</label>
                <div className="space-y-2">
                    <label className="text-[10px] text-gray-500">Store Output in Variable</label>
                    <select className="w-full text-sm border p-2 rounded bg-gray-50" value={selectedNode.data.resultVar || ''} onChange={e => updateData('resultVar', e.target.value)}>
                        <option value="">(None)</option>
                        {flowVariables.map((v: any) => <option key={v.name} value={v.name}>{v.name}</option>)}
                    </select>
                </div>
            </div>
        </div>
    );
};

const SqlDmlConfig = ({ data, onChange, inputColumns, rawConnections: rawConnectionsProp, datasetTables }: any) => {
    const [dbConnections, setDbConnections] = useState<any[]>(rawConnectionsProp || []);
    const [loadingConnections, setLoadingConnections] = useState(false);

    // Self-fetch on mount â€” mirrors DatabaseLookupConfig pattern so the dropdown
    // always shows even when the parent's rawConnections is empty.
    useEffect(() => {
        // If parent already supplied connections, use them immediately
        if (Array.isArray(rawConnectionsProp) && rawConnectionsProp.length > 0) {
            setDbConnections(rawConnectionsProp);
            return;
        }
        setLoadingConnections(true);
        apiGet('/datasources')
            .then((res: any[]) => { if (Array.isArray(res)) setDbConnections(res); })
            .catch(() => { })
            .finally(() => setLoadingConnections(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Also sync if parent prop later arrives
    useEffect(() => {
        if (Array.isArray(rawConnectionsProp) && rawConnectionsProp.length > 0) {
            setDbConnections(rawConnectionsProp);
        }
    }, [rawConnectionsProp]);

    return (
        <div className="space-y-4">
            <div>
                <Label className="text-xs font-semibold text-gray-700 uppercase">Connection</Label>
                <select
                    className="w-full mt-1 text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                    value={data.connectionId || ''}
                    onChange={(e) => {
                        const selected = dbConnections.find((c: any) => c.id === e.target.value);
                        console.log('[SqlDmlConfig] âœ“ Saving connectionId:', e.target.value, 'name:', selected?.name);
                        onChange({ connectionId: e.target.value, connectionName: selected?.name || '' });
                    }}
                    disabled={loadingConnections}
                >
                    <option value="">
                        {loadingConnections
                            ? 'Loading connections...'
                            : dbConnections.length === 0
                                ? 'No database connections available'
                                : 'Select a connection...'}
                    </option>
                    {dbConnections.map((c: any) => (
                        <option key={c.id} value={c.id}>
                            {c.name} ({c.type})
                        </option>
                    ))}
                </select>
                {data.connectionId && (
                    <p className="text-xs text-green-600 mt-1">
                        âœ“ Connected: {dbConnections.find((c: any) => c.id === data.connectionId)?.name || data.connectionId}
                    </p>
                )}
            </div>

            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold text-gray-700 uppercase">DML Query</Label>
                    <AIGenerateButton
                        contentType="SQL DML query"
                        placeholder="e.g. UPDATE Orders SET Status = 'Processed' WHERE Id = {{Id}}"
                        onGenerate={(text) => onChange('query', text)}
                        context={inputColumns?.map((c: any) => c.name).join(', ') || 'Use {{ColumnName}} for row values'}
                    />
                </div>
                <div className="h-64 border rounded overflow-hidden">
                    <SqlEditor
                        value={data.query || ''}
                        onChange={(v: string) => onChange('query', v)}
                        columns={[
                            ...inputColumns,
                            ...(datasetTables || []).map((t: any) => ({ name: t.name, dataType: 'TABLE' }))
                        ]}
                        className="w-full h-full"
                        placeholder="UPDATE TargetTable SET Status = 'Processed' WHERE Id = {{Id}}"
                    />
                </div>
                <div className="text-[10px] text-gray-500">
                    Supports <code>UPDATE</code> and <code>DELETE</code>. Use <code>{`{{ColumnName}}`}</code> for parameters.
                </div>
            </div>

            <div className="flex items-center space-x-2 border p-2 rounded bg-gray-50">
                <input
                    type="checkbox"
                    checked={data.batchExecution !== false}
                    onChange={(e) => onChange('batchExecution', e.target.checked)}
                    className="rounded text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                />
                <div>
                    <div className="text-xs font-medium text-gray-900">Batch Execution</div>
                    <div className="text-[10px] text-gray-500">Group updates for performance (if supported)</div>
                </div>
            </div>
        </div>
    );
};

const UserActionConfig = ({ data, onChange, inputColumns, rawConnections, datasetTables }: any) => {
    const { user } = useAuth();
    const [wbTables, setWbTables] = useState<any[]>([]);
    const [roleUsers, setRoleUsers] = useState<any[]>([]);
    const [loadingRoleUsers, setLoadingRoleUsers] = useState(false);

    const userRole = user?.role;

    const roleHierarchy = {
        'Super Admin': 0,
        'Platform Admin': 1,
        'Partner Admin': 2,
        'Tenant Admin': 3,
        'Support Admin': 4,
        'App Creator': 5,
        'Tenant User': 6,
    };

    const canSeeRole = (role: string) => {
        if (!userRole) return true;
        const tenantRoles = ['Tenant Admin', 'App Creator', 'Tenant User'];

        if (userRole === 'Tenant User' || userRole === 'Tenant Admin') {
            return tenantRoles.includes(role);
        }
        if (userRole === 'Partner Admin') {
            return [...tenantRoles, 'Partner Admin'].includes(role);
        }
        return true;
    };

    // Fetch users when Role is selected
    useEffect(() => {
        if (data.assignmentType === 'Role' && data.assignmentRole) {
            setLoadingRoleUsers(true);
            const token = localStorage.getItem('token') || localStorage.getItem('access_token') || '';
            apiFetch(`/users/by-role?role=${encodeURIComponent(data.assignmentRole)}`, token)
                .then(users => setRoleUsers(users || []))
                .catch(err => console.error("Failed to fetch users by role", err))
                .finally(() => setLoadingRoleUsers(false));
        } else {
            setRoleUsers([]);
        }
    }, [data.assignmentType, data.assignmentRole]);

    // Fetch tables when Write-Back Connection changes
    useEffect(() => {
        const connId = data.writeBackConfig?.connectionId;
        if (!connId) {
            setWbTables([]);
            return;
        }

        const fetchTables = async () => {
            try {
                const structure = await apiFetch(`/datasources/${connId}/structure`, null, { method: 'GET' });
                setWbTables(structure?.tables || []);
            } catch (e) {
                console.error("Failed to fetch tables for write-back", e);
            }
        };

        fetchTables();
    }, [data.writeBackConfig?.connectionId]);

    return (
        <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                User Task Configuration
            </h3>

            {/* Basic Info */}
            <div className="space-y-4 border-b pb-4">
                <div className="space-y-2">
                    <Label className="text-xs font-semibold text-gray-700 uppercase">Task Title</Label>
                    <Input
                        className="text-xs"
                        value={data.title || ''}
                        onChange={(e) => onChange('title', e.target.value)}
                        placeholder="e.g. Approve Invoice"
                    />
                </div>
                <div className="space-y-2">
                    <Label className="text-xs font-semibold text-gray-700 uppercase">Description</Label>
                    <Textarea
                        className="text-xs min-h-[60px]"
                        value={data.description || ''}
                        onChange={(e) => onChange('description', e.target.value)}
                        placeholder="Instructions for the assignee..."
                    />
                </div>
            </div>

            {/* Context Data (Show to User) */}
            <div className="space-y-4 border-b pb-4">
                <Label className="text-xs font-semibold text-gray-700 uppercase">Context Data (Show to User)</Label>
                <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50 p-2 space-y-1">
                    {inputColumns.map((col: any) => (
                        <div key={col.name} className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                className="rounded border-gray-300 text-violet-600 focus:ring-violet-500 h-4 w-4"
                                checked={(data.contextColumns || []).includes(col.name)}
                                onChange={(e) => {
                                    const current = data.contextColumns || [];
                                    const newCols = e.target.checked
                                        ? [...current, col.name]
                                        : current.filter((c: string) => c !== col.name);
                                    onChange('contextColumns', newCols); // Persist as contextColumns
                                }}
                            />
                            <span className="text-xs text-gray-700 truncate" title={col.name}>{col.name}</span>
                        </div>
                    ))}
                    {inputColumns.length === 0 && <div className="text-[10px] text-gray-400 italic">No input columns available.</div>}
                </div>
                <div className="flex justify-between items-center text-[10px]">
                    <div className="space-x-2">
                        <button className="text-violet-600 hover:underline" onClick={() => onChange('contextColumns', inputColumns.map((c: any) => c.name))}>
                            Select All
                        </button>
                        <button className="text-gray-400 hover:text-gray-600" onClick={() => onChange('contextColumns', [])}>
                            Clear
                        </button>
                    </div>
                </div>
                <p className="text-[10px] text-gray-400">Selected columns will be displayed to the assignee to help them make a decision.</p>
            </div>

            {/* Assignment Section */}
            <div className="space-y-4 border-b pb-4">
                <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold text-gray-700 uppercase">Assignment Strategy</Label>
                    <Select value={data.assignmentType || 'StaticUser'} onValueChange={(v) => onChange('assignmentType', v)}>
                        <SelectTrigger className="w-[120px] h-6 text-[10px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                            <SelectItem value="StaticUser">User (Email)</SelectItem>
                            <SelectItem value="Role">Role / Team</SelectItem>
                            {/* <SelectItem value="DynamicColumn">Dynamic (Column)</SelectItem> */}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-2">
                    <Label className="text-xs font-medium text-gray-500">
                    </Label>
                    {data.assignmentType === 'Role' ? (
                        <div className="space-y-2">
                            <Select
                                value={data.assignmentRole || ''}
                                onValueChange={(v) => onChange('assignmentRole', v)}
                            >
                                <SelectTrigger className="w-full text-xs h-8 bg-white"><SelectValue placeholder="Step 1: Select Role..." /></SelectTrigger>
                                <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                    {canSeeRole('Super Admin') && <SelectItem value="Super Admin">Super Admin</SelectItem>}
                                    {canSeeRole('Platform Admin') && <SelectItem value="Platform Admin">Platform Admin</SelectItem>}
                                    {canSeeRole('Partner Admin') && <SelectItem value="Partner Admin">Partner Admin</SelectItem>}
                                    {canSeeRole('Tenant Admin') && <SelectItem value="Tenant Admin">Tenant Admin</SelectItem>}
                                    {canSeeRole('Support Admin') && <SelectItem value="Support Admin">Support Admin</SelectItem>}
                                    <SelectItem value="App Creator">App Creator</SelectItem>
                                    <SelectItem value="Tenant User">Tenant User</SelectItem>
                                </SelectContent>
                            </Select>

                            {data.assignmentRole && (
                                <Select
                                    value={data.assignee || ''}
                                    onValueChange={(v) => onChange('assignee', v)}
                                    disabled={loadingRoleUsers}
                                >
                                    <SelectTrigger className="w-full text-xs h-8 bg-white">
                                        <SelectValue placeholder={loadingRoleUsers ? "Loading users..." : "Step 2: Select User..."} />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                        {roleUsers.length === 0 && !loadingRoleUsers && (
                                            <div className="text-xs p-2 text-gray-500">No active users found for this role.</div>
                                        )}
                                        {roleUsers.map(u => (
                                            <SelectItem key={u.email} value={`user:${u.email}`}>{u.name} ({u.email})</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                            <p className="text-[10px] text-gray-400">Directly assign the task to an individual user via their selected role hierarchy.</p>
                        </div>
                    ) : (
                        <Input
                            className="text-xs"
                            value={data.assignee ? data.assignee.replace(/^user:/, '') : ''}
                            onChange={(e) => onChange('assignee', `user:${e.target.value}`)}
                            placeholder="e.g. alice@company.com"
                        />
                    )}
                </div>
            </div>

            {/* Write-Back Section */}
            <div className="space-y-4">
                <div className="flex items-center space-x-2">
                    <input
                        type="checkbox"
                        id="wb-toggle"
                        className="rounded text-violet-600 focus:ring-violet-500 h-4 w-4"
                        checked={!!data.writeBackConfig}
                        onChange={(e) => onChange('writeBackConfig', e.target.checked ? { connectionId: '', tableName: '' } : null)}
                    />
                    <Label htmlFor="wb-toggle" className="text-xs font-semibold text-gray-700 uppercase cursor-pointer">
                        Enable Write-Back
                    </Label>
                </div>

                {data.writeBackConfig && (
                    <div className="space-y-3 p-3 bg-gray-50 rounded border border-gray-200 animate-in slide-in-from-top-2">
                        <p className="text-[10px] text-gray-500 mb-2">
                            Update a database record when the task completes.
                        </p>

                        {/* Connection */}
                        <div>
                            <Label className="text-[10px] text-gray-500 uppercase">Database Connection</Label>
                            <Select
                                value={data.writeBackConfig?.connectionId || ''}
                                onValueChange={(v) => onChange('writeBackConfig', { ...data.writeBackConfig, connectionId: v })}
                            >
                                <SelectTrigger className="w-full text-xs h-7 bg-white"><SelectValue placeholder="Select DB..." /></SelectTrigger>
                                <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                    {(rawConnections || []).filter(isDatabaseConnection).map((c: any) => (
                                        <SelectItem key={c.id} value={c.id}>{c.name} ({dsTypeLabel(c)})</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Table */}
                        <div>
                            <Label className="text-[10px] text-gray-500 uppercase">Target Table</Label>
                            <Select
                                value={data.writeBackConfig?.tableName || ''}
                                onValueChange={(v) => onChange('writeBackConfig', { ...data.writeBackConfig, tableName: v })}
                            >
                                <SelectTrigger className="w-full text-xs h-7 bg-white"><SelectValue placeholder="Select Table..." /></SelectTrigger>
                                <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                    {wbTables.map((t: any) => (
                                        <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const TRANSFORM_PRESETS: { id: string; label: string; prompt: string }[] = [
    { id: 'translate', label: 'Translate', prompt: 'Translate the following text to English. Preserve meaning and tone.' },
    { id: 'extract_json', label: 'Extract to JSON', prompt: 'Extract key fields (e.g. name, date, amount) as JSON. Use keys: name, date, amount, notes.' },
    { id: 'standardize', label: 'Standardize Format', prompt: 'Standardize the text: fix casing, punctuation, and consistent formatting.' },
    { id: 'parse_date', label: 'Parse Dates', prompt: 'Extract any dates and return in ISO format YYYY-MM-DD. If multiple, list comma-separated.' },
    { id: 'custom', label: 'Custom Instruction', prompt: '' },
];

const SmartTransformConfig = ({ data, onChange, inputColumns }: any) => {
    const handlePreset = (preset: typeof TRANSFORM_PRESETS[0]) => {
        onChange('transformPreset', preset.id);
        if (preset.prompt) onChange('prompt', preset.prompt);
    };

    return (
        <div className="space-y-6">
            <div className="p-4 bg-purple-50 border border-purple-100 rounded-xl space-y-2">
                <div className="flex items-center gap-2 text-purple-700 font-semibold text-sm">
                    <Sparkles className="h-4 w-4" />
                    AI Logic â€” Data Transformation
                </div>
                <p className="text-xs text-purple-600 leading-snug">
                    Transform data with a single instruction. Extract, translate, or standardize.
                </p>
            </div>

            <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Quick Presets</label>
                <div className="flex flex-wrap gap-1">
                    {TRANSFORM_PRESETS.filter(p => p.id !== 'custom').map((preset) => (
                        <button
                            key={preset.id}
                            type="button"
                            onClick={() => handlePreset(preset)}
                            className="text-[10px] px-2 py-1 rounded-full bg-purple-100 text-purple-700 hover:bg-purple-200 border border-purple-200"
                        >
                            {preset.label}
                        </button>
                    ))}
                </div>
            </div>

            <AIProviderConfig data={data} onChange={onChange} />

            <div className="space-y-3">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Input Context (Optional)</label>
                <p className="text-[10px] text-gray-500">If processing text, select the column containing the source text.</p>
                <select
                    className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg focus:border-purple-500 bg-gray-50 focus:bg-white"
                    value={data.inputColumn || ''}
                    onChange={(e) => onChange('inputColumn', e.target.value)}
                >
                    <option value="">(All Columns / File)</option>
                    {(inputColumns || []).map((col: any) => (
                        <option key={col.name} value={col.name}>{col.name}</option>
                    ))}
                </select>
            </div>

            <div className="space-y-2">
                <label className="flex items-start space-x-3 border p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer">
                    <input
                        type="checkbox"
                        checked={data.isFilePath === true}
                        onChange={(e) => onChange('isFilePath', e.target.checked)}
                        className="rounded text-purple-600 focus:ring-purple-500 h-4 w-4 mt-0.5"
                    />
                    <div>
                        <div className="text-xs font-semibold text-gray-900">Input is File Path</div>
                        <div className="text-[10px] text-gray-500">Read content (PDF, Text, Image) from path.</div>
                    </div>
                </label>
            </div>

            <div className="space-y-3">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Instruction</label>
                <p className="text-[10px] text-gray-500">Describe how to transform the data. Use presets above or write custom.</p>
                <textarea
                    className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg focus:border-purple-500 bg-gray-50 focus:bg-white min-h-[100px]"
                    value={data.prompt || ''}
                    onChange={(e) => onChange('prompt', e.target.value)}
                    placeholder="e.g. Extract the date and amount from the invoice text..."
                />
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Output Column</label>
                    <input
                        type="text"
                        className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg focus:border-purple-500 bg-gray-50 focus:bg-white"
                        value={data.outputColumn || 'ai_transform'}
                        onChange={(e) => onChange('outputColumn', e.target.value)}
                        placeholder="ai_transform"
                    />
                </div>
                <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Output Format</label>
                    <select
                        className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg focus:border-purple-500 bg-gray-50 focus:bg-white"
                        value={data.outputFormat || 'text'}
                        onChange={(e) => onChange('outputFormat', e.target.value)}
                    >
                        <option value="text">Plain text</option>
                        <option value="json">JSON</option>
                    </select>
                    <p className="text-[10px] text-gray-500">JSON = structured output</p>
                </div>
            </div>
        </div>
    );
};

const EdiSourceConfig = ({ data, onChange }: any) => {
    return (
        <div className="space-y-6">
            <div className="space-y-2">
                <label className="flex items-start space-x-3 border p-3 rounded-lg bg-blue-50/50 hover:bg-blue-50 transition-colors cursor-pointer border-blue-100">
                    <input
                        type="checkbox"
                        checked={data.isAppInput === true}
                        onChange={(e) => onChange('isAppInput', e.target.checked)}
                        className="rounded text-blue-600 focus:ring-blue-500 h-4 w-4 mt-0.5"
                    />
                    <div>
                        <div className="text-xs font-semibold text-blue-900">Mark as App Input</div>
                        <div className="text-[10px] text-blue-600">User will upload file for this input.</div>
                    </div>
                </label>
            </div>

            <div className="space-y-3">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">EDI FILE PATH</label>
                <input
                    type="text"
                    className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg focus:border-blue-500 bg-gray-50 focus:bg-white"
                    value={data.filePath || ''}
                    onChange={(e) => onChange('filePath', e.target.value)}
                    placeholder="/path/to/835.txt"
                />
                <p className="text-[10px] text-gray-500">Path to the X12 835 file.</p>
            </div>
        </div>
    );
};

const AIAgentConfig = ({ data, onChange, inputColumns = [] }: any) => {
    const [gateways, setGateways] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchGateways = async () => {
            setLoading(true);
            try {
                const list = await apiFetch('/gateways', null, { method: 'GET' });
                setGateways(Array.isArray(list) ? list : []);
            } catch (e) {
                console.error("Failed to fetch gateways", e);
            } finally {
                setLoading(false);
            }
        };
        fetchGateways();
    }, []);

    const AVAILABLE_TOOLS = [
        { id: 'web_search', name: 'Web Search', icon: Search },
        { id: 'calculator', name: 'Calculator', icon: Sigma },
        { id: 'read_query', name: 'Dataset Query (Summary-First)', icon: Database },
        { id: 'read_rows', name: 'Read Rows (Preview)', icon: Database },
        { id: 'get_schema', name: 'Get Table Schema', icon: FileText },
        { id: 'time', name: 'Time/Date', icon: Clock },
        { id: 'list_files', name: 'List Files', icon: FileText, localOnly: true },
        { id: 'read_file', name: 'Read File', icon: FileText, localOnly: true },
        { id: 'write_file', name: 'Write File', icon: Save, localOnly: true },
        { id: 'run_command', name: 'Run Command', icon: Terminal, localOnly: true },
    ];

    const toggleTool = (toolId: string) => {
        const currentTools = data.tools || [];
        const newTools = currentTools.includes(toolId)
            ? currentTools.filter((t: string) => t !== toolId)
            : [...currentTools, toolId];
        onChange('tools', newTools);
    };

    return (
        <div className="space-y-6">
            <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-lg text-xs text-indigo-800">
                <h4 className="font-semibold mb-1 flex items-center gap-1"><Bot className="h-3 w-3" /> AI Agent</h4>
                <p>Goal-driven: define a high-level goal. The agent receives full row data and produces a result. Tools below extend capabilities.</p>
            </div>

            <div className="space-y-3">
                <Label className="text-xs font-semibold text-gray-700 uppercase">Goal / Prompt</Label>
                <Textarea
                    className="w-full text-sm min-h-[80px] border border-gray-200 rounded-lg focus:border-indigo-500"
                    value={data.goal || ''}
                    onChange={(e) => onChange('goal', e.target.value)}
                    placeholder="e.g. Analyze sales trends and identify top 3 regions..."
                />
            </div>

            <div className="space-y-3">
                <Label className="text-xs font-semibold text-gray-700 uppercase flex justify-between">
                    <span>Execution Environment</span>
                    <span className="text-[10px] text-gray-400 font-normal">{loading ? 'Loading...' : `${gateways.length} available`}</span>
                </Label>
                <Select
                    value={data.contextData?.GatewayId || 'cloud'}
                    onValueChange={(val) => {
                        const newContext = { ...data.contextData, GatewayId: val === 'cloud' ? null : val };
                        onChange('contextData', newContext);
                    }}
                >
                    <SelectTrigger className="w-full text-sm">
                        <SelectValue placeholder="Select Environment" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-gray-200 shadow-lg z-[100]">
                        <SelectItem value="cloud">â˜ï¸ Cloud Backend (Default)</SelectItem>
                        {gateways.map((g) => (
                            <SelectItem key={g.id} value={g.id} disabled={!g.isOnline}>
                                <div className="flex items-center gap-2">
                                    <span>{g.isOnline ? 'ðŸŸ¢' : 'âšª'}</span>
                                    <span>{g.name}</span>
                                    {!g.isOnline && <span className="text-xs text-gray-400">(Offline)</span>}
                                </div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="space-y-3">
                <Label className="text-xs font-semibold text-gray-700 uppercase">Enabled Tools</Label>
                <div className="grid grid-cols-2 gap-2">
                    {AVAILABLE_TOOLS.map((tool) => {
                        const Icon = tool.icon;
                        const isSelected = (data.tools || []).includes(tool.id);
                        const isLocal = tool.localOnly;
                        const hasGateway = !!(data.contextData?.GatewayId);
                        const disabled = isLocal && !hasGateway;

                        return (
                            <div
                                key={tool.id}
                                onClick={() => !disabled && toggleTool(tool.id)}
                                className={`
                                    flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all text-xs
                                    ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50 border-gray-100' : ''}
                                    ${isSelected
                                        ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'}
                                `}
                            >
                                <Icon className={`h-3.5 w-3.5 ${isSelected ? 'text-indigo-600' : 'text-gray-400'}`} />
                                <span className="font-medium truncate">{tool.name}</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-700 uppercase">Output Column</Label>
                <Input
                    className="w-full text-sm"
                    value={data.outputColumn || 'agent_result'}
                    onChange={(e) => onChange('outputColumn', e.target.value)}
                    placeholder="agent_result"
                />
            </div>

            <AIProviderConfig data={data} onChange={onChange} />

            {inputColumns.length > 0 && (
                <div className="p-2 bg-gray-50 border border-gray-100 rounded-lg">
                    <p className="text-[10px] text-gray-500">Input columns per row: {inputColumns.map((c: any) => c.name).join(', ')}</p>
                </div>
            )}
        </div>
    );
};


const TeamsConfig = ({ data, onChange }: any) => (
    <div className="p-4 text-xs text-gray-500">
        <div className="font-semibold text-gray-700 mb-2">Microsoft Teams Config</div>
        <div className="mb-2">Message:</div>
        <Textarea
            className="w-full border rounded p-2 text-xs"
            value={data.message || ''}
            onChange={(e) => onChange('message', e.target.value)}
        />
        <div className="mt-2 text-[10px] text-gray-400">Webhook URL will be set in environment variables.</div>
    </div>
);

const ApiCallConfig = ({ data, onChange }: any) => {
    const normalizeHeaderRows = (): { key: string; value: string }[] => {
        if (Array.isArray(data.headers_rows)) {
            return data.headers_rows.map((row: any) => ({
                key: String(row?.key || ''),
                value: String(row?.value || ''),
            }));
        }

        if (data.headers && typeof data.headers === 'object') {
            return Object.entries(data.headers).map(([key, value]) => ({ key, value: String(value ?? '') }));
        }

        return [];
    };

    const headers: { key: string; value: string }[] = normalizeHeaderRows();

    const setHeaders = (rows: { key: string; value: string }[]) => {
        const cleanedRows = rows.map((r) => ({ key: r.key ?? '', value: r.value ?? '' }));
        const obj: Record<string, string> = {};
        cleanedRows.forEach((r) => {
            const trimmedKey = r.key.trim();
            if (trimmedKey) obj[trimmedKey] = r.value;
        });
        onChange({ headers_rows: cleanedRows, headers: obj });
    };

    const addHeader = () => setHeaders([...headers, { key: '', value: '' }]);
    const updateHeader = (i: number, field: 'key' | 'value', val: string) => {
        const next = headers.map((h, idx) => idx === i ? { ...h, [field]: val } : h);
        setHeaders(next);
    };
    const removeHeader = (i: number) => setHeaders(headers.filter((_, idx) => idx !== i));

    return (
        <div className="space-y-5">
            {/* Banner */}
            <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg flex items-center gap-2">
                <span className="text-blue-700">ðŸŒ</span>
                <div>
                    <div className="text-xs font-semibold text-blue-800">REST API Call</div>
                    <div className="text-[10px] text-blue-600">Make an HTTP request and optionally store the response as a table.</div>
                </div>
            </div>

            {/* URL + Method */}
            <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Method & URL</label>
                <div className="flex gap-2">
                    <select
                        className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-gray-50 w-24"
                        value={data.method || 'GET'}
                        onChange={(e) => onChange('method', e.target.value)}
                    >
                        {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(m => (
                            <option key={m} value={m}>{m}</option>
                        ))}
                    </select>
                    <input
                        type="url"
                        className="flex-1 text-xs border border-gray-200 rounded-lg px-3 py-1.5 bg-gray-50 focus:bg-white focus:border-blue-400"
                        value={data.url || ''}
                        onChange={(e) => onChange('url', e.target.value)}
                        placeholder="https://api.example.com/endpoint"
                    />
                </div>
                <p className="text-[10px] text-gray-500">ðŸ’¡ Use variables: <code className="bg-gray-100 px-1 rounded">{'{{var.date}}'}</code> or node outputs: <code className="bg-gray-100 px-1 rounded">{'{{field_name}}'}</code></p>
            </div>

            {/* Authentication */}
            <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Authentication</label>
                <select
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-1.5 bg-gray-50"
                    value={data.auth_type || 'none'}
                    onChange={(e) => onChange('auth_type', e.target.value)}
                >
                    <option value="none">None</option>
                    <option value="bearer">Bearer Token</option>
                    <option value="basic">Basic Auth</option>
                    <option value="api_key">API Key Header</option>
                </select>
                {data.auth_type === 'bearer' && (
                    <input
                        type="password"
                        className="w-full text-xs border border-gray-200 rounded-lg px-3 py-1.5 bg-gray-50"
                        placeholder="Bearer token..."
                        value={data.auth_token || ''}
                        onChange={(e) => onChange('auth_token', e.target.value)}
                    />
                )}
                {data.auth_type === 'basic' && (
                    <div className="grid grid-cols-2 gap-2">
                        <input type="text" className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-gray-50" placeholder="Username" value={data.auth_user || ''} onChange={(e) => onChange('auth_user', e.target.value)} />
                        <input type="password" className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-gray-50" placeholder="Password" value={data.auth_pass || ''} onChange={(e) => onChange('auth_pass', e.target.value)} />
                    </div>
                )}
                {data.auth_type === 'api_key' && (
                    <div className="grid grid-cols-2 gap-2">
                        <input type="text" className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-gray-50" placeholder="Header name" value={data.auth_key_name || 'X-API-Key'} onChange={(e) => onChange('auth_key_name', e.target.value)} />
                        <input type="password" className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-gray-50" placeholder="Key value" value={data.auth_key_value || ''} onChange={(e) => onChange('auth_key_value', e.target.value)} />
                    </div>
                )}
            </div>

            {/* Headers */}
            <div className="space-y-2">
                <div className="flex justify-between items-center">
                    <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Headers</label>
                    <button type="button" onClick={addHeader} className="text-[10px] text-blue-600 hover:underline">+ Add</button>
                </div>
                {headers.map((h, i) => (
                    <div key={i} className="flex gap-1 items-center">
                        <input className="flex-1 text-xs border border-gray-200 rounded px-2 py-1 bg-gray-50" placeholder="Key" value={h.key} onChange={(e) => updateHeader(i, 'key', e.target.value)} />
                        <input className="flex-1 text-xs border border-gray-200 rounded px-2 py-1 bg-gray-50" placeholder="Value" value={h.value} onChange={(e) => updateHeader(i, 'value', e.target.value)} />
                        <button type="button" onClick={() => removeHeader(i)} className="text-red-400 hover:text-red-600 text-xs px-1">âœ•</button>
                    </div>
                ))}
                {headers.length === 0 && <p className="text-[10px] text-gray-400">No custom headers â€” add above.</p>}
                <p className="text-[10px] text-gray-500">ðŸ’¡ For auth tokens use: <code className="bg-gray-100 px-1 rounded text-[9px]">Authorization: Zoho-oauthtoken {'{{var.token}}'}</code></p>
            </div>

            {/* Request Body */}
            {['POST', 'PUT', 'PATCH'].includes(data.method || 'GET') && (
                <div className="space-y-2">
                    <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        <input type="checkbox" checked={!!data.send_data} onChange={(e) => onChange('send_data', e.target.checked)} className="rounded" />
                        Send Input Data as Body
                    </label>
                    {!data.send_data && (
                        <>
                            <label className="text-[10px] text-gray-500">Static JSON Body (optional)</label>
                            <textarea
                                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 focus:bg-white focus:border-blue-400 font-mono min-h-[80px]"
                                placeholder='{"key": "value"}'
                                value={data.body || ''}
                                onChange={(e) => onChange('body', e.target.value)}
                            />
                        </>
                    )}
                </div>
            )}

            {/* Response */}
            <div className="space-y-2 border-t pt-3">
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    <input type="checkbox" checked={!!data.store_response} onChange={(e) => onChange('store_response', e.target.checked)} className="rounded" />
                    Store Response as Output Table
                </label>
                <p className="text-[10px] text-gray-400">When enabled, the JSON response is available as a table for downstream nodes.</p>

                {data.store_response && (
                    <>
                        <label className="text-[10px] text-gray-600 font-medium">Response Path (optional - auto-detects if empty)</label>
                        <input
                            type="text"
                            className="w-full text-xs border border-gray-200 rounded-lg px-3 py-1.5 bg-gray-50 focus:bg-white focus:border-blue-400 font-mono"
                            placeholder="e.g., response.result or data.items"
                            value={data.response_path || ''}
                            onChange={(e) => onChange('response_path', e.target.value)}
                        />
                        <p className="text-[10px] text-gray-400">
                            ðŸ’¡ Leave empty to auto-detect arrays, or specify path like:<br />
                            â€¢ <code className="bg-gray-100 px-1 rounded">response.result</code> for <code className="bg-gray-100 px-1 rounded">{'{"response": {"result": [...]}}'}</code><br />
                            â€¢ <code className="bg-gray-100 px-1 rounded">data</code> for <code className="bg-gray-100 px-1 rounded">{'{"data": [...]}'}</code><br />
                            â€¢ <code className="bg-gray-100 px-1 rounded">items.users</code> for nested paths
                        </p>
                    </>
                )}
            </div>

            {/* Timeout */}
            <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Timeout (seconds)</label>
                <input
                    type="number"
                    min={1} max={300}
                    className="w-24 text-xs border border-gray-200 rounded-lg px-3 py-1.5 bg-gray-50"
                    value={data.timeout_seconds || 30}
                    onChange={(e) => onChange('timeout_seconds', parseInt(e.target.value) || 30)}
                />
            </div>
        </div>
    );
};


const GenerateDocumentConfig = ({ data, onChange }: any) => (
    <div className="p-4 text-xs text-gray-500">
        <div className="font-semibold text-gray-700 mb-2">Generate Document</div>
        <div className="mb-2">Template Path:</div>
        <Input
            className="w-full border rounded p-2 text-xs"
            value={data.templatePath || ''}
            onChange={(e) => onChange('templatePath', e.target.value)}
        />
    </div>
);

const ForEachConfig = ({ data, onChange }: any) => (
    <div className="p-4 text-xs text-gray-500">
        <div className="font-semibold text-gray-700 mb-2">Loop Configuration</div>
        <div className="mb-2">Items (Array):</div>
        <Input
            className="w-full border rounded p-2 text-xs"
            placeholder="e.g. @{triggerBody()?.items}"
            value={data.items || ''}
            onChange={(e) => onChange('items', e.target.value)}
        />
    </div>
);

const PropertiesPanel = ({ flowVariables = [], connections = [], onShowLeaderboard, flowId, flowSchema = {}, onRunToNode, onNodeUpdate, selectedNode: propSelectedNode, previewData, showInfoPanel = false, readOnly = false }: { flowVariables?: any[], connections?: any[], onShowLeaderboard?: (nodeId: string) => void, flowId?: string, flowSchema?: any, onRunToNode?: (nodeId: string) => void, onNodeUpdate?: (id: string, data: any) => void, selectedNode?: Node | null, previewData?: any, showInfoPanel?: boolean, readOnly?: boolean }) => {
    const { setNodes, getNodes, getEdges, setEdges } = useReactFlow();
    const nodes = useNodes(); // Reactive nodes state
    const edges = useEdges(); // Reactive edges state
    const [selectedNode, setSelectedNode] = useState<Node | null>(null);
    const [selectedTab, setSelectedTab] = useState('data');

    // AI State for Split Node
    const [splitAiModal, setSplitAiModal] = useState<{ idx: number } | null>(null);
    const [splitAiPrompt, setSplitAiPrompt] = useState("");
    const [isSplitAiLoading, setIsSplitAiLoading] = useState(false);

    const handleAiGenerate = async () => {
        if (!splitAiModal || !splitAiPrompt) return;
        setIsSplitAiLoading(true);
        try {
            // Use apiFetch â€” reads sessionStorage.access_token + handles silent refresh
            const data = await apiFetch('/ai/transform', null, {
                method: 'POST',
                body: JSON.stringify({
                    Prompt: splitAiPrompt,
                    Columns: inputColumns.map((c: any) => ({ Name: c.name, DataType: c.type || 'String' })),
                    TaskType: 'Filter'
                })
            });
            if (data?.expression || data?.Expression) {
                const expr = data.expression || data.Expression;
                const newConds = [...(selectedNode?.data.conditions || [])];
                if (newConds[splitAiModal.idx]) {
                    newConds[splitAiModal.idx].expression = expr;
                    updateData('conditions', newConds);
                }
                setSplitAiModal(null);
                setSplitAiPrompt("");
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsSplitAiLoading(false);
        }
    };

    const handleBrowseFile = () => {
        toast.info("Server-side file browsing is coming soon. Please enter the absolute path manually.");
    };

    const [dataSources, setDataSources] = useState<any[]>([]); // DataSources = Datasets
    const [rawConnections, setRawConnections] = useState<any[]>(connections);

    // Helper to get upstream node names for MultiJoin
    const getMultiJoinInputs = () => {
        if (!selectedNode || !['multiJoin', 'MULTI_JOIN', 'MULTIJOIN'].includes(selectedNode.type || '')) return {};
        const incoming = edges.filter((e: any) => e.target === selectedNode.id);
        const map: any = {};
        incoming.forEach((e: any) => {
            const srcNode = nodes.find((n: any) => n.id === e.source);
            if (srcNode) {
                const data: any = srcNode.data || {};
                let label = data.table || data.tableName;

                // If file based, use filename
                if (!label && data.filePath) {
                    const parts = data.filePath.split(/[/\\]/);
                    label = parts[parts.length - 1];
                }

                // Fallback to label or ID
                label = label || data.label || data.sourceName || srcNode.id;

                if (e.targetHandle) map[e.targetHandle] = label;
            }
        });
        return map;
    };

    // Sync rawConnections with connections prop
    useEffect(() => {
        setRawConnections(connections);
    }, [connections]);

    // Self-fetch datasources as fallback if rawConnections is still empty after 1.5s
    // This handles ECS environments where the parent's fetch may fail or return empty
    useEffect(() => {
        if (connections.length > 0) return; // Parent already provided connections, no need to self-fetch
        const timer = setTimeout(async () => {
            if (rawConnections.length > 0) return; // Already populated by parent
            try {
                const fetched = await apiGet('/datasources');
                if (Array.isArray(fetched) && fetched.length > 0) {
                    console.log('[PropertiesPanel] Self-fetched datasources:', fetched.length, 'entries');
                    setRawConnections(fetched);
                } else {
                    console.warn('[PropertiesPanel] Self-fetch returned empty datasources. workspace_id may not match, or no connections created yet in this environment.');
                }
            } catch (err) {
                console.error('[PropertiesPanel] Failed to self-fetch datasources:', err);
            }
        }, 1500);
        return () => clearTimeout(timer);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const [previewModal, setPreviewModal] = useState<{ isOpen: boolean, dataSourceId?: string, objectName?: string }>({ isOpen: false });
    const [columnSearch, setColumnSearch] = useState("");

    // --- Visual Config ---
    const VisualConfig = () => (
        <div className="space-y-4">
            <div className="space-y-1">
                <label className="text-xs font-medium text-gray-500">Visualization Type</label>
                <div className="grid grid-cols-2 gap-2">
                    {['bar', 'line', 'area', 'pie', 'scatter'].map(t => (
                        <div
                            key={t}
                            onClick={() => updateData('visualType', t)}
                            className={`cursor-pointer px-3 py-2 rounded border flex items-center justify-center text-xs font-semibold uppercase ${selectedNode?.data.visualType === t ? 'border-purple-600 bg-purple-50 text-purple-700' : 'border-gray-200 hover:border-purple-300'}`}
                        >
                            {t}
                        </div>
                    ))}
                </div>
            </div>

            <div className="space-y-1">
                <label className="text-xs font-medium text-gray-500">X-Axis (Category)</label>
                <Select value={selectedNode?.data.xAxis || ""} onValueChange={(val) => updateData('xAxis', val)}>
                    <SelectTrigger><SelectValue placeholder="Select Column..." /></SelectTrigger>
                    <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                        {inputColumns.map((col: any) => (
                            <SelectItem key={col.name} value={col.name}>{col.name} ({col.type})</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="space-y-1">
                <label className="text-xs font-medium text-gray-500">Y-Axis (Value)</label>
                <Select value={selectedNode?.data.yAxis || ""} onValueChange={(val) => updateData('yAxis', val)}>
                    <SelectTrigger><SelectValue placeholder="Select Column..." /></SelectTrigger>
                    <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                        {/* Relaxed filtering to allow Int32, Single, etc or just show all if unsure */}
                        {inputColumns.map((col: any) => (
                            <SelectItem key={col.name} value={col.name}>{col.name} ({col.type})</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="space-y-1">
                <label className="text-xs font-medium text-gray-500">Legend / Group By (Optional)</label>
                <Select value={selectedNode?.data.group || ""} onValueChange={(val) => updateData('group', val)}>
                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                        <SelectItem value=" ">None</SelectItem>
                        {inputColumns.map((col: any) => (
                            <SelectItem key={col.name} value={col.name}>{col.name} ({col.type})</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="space-y-1">
                <label className="text-xs font-medium text-gray-500">Aggregation</label>
                <Select value={selectedNode?.data.aggregation || "none"} onValueChange={(val) => updateData('aggregation', val)}>
                    <SelectTrigger><SelectValue placeholder="Function" /></SelectTrigger>
                    <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                        <SelectItem value="none">None (Raw)</SelectItem>
                        <SelectItem value="sum">Sum</SelectItem>
                        <SelectItem value="count">Count</SelectItem>
                        <SelectItem value="avg">Average</SelectItem>
                        <SelectItem value="min">Min</SelectItem>
                        <SelectItem value="max">Max</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="space-y-1 pt-2 border-t border-gray-100">
                <label className="text-xs font-medium text-gray-700 uppercase tracking-wider">Formatting</label>
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <label className="text-[10px] text-gray-500">Format</label>
                        <Select value={selectedNode?.data.formatType || "none"} onValueChange={(val) => updateData('formatType', val)}>
                            <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="None" /></SelectTrigger>
                            <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                <SelectItem value="none">Number</SelectItem>
                                <SelectItem value="currency">Currency</SelectItem>
                                <SelectItem value="percent">Percent</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-[10px] text-gray-500">Units</label>
                        <Select value={selectedNode?.data.units || "auto"} onValueChange={(val) => updateData('units', val)}>
                            <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Auto" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="auto">Auto</SelectItem>
                                <SelectItem value="none">None</SelectItem>
                                <SelectItem value="K">Thousands (K)</SelectItem>
                                <SelectItem value="M">Millions (M)</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {selectedNode?.data.formatType === 'currency' && (
                    <div className="grid grid-cols-2 gap-2 mt-1">
                        <div>
                            <label className="text-[10px] text-gray-500">Currency</label>
                            <Select value={selectedNode?.data.currency || "USD"} onValueChange={(val) => updateData('currency', val)}>
                                <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="USD" /></SelectTrigger>
                                <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                    <SelectItem value="USD">USD ($)</SelectItem>
                                    <SelectItem value="EUR">EUR (â‚¬)</SelectItem>
                                    <SelectItem value="GBP">GBP (Â£)</SelectItem>
                                    <SelectItem value="INR">INR (â‚¹)</SelectItem>
                                    <SelectItem value="JPY">JPY (Â¥)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                )}

                <div className="mt-1">
                    <label className="text-[10px] text-gray-500">Decimals</label>
                    <Input
                        type="number"
                        min={0}
                        max={10}
                        value={selectedNode?.data.decimals ?? 0}
                        onChange={(e) => updateData('decimals', parseInt(e.target.value) || 0)}
                        className="h-7 text-xs"
                    />
                </div>
            </div>

            <div className="space-y-2">
                <label className="flex items-center space-x-2 border p-2 rounded bg-gray-50">
                    <input
                        type="checkbox"
                        checked={selectedNode?.data.showGrid === true}
                        onChange={(e) => updateData('showGrid', e.target.checked)}
                        className="rounded text-purple-600 focus:ring-purple-500 h-4 w-4"
                    />
                    <div>
                        <div className="text-xs font-medium text-gray-900">Show Grid Lines</div>
                    </div>
                </label>
                <label className="flex items-center space-x-2 border p-2 rounded bg-gray-50">
                    <input
                        type="checkbox"
                        checked={selectedNode?.data.showLabels === true}
                        onChange={(e) => updateData('showLabels', e.target.checked)}
                        className="rounded text-purple-600 focus:ring-purple-500 h-4 w-4"
                    />
                    <div>
                        <div className="text-xs font-medium text-gray-900">Show Data Labels</div>
                    </div>
                </label>
                <label className="flex items-center space-x-2 border p-2 rounded bg-gray-50">
                    <input
                        type="checkbox"
                        checked={selectedNode?.data.showLegend === true}
                        onChange={(e) => updateData('showLegend', e.target.checked)}
                        className="rounded text-purple-600 focus:ring-purple-500 h-4 w-4"
                    />
                    <div>
                        <div className="text-xs font-medium text-gray-900">Show Legend</div>
                    </div>
                </label>
            </div>

            <div className="space-y-1">
                <label className="text-xs font-medium text-gray-500">Sort By (Optional)</label>
                <div className="flex gap-2">
                    <Select value={selectedNode?.data.sortColumn || ""} onValueChange={(val) => updateData('sortColumn', val)}>
                        <SelectTrigger className="flex-1"><SelectValue placeholder="Sort Column..." /></SelectTrigger>
                        <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                            {inputColumns.map((col: any) => (
                                <SelectItem key={col.name} value={col.name}>{col.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <button
                        onClick={() => updateData('sortDirection', selectedNode?.data.sortDirection === 'DESC' ? 'ASC' : 'DESC')}
                        className="px-2 border rounded bg-gray-50 hover:bg-gray-100 text-xs font-medium text-gray-600 w-16"
                    >
                        {selectedNode?.data.sortDirection === 'DESC' ? 'DESC' : 'ASC'}
                    </button>
                </div>
            </div>

            <div className="space-y-1">
                <label className="text-xs font-medium text-gray-500">Color</label>
                <div className="flex gap-2 flex-wrap">
                    {['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#8dd1e1', '#ef4444'].map(c => (
                        <div
                            key={c}
                            onClick={() => updateData('color', c)}
                            className={`w-6 h-6 rounded-full cursor-pointer ${selectedNode?.data.color === c ? 'ring-2 ring-offset-1 ring-gray-400' : ''}`}
                            style={{ backgroundColor: c }}
                        />
                    ))}
                </div>
            </div>

            <div className="pt-2 border-t border-gray-100">
                <p className="text-[10px] text-gray-400">
                    * Run the flow to update the preview data.
                </p>
            </div>
        </div>
    );
    // --- State: Connections & Methods ---
    const [isReviewOpen, setIsReviewOpen] = useState(false);
    const [isJoinConfigOpen, setIsJoinConfigOpen] = useState(false);
    const [showProfile, setShowProfile] = useState(false);
    const [profileData, setProfileData] = useState<any>(null);
    const [profileLoading, setProfileLoading] = useState(false);

    // Sync props
    useEffect(() => {
        if (connections.length > 0) setRawConnections(connections);
    }, [connections]);

    const [datasetTables, setDatasetTables] = useState<any[]>([]);

    // For Filter & Clean & Formula
    const [inputColumns, setInputColumns] = useState<any[]>([]);

    // For Destination node table picker
    const [destTables, setDestTables] = useState<string[]>([]);
    const [destTablesLoading, setDestTablesLoading] = useState(false);

    // Local draft for Node Name input â€” decoupled from reactive selectedNode syncs
    const [nodeLabelDraft, setNodeLabelDraft] = useState<string>('');
    const nodeIdRef = useRef<string | undefined>(undefined);   // tracks which node is "open"
    const isEditingLabelRef = useRef(false);                   // true while user is typing

    // Sync draft when a DIFFERENT node is selected, or when the draft hasn't been
    // initialized yet for the current node (React 18 batching edge-case on cold mount)
    useEffect(() => {
        const newId = selectedNode?.id;
        if (isEditingLabelRef.current) return; // never overwrite user's in-progress edit
        if (newId !== nodeIdRef.current) {
            // Switched to a different node â€” always update
            nodeIdRef.current = newId;
            setNodeLabelDraft(selectedNode?.data?.label || '');
        } else if (nodeLabelDraft === '' && selectedNode?.data?.label) {
            // Same node but draft wasn't initialized (e.g. React 18 batching on first mount)
            setNodeLabelDraft(selectedNode.data.label);
        }
    }, [selectedNode]); // watches full object so it runs after useEffect([nodes]) sets it

    useEffect(() => {
        // console.log("PropertiesPanel: inputColumns updated:", inputColumns);
    }, [inputColumns]);

    // UI State for Formula Builder
    const [showFuncHelp, setShowFuncHelp] = useState(false);
    // showInfoPanel is now controlled externally via prop

    // AI State
    const [aiPrompt, setAiPrompt] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [activeFormulaId, setActiveFormulaId] = useState<number | null>(null);

    const getToken = () => {
        // Primary: sessionStorage (set by api/index.js saveTokens)
        const sess = sessionStorage.getItem('access_token');
        if (sess) return sess;
        // Fallback: legacy localStorage paths
        return localStorage.getItem('token') || JSON.parse(localStorage.getItem('auth-storage') || '{}')?.state?.token || null;
    };


    const handleViewProfile = async () => {
        if (!selectedNode) return;
        setProfileLoading(true);
        try {
            const token = getToken();
            const flowGraph = {
                Nodes: nodes,
                Edges: edges,
                Variables: []
            };

            const result = await apiFetch('/dataprofile/analyze', token, {
                method: 'POST',
                body: JSON.stringify({
                    NodeId: selectedNode.id,
                    FlowGraph: flowGraph
                })
            });

            if (!result) throw new Error("Failed to analyze");
            setProfileData(result);
            setShowProfile(true);
        } catch (e) {
            console.error("Data Profile Error:", e);
        } finally {
            setProfileLoading(false);
        }
    };

    const getToken_old = () => {
        try {
            const storage = localStorage.getItem('auth-storage');
            if (storage) return JSON.parse(storage).state?.token || null;
        } catch (e) {
            console.error("Failed to parse auth token", e);
        }
        return null;
    };
    // getToken moved to top-level for consistency

    const generateExpression = async (taskType: string, index?: number) => {
        if (!aiPrompt.trim()) return;
        setIsGenerating(true);

        try {
            // Use apiFetch â€” it reads sessionStorage.access_token and handles silent refresh on 401
            const data = await apiFetch('/ai/transform', null, {
                method: 'POST',
                body: JSON.stringify({
                    Prompt: aiPrompt,
                    Columns: inputColumns.map(c => ({ Name: c.name, DataType: c.type })),
                    TaskType: taskType
                })
            });

            if (data?.expression) {
                let expr = data.expression;

                // Strip double quotes if the AI wrapped the entire SQL expression in them
                if (typeof expr === 'string' && expr.startsWith('"') && expr.endsWith('"') && expr.length >= 2) {
                    expr = expr.substring(1, expr.length - 1);
                }

                if (taskType === 'Filter') {
                    updateData('condition', expr);
                } else if (taskType === 'Formula' && typeof index === 'number') {
                    updateFormula(index, 'expression', expr);
                }
                setAiPrompt('');
            }
        } catch (e) {
            console.error("AI Generation Failed", e);
        } finally {
            setIsGenerating(false);
        }
    };

    const renderAiInput = (taskType: string, index?: number) => (
        <div className="flex gap-2 items-center mb-2 bg-purple-50 p-2 rounded-lg border border-purple-100">
            <Wand2 className="h-4 w-4 text-purple-600" />
            <input
                className="flex-1 text-[11px] bg-transparent border-none focus:outline-none text-purple-800 placeholder:text-purple-300"
                placeholder="Describe logic in English..."
                value={activeFormulaId === index || (taskType === 'Filter') ? aiPrompt : ''}
                onFocus={() => { if (typeof index === 'number') setActiveFormulaId(index); }}
                onChange={(e) => setAiPrompt(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') generateExpression(taskType, index);
                }}
            />
            <button
                className={`text - [10px] bg - purple - 600 text - white px - 2 py - 0.5 rounded shadow - sm hover: bg - purple - 700 disabled: opacity - 50`}
                onClick={() => generateExpression(taskType, index)}
                disabled={isGenerating || !aiPrompt.trim()}
            >
                {isGenerating ? '...' : 'Generate'}
            </button>
        </div>
    );



    // Trigger schema fetch when connectionId changes for Destination nodes
    useEffect(() => {
        if (selectedNode && (selectedNode.type === 'destination' || selectedNode.type === 'sink' || selectedNode.type === 'SINK' || selectedNode.type === 'output' || selectedNode.type === 'OUTPUT')) {
            if (selectedNode.data.connectionId) {
                fetchSchema(selectedNode.data.connectionId);
            } else {
                setDatasetTables([]);
            }
        }
    }, [selectedNode?.data.connectionId]);

    // Schema Cache state (Persist across renders)
    const schemaCache = useRef<Record<string, any[]>>({});

    const fetchSchema = async (dataSourceId: string, tableName?: string) => {
        if (!dataSourceId) return [];

        try {
            // Check Cache first
            let schema = schemaCache.current[dataSourceId];

            if (!schema) {
                // Fetch if not cached
                // console.log(`[Schema Cache] Miss for ${dataSourceId}, fetching...`);
                schema = await apiGet(`/datasources/${dataSourceId}/schema`);
                if (Array.isArray(schema)) {
                    schemaCache.current[dataSourceId] = schema;
                } else {
                    schema = [];
                }
            } else {
                console.log(`[Schema Cache] Hit for ${dataSourceId}`);
            }

            if (Array.isArray(schema) && schema.length > 0) {
                if (tableName) {
                    const table = schema.find((t: any) => t.name === tableName);
                    if (table && Array.isArray(table.columns)) return table.columns;
                } else {
                    // Start populating dataset tables if needed
                    setDatasetTables(schema);
                    return schema; // Return full schema for inspection
                }
            }

            // If empty array, it might be a data model ID, so we fall through to the catch-like block
            if (Array.isArray(schema) && schema.length === 0) {
                // Fall through to data model attempt
            } else {
                setDatasetTables([]);
                return [];
            }
        } catch (e) {
            console.error("[PropertiesPanel] DataSource schema fetch failed, trying Data Model fallback:", e);
        }

        // Try Data Model Fallback
        try {
            const model = await apiGet(`/datamodels/${dataSourceId}`);
            const tables = Array.isArray(model?.tables) ? model.tables : (model?.model_definition?.tables || []);
            if (tables.length > 0) {
                if (tableName) {
                    const table = tables.find((t: any) => (t.name === tableName) || (t.name && t.name.endsWith && t.name.endsWith(tableName)) || (tableName.endsWith(t.name)));
                    if (table && Array.isArray(table.columns) && table.columns.length > 0) {
                        return table.columns.map((c: any) => ({
                            name: c.name,
                            type: c.data_type || c.type || c.dataType || 'string'
                        }));
                    }
                    if (!table && tables.length > 0) {
                        const first = tables[0];
                        if (first && Array.isArray(first.columns) && first.columns.length > 0) {
                            return first.columns.map((c: any) => ({
                                name: c.name,
                                type: c.data_type || c.type || c.dataType || 'string'
                            }));
                        }
                    }
                } else {
                    setDatasetTables(tables);
                    return tables;
                }
            }
        } catch (err) {
            console.error("[PropertiesPanel] Data Model fetch failed:", err);
        }

        return [];
    };

    // Resolve Schema Helper for Joins
    const resolveSchema = async (nodeId: string): Promise<{ name: string, type: string, sourceId?: string }[]> => {
        const cache = new Map<string, any[]>();
        const visiting = new Set<string>();

        const resolveRecursive = async (currId: string): Promise<any[]> => {
            if (visiting.has(currId)) {
                console.warn("Cycle detected in schema resolution", currId);
                return [];
            }
            if (cache.has(currId)) return cache.get(currId)!;

            visiting.add(currId);

            const nodes = getNodes();
            const edges = getEdges();
            const targetNode = nodes.find(n => n.id === currId);
            if (!targetNode) {
                visiting.delete(currId);
                return [];
            }

            const type = targetNode.type?.toLowerCase();
            let result: any[] = [];

            // 1. If Source, File Source, or Data Model, fetch direct
            if (type === 'source' || type === 'datamodel' || type === 'data_model' || type === 'filesource' || type === 'file_source') {
                if (targetNode.data.outputSchema && Array.isArray(targetNode.data.outputSchema)) {
                    result = targetNode.data.outputSchema.map((c: any) => ({ ...c, name: c.name, type: c.dataType || c.type || 'string', sourceId: targetNode.id }));
                } else if (targetNode.data.columns && Array.isArray(targetNode.data.columns)) {
                    result = targetNode.data.columns.map((c: any) => ({ ...c, name: c.name, type: c.dataType || c.type || 'string', sourceId: targetNode.id }));
                } else {
                    const dsId = targetNode.data.dataModelId || targetNode.data.datasetId || targetNode.data.dataSourceId;
                    if (dsId) {
                        try {
                            if (targetNode.data.table) {
                                let cols = await fetchSchema(dsId, targetNode.data.table);
                                if ((!cols || cols.length === 0) && targetNode.data.table.includes('.')) {
                                    const shortName = targetNode.data.table.split('.').pop() || targetNode.data.table;
                                    cols = await fetchSchema(dsId, shortName);
                                }
                                if (cols && cols.length > 0) {
                                    result = cols.map((c: any) => ({ ...c, name: c.name, type: c.dataType || c.type || c.data_type || 'string', sourceId: targetNode.id }));
                                }
                            }
                            if (result.length === 0) {
                                const schema = await fetchSchema(dsId);
                                if (Array.isArray(schema) && schema.length > 0) {
                                    const tblName = targetNode.data.table;
                                    let table = tblName
                                        ? schema.find((t: any) => t.name === tblName || (t.name && String(t.name).endsWith(tblName)) || (tblName && String(tblName).endsWith(t.name)))
                                        : schema[0];
                                    if (!table) table = schema[0];
                                    if (table && Array.isArray(table.columns) && table.columns.length > 0) {
                                        result = table.columns.map((c: any) => ({ name: c.name, type: c.dataType || c.type || c.data_type || 'string', sourceId: targetNode.id }));
                                    }
                                }
                            }
                        } catch (e) { console.error(e); }
                    }
                }
            }
            // 1.5 File Listener (trigger - outputs file metadata)
            else if (type === 'filelistener' || type === 'file_listener') {
                result = [
                    { name: 'file_name', type: 'string', sourceId: targetNode.id },
                    { name: 'file_path', type: 'string', sourceId: targetNode.id },
                    { name: 'file_size', type: 'int64', sourceId: targetNode.id },
                    { name: 'modified_time', type: 'string', sourceId: targetNode.id },
                    { name: 'file_type', type: 'string', sourceId: targetNode.id },
                    { name: 'storage_type', type: 'string', sourceId: targetNode.id },
                    { name: 'is_new', type: 'boolean', sourceId: targetNode.id }
                ];
            }
            // 1.55 Email Trigger (outputs email payload columns)
            else if (type === 'emailtrigger' || type === 'email_trigger' || type === 'EMAILTRIGGER' || type === 'fetchemail') {
                result = [
                    { name: 'message_id', type: 'string', sourceId: targetNode.id },
                    { name: 'subject', type: 'string', sourceId: targetNode.id },
                    { name: 'sender', type: 'string', sourceId: targetNode.id },
                    { name: 'to_address', type: 'string', sourceId: targetNode.id },
                    { name: 'cc', type: 'string', sourceId: targetNode.id },
                    { name: 'received_date', type: 'string', sourceId: targetNode.id },
                    { name: 'body_text', type: 'string', sourceId: targetNode.id },
                    { name: 'body_html', type: 'string', sourceId: targetNode.id },
                    { name: 'attachments', type: 'string', sourceId: targetNode.id },
                    { name: 'attachment_count', type: 'int64', sourceId: targetNode.id },
                ];
            }
            // 1.6 EDI Source
            else if (type === 'edisource') {
                result = [
                    { name: 'ClaimID', type: 'string' },
                    { name: 'PayerName', type: 'string' },
                    { name: 'PayeeName', type: 'string' },
                    { name: 'ClaimStatus', type: 'string' },
                    { name: 'TotalChargeAmount', type: 'double' },
                    { name: 'TotalPaymentAmount', type: 'double' },
                    { name: 'PatientControlNumber', type: 'string' },
                    { name: 'PayerControlNumber', type: 'string' },
                    { name: 'ServiceDate', type: 'date' },
                    { name: 'DenialReasonCode', type: 'string' },
                    { name: 'DenialDescription', type: 'string' }
                ];
            }
            // 1.7 API Call / Universal API Node
            else if (type === 'universalapi' || type === 'apicall' || type === 'api_call' || type === 'universal_api') {
                // Check if previewData has schema for this node
                if (previewData && previewData[currId]) {
                    const preview = previewData[currId];
                    // Extract columns from preview data
                    if (preview.columns && Array.isArray(preview.columns)) {
                        result = preview.columns.map((col: any) => ({
                            name: col.name || col,
                            type: col.type || col.dataType || 'string',
                            sourceId: targetNode.id
                        }));
                    } else if (preview.data && Array.isArray(preview.data) && preview.data.length > 0) {
                        // Infer schema from first data row
                        const firstRow = preview.data[0];
                        result = Object.keys(firstRow).map(key => ({
                            name: key,
                            type: 'string', // Default type, could infer from value
                            sourceId: targetNode.id
                        }));
                    }
                }
                // If no preview data or schema, try to get from output schema config
                if (result.length === 0 && targetNode.data.outputSchema) {
                    result = targetNode.data.outputSchema.map((c: any) => ({
                        name: c.name,
                        type: c.type || c.dataType || 'string',
                        sourceId: targetNode.id
                    }));
                }
                // If still no schema and node has been executed, inherit from parent
                if (result.length === 0) {
                    const incoming = edges.find(e => e.target === currId);
                    if (incoming) {
                        const parentSchema = await resolveRecursive(incoming.source);
                        // API nodes that transform data should define their own schema
                        // For now, if store_response is enabled, we expect output schema to be set
                        if (targetNode.data.store_response) {
                            // Wait for execution or use empty schema as signal
                            result = [];
                        } else {
                            // Pass-through mode
                            result = parentSchema;
                        }
                    }
                }
            }
            // 2. Join
            else if (type === 'join') {
                const incoming = edges.filter(e => e.target === currId);
                if (incoming.length < 2) {
                    if (incoming.length === 1) result = await resolveRecursive(incoming[0].source);
                    else result = [];
                } else {
                    const leftEdge = incoming.find(e => e.targetHandle === 'left') || incoming[0];
                    const rightEdge = incoming.find(e => e.targetHandle === 'right') || incoming[1];
                    const e2 = (rightEdge && rightEdge.id !== leftEdge.id) ? rightEdge : (incoming.find(e => e.id !== leftEdge.id) || incoming[1]);

                    const s1 = await resolveRecursive(leftEdge.source);
                    const s2 = e2 ? await resolveRecursive(e2.source) : [];

                    const lCols = s1.map(c => ({ ...c, source: 'left' }));
                    const rCols = s2.map(c => ({ ...c, source: 'right' }));
                    result = [...lCols, ...rCols];
                }
            }
            // 2b. Multi-Join â€” merge schemas from ALL incoming edges
            else if (type === 'multijoin' || type === 'multi_join' || type === 'MULTIJOIN' || type === 'MULTI_JOIN') {
                // Prefer post-execution outputSchema (populated from previewData after NODE_COMPLETED)
                if (targetNode.data.outputSchema && Array.isArray(targetNode.data.outputSchema) && targetNode.data.outputSchema.length > 0) {
                    result = targetNode.data.outputSchema.map((c: any) => ({
                        name: c.name,
                        type: c.dataType || c.type || 'string',
                        sourceId: targetNode.id
                    }));
                } else {
                    // Pre-execution fallback: merge schemas from all connected inputs
                    const incomingEdges = edges.filter(e => e.target === currId);
                    const allCols: any[] = [];
                    const seen = new Set<string>();
                    for (const edge of incomingEdges) {
                        const schema = await resolveRecursive(edge.source);
                        schema.forEach((c: any) => {
                            if (!seen.has(c.name)) {
                                seen.add(c.name);
                                allCols.push({ ...c, sourceId: edge.source });
                            }
                        });
                    }
                    result = allCols;
                }
            }

            // Union
            else if (type === 'union') {
                if (targetNode.data.columns && targetNode.data.columns.length > 0) {
                    result = targetNode.data.columns.map((c: any) => ({ name: c.name, type: c.type || 'string' }));
                } else {
                    const incoming = edges.filter(e => e.target === currId);
                    if (incoming.length > 0) result = await resolveRecursive(incoming[0].source);
                }
            }
            // Workflow automation nodes â€” parent schema + known output columns (for Duplicate Detector etc.)
            else if (['contentmerger', 'content_merger', 'documentparser', 'document_parser', 'databaselookup', 'database_lookup', 'aisummarizer', 'ai_summarizer', 'templaterenderer', 'template_renderer', 'duplicatedetector', 'duplicate_detector', 'llmnode', 'llm_node', 'ai_agent', 'aiagent', 'ai_insight', 'aiinsight', 'aitransform', 'ai_transform'].includes(type || '')) {
                const incoming = edges.find((e: any) => e.target === currId);
                if (!incoming) {
                    // No explicit graph edge â€” this node is likely a loop child receiving implicit seed data.
                    // Fall back to: 1) post-execution outputSchema, 2) known static columns per type.
                    if (targetNode.data?.outputSchema?.length > 0) {
                        result = targetNode.data.outputSchema.map((c: any) => ({
                            name: c.name,
                            type: c.dataType || c.type || 'string',
                            sourceId: targetNode.id
                        }));
                    } else if (type === 'documentparser' || type === 'document_parser') {
                        const dpKeyCol = targetNode.data?.keyColumn;
                        result = [
                            ...(dpKeyCol ? [{ name: dpKeyCol, type: 'string', sourceId: targetNode.id }] : []),
                            { name: 'file_name', type: 'string' }, { name: 'file_path', type: 'string' },
                            { name: 'document_type', type: 'string' }, { name: 'extracted_text', type: 'string' },
                            { name: 'processing_methods', type: 'string' }, { name: 'parsed_at', type: 'string' },
                            { name: 'text_length', type: 'int32' }, { name: 'phi_detected', type: 'boolean' },
                            { name: 'phi_count', type: 'int32' }
                        ];
                    } else if (type === 'contentmerger' || type === 'content_merger') {
                        result = [
                            { name: 'group_key', type: 'string' }, { name: 'merged_content', type: 'string' },
                            { name: 'source_files', type: 'string' }, { name: 'document_count', type: 'int32' }
                        ];
                    } else {
                        result = [];
                    }
                } else {
                    const parentSchema = await resolveRecursive(incoming.source);
                    const extras: { name: string; type: string; sourceId?: string }[] = [];
                    if (type === 'contentmerger' || type === 'content_merger') {
                        extras.push({ name: 'group_key', type: 'string', sourceId: targetNode.id }, { name: 'content', type: 'string', sourceId: targetNode.id }, { name: 'file_path', type: 'string', sourceId: targetNode.id });
                    } else if (type === 'documentparser' || type === 'document_parser') {
                        extras.push({ name: 'extracted_text', type: 'string', sourceId: targetNode.id }, { name: 'file_path', type: 'string', sourceId: targetNode.id }, { name: 'file_name', type: 'string', sourceId: targetNode.id });
                    } else if (type === 'aisummarizer' || type === 'ai_summarizer') {
                        extras.push({ name: 'ai_summary', type: 'string', sourceId: targetNode.id });
                    } else if (type === 'templaterenderer' || type === 'template_renderer') {
                        extras.push({ name: 'rendered_output', type: 'string', sourceId: targetNode.id }, { name: 'output_file_path', type: 'string', sourceId: targetNode.id }, { name: 'rendered_at', type: 'string', sourceId: targetNode.id });
                    } else if (['llmnode', 'llm_node', 'ai_agent', 'aiagent', 'ai_insight', 'aiinsight'].includes(type || '')) {
                        extras.push({ name: targetNode.data.outputColumn || (type.includes('agent') ? 'agent_result' : type.includes('insight') ? 'ai_insight' : 'llm_response'), type: 'string', sourceId: targetNode.id });
                    } else if (type === 'aitransform' || type === 'ai_transform') {
                        // AI Transform adds a JSON column based on user config, fallback to transformed_data
                        extras.push({ name: targetNode.data.outputColumn || 'transformed_data', type: 'any', sourceId: targetNode.id });
                    }
                    const existing = new Set((parentSchema || []).map((c: any) => c.name));
                    result = [...(parentSchema || []), ...extras.filter((e: any) => !existing.has(e.name))];
                }
            }
            // 3. Single Input Transform
            else {
                const incoming = edges.find(e => e.target === currId);
                if (!incoming) {
                    // Check if node is inside a container (Loop)
                    const pId = targetNode.parentId || targetNode.parentNode;
                    if (pId) {
                        const pNode = nodes.find(n => n.id === pId);
                        if (pNode && (pNode.type?.toLowerCase() === 'loop' || pNode.type?.toLowerCase() === 'container')) {
                            // Inherit schema from the container
                            result = await resolveRecursive(pId);
                        } else {
                            result = [];
                        }
                    } else {
                        result = [];
                    }
                } else {
                    const parentSchema = await resolveRecursive(incoming.source);

                    if (type === 'column' && targetNode.data.columns) {
                        const newSchema: any[] = [];
                        targetNode.data.columns.forEach((col: any) => {
                            if (col.keep !== false) {
                                const original = parentSchema.find((c: any) => c.name === col.name);
                                newSchema.push({
                                    ...original,
                                    name: col.newName || col.name,
                                    type: col.type !== 'AUTO' ? col.type : (original?.type || 'string')
                                });
                            }
                        });
                        result = newSchema.length > 0 ? newSchema : parentSchema;

                        // Append RowID if enabled
                        if (targetNode.data.generateRowId) {
                            result.push({ name: 'RowID', type: 'integer' });
                        }
                    }
                    else if (type === 'aggregate') {
                        const groups = targetNode.data.groups || [];
                        const aggs = targetNode.data.aggregates || [];
                        const newSchema = parentSchema.filter(c => groups.includes(c.name));
                        aggs.forEach((a: any) => {
                            newSchema.push({ name: a.alias || `${a.func}_${a.column}`, type: 'double' });
                        });
                        result = newSchema;
                    }
                    else if (type === 'unpivot') {
                        // Frontend saves: unpivot_columns, var_name, value_name
                        const uCols = targetNode.data.unpivot_columns || targetNode.data.dataColumns || targetNode.data.unpivotCols || [];
                        const keptColumns = parentSchema.filter((c: any) => !uCols.includes(c.name));
                        result = [
                            ...keptColumns,
                            { name: targetNode.data.var_name || targetNode.data.nameHeader || 'Attribute', type: 'string' },
                            { name: targetNode.data.value_name || targetNode.data.valueHeader || 'Value', type: 'any' }
                        ];
                    }
                    else if (type === 'pivot') {
                        // Frontend saves: group_by (array), pivot_values (array)
                        const groups = targetNode.data.group_by || targetNode.data.groups || [];
                        const baseSchema = groups.length > 0 ? parentSchema.filter((c: any) => groups.includes(c.name)) : [...parentSchema];

                        // pivot_values is an array from PivotNodeConfig; fallback: comma-string for legacy
                        let pValues: string[] = [];
                        if (Array.isArray(targetNode.data.pivot_values)) {
                            pValues = targetNode.data.pivot_values.filter((v: string) => v);
                        } else if (typeof targetNode.data.pivotValues === 'string') {
                            pValues = targetNode.data.pivotValues.split(',').map((v: string) => v.trim()).filter((v: string) => v);
                        }
                        const pivotCols = pValues.map((val: string) => ({ name: val, type: 'any' }));
                        result = [...baseSchema, ...pivotCols];
                    }
                    else if (type === 'pareto') {
                        result = [
                            ...parentSchema,
                            { name: '_Pareto_CumSum', type: 'double' },
                            { name: '_Pareto_Total', type: 'double' },
                            { name: '_Pareto_Pct', type: 'double' },
                            { name: '_Pareto_Segment', type: 'string' }
                        ];
                    }
                    else if ((type === 'formula') && targetNode.data.formulas) {
                        result = [...parentSchema]; // Clone!
                        targetNode.data.formulas.forEach((f: any) => {
                            if (f.column) {
                                const idx = result.findIndex(c => c.name === f.column);
                                if (idx >= 0) result[idx] = { ...result[idx], type: f.dataType || 'string' };
                                else result.push({ name: f.column, type: f.dataType || 'string' });
                            }
                        });
                    }
                    else if (type === 'scoremodel' || type === 'ml_score' || type === 'mlpredict' || type === 'predict' || type === 'ml_predict') {
                        const incomingAll = edges.filter((e: any) => e.target === currId);
                        let dataSchema = parentSchema;
                        if (incomingAll.length > 1) {
                            const schemas = await Promise.all(incomingAll.map((e: any) => resolveRecursive(e.source)));
                            dataSchema = schemas.reduce((best, s) => (s.length > best.length && !s.some((c: any) => c.name === 'model_path')) ? s : best, schemas[0] || []);
                        }
                        result = [...dataSchema];
                        const outCol = targetNode.data?.outputColumn || targetNode.data?.scoreColumn || 'Score';
                        if (outCol && !result.some((c: any) => c.name === outCol)) result.push({ name: outCol, type: 'double' });
                        if (!result.some((c: any) => c.name === 'Score')) result.push({ name: 'Score', type: 'double' });
                        if (!result.some((c: any) => c.name === 'PredictedLabel')) result.push({ name: 'PredictedLabel', type: 'double' });
                    }
                    else if (type === 'window') {
                        result = [...parentSchema];
                        (targetNode.data.windowFunctions || []).forEach((f: any) => {
                            const colName = f.targetColumn || f.alias || `${f.function}_${f.argumentColumn}`;
                            if (colName && !result.find(c => c.name === colName)) {
                                result.push({ name: colName, type: 'double' });
                            }
                        });
                    }
                    else if (type === 'aitransform' || type === 'aiinsight') {
                        result = [...parentSchema];
                        const newCol = targetNode.data.outputColumn || targetNode.data.generatedColumn || (type === 'aiinsight' ? 'AI_Insight' : null);
                        if (newCol && !result.find(c => c.name === newCol)) {
                            result.push({ name: newCol, type: 'string' });
                        }
                    }
                    else if (type === 'documentparser' || type === 'document_parser') {
                        result = [
                            { name: 'file_name', type: 'string' },
                            { name: 'file_path', type: 'string' },
                            { name: 'document_type', type: 'string' },
                            { name: 'extracted_text', type: 'string' },
                            { name: 'processing_methods', type: 'string' },
                            { name: 'parsed_at', type: 'string' },
                            { name: 'text_length', type: 'int32' },
                            { name: 'phi_detected', type: 'boolean' },
                            { name: 'phi_count', type: 'int32' }
                        ];
                    }
                    else if (type === 'contentmerger' || type === 'content_merger') {
                        result = [
                            { name: 'group_key', type: 'string' },
                            { name: 'merged_content', type: 'string' },
                            { name: 'source_files', type: 'string' },
                            { name: 'document_count', type: 'int32' }
                        ];
                    }
                    else if (type === 'aisummarizer' || type === 'ai_summarizer') {
                        result = [...parentSchema, { name: targetNode.data.outputColumn || 'ai_summary', type: 'string' }, { name: 'summarized_at', type: 'string' }];
                    }
                    else if (type === 'llmnode' || type === 'llm_node') {
                        result = [...parentSchema];
                        const newCol = targetNode.data.outputColumn || 'llm_response';
                        if (!result.find(c => c.name === newCol)) result.push({ name: newCol, type: 'string' });
                    }
                    else if (type === 'aiagent' || type === 'ai_agent') {
                        result = [...parentSchema];
                        const newCol = targetNode.data.outputColumn || 'agent_result';
                        if (!result.find(c => c.name === newCol)) result.push({ name: newCol, type: 'string' });
                    }
                    else if ((type === 'autotype' || type === 'auto_type') && targetNode.data.columns) {
                        result = parentSchema.map((col: any) => {
                            const change = targetNode.data.columns.find((c: any) => c.name === col.name);
                            if (change) return { ...col, type: change.type };
                            return col;
                        });
                    }
                    else if (type === 'pythonscript' || type === 'python_script') {
                        // Python Script can add/remove/rename columns â€” use post-execution outputSchema if available
                        if (targetNode.data.outputSchema && Array.isArray(targetNode.data.outputSchema) && targetNode.data.outputSchema.length > 0) {
                            result = targetNode.data.outputSchema.map((c: any) => ({
                                name: c.name,
                                type: c.dataType || c.type || 'string',
                                sourceId: targetNode.id
                            }));
                        } else if (previewData && previewData[currId]) {
                            // Infer from preview data if available
                            const preview = previewData[currId];
                            if (preview.columns && Array.isArray(preview.columns)) {
                                result = preview.columns.map((col: any) => ({
                                    name: col.name || col,
                                    type: col.type || col.dataType || 'string',
                                    sourceId: targetNode.id
                                }));
                            } else if (Array.isArray(preview) && preview.length > 0) {
                                result = Object.keys(preview[0] || {}).map(key => ({
                                    name: key,
                                    type: 'string',
                                    sourceId: targetNode.id
                                }));
                            } else {
                                result = parentSchema;
                            }
                        } else {
                            // Not yet executed â€” fall back to parent schema so downstream nodes have something to work with
                            result = parentSchema;
                        }
                    }
                    else {
                        // â”€â”€ Universal fallback: use post-execution outputSchema if the node has been run â”€â”€
                        // This is critical for loop children (Document Parser, etc.) which have no explicit
                        // incoming edge in the graph â€” their parentSchema is {} but outputSchema is populated
                        // by the NODE_COMPLETED WS event.
                        if (targetNode.data?.outputSchema?.length > 0) {
                            result = targetNode.data.outputSchema.map((c: any) => ({
                                name: c.name,
                                type: c.dataType || c.type || 'string',
                                sourceId: targetNode.id
                            }));
                        } else if (previewData && Array.isArray(previewData[currId]) && (previewData[currId] as any[]).length > 0) {
                            result = Object.keys((previewData[currId] as any[])[0] || {}).map(key => ({
                                name: key,
                                type: 'string',
                                sourceId: targetNode.id
                            }));
                        } else {
                            result = parentSchema;
                        }
                    }
                }
            }

            visiting.delete(currId);
            cache.set(currId, result);
            return result;
        };

        return resolveRecursive(nodeId);
    };

    // Schema Propagation Effect
    useEffect(() => {
        const type = selectedNode?.type?.toLowerCase();
        if (['filter', 'clean', 'cleanse', 'formula', 'column', 'pivot', 'unpivot', 'join', 'multijoin', 'union', 'aggregate', 'autotype', 'auto_type', 'sort', 'deduplicate', 'window', 'pareto', 'trainmodel', 'ml_train', 'scoremodel', 'ml_score', 'mlpredict', 'predict', 'ml_predict', 'evaluatemodel', 'ml_evaluate', 'aitransform', 'aiinsight', 'smarttransform', 'smartimpute', 'visual', 'mdmingest', 'destination', 'split', 'conditional_split', 'cond_split',
            'documentparser', 'document_parser', 'filelistener', 'file_listener',
            'conditionalrouter', 'conditional_router', 'datamapper', 'data_mapper', 'documentgenerator', 'document_generator',
            'duplicatedetector', 'duplicate_detector', 'loop', 'pythonscript', 'python_script',
            'contentmerger', 'content_merger', 'aisummarizer', 'ai_summarizer', 'databaselookup', 'database_lookup',
            'templaterenderer', 'template_renderer', 'fileoutput', 'file_output',
            'llmnode', 'llm_node', 'aiagent', 'ai_agent', 'date', 'geo', 'currency', 'universalapi', 'universal_api', 'api_call', 'apicall', 'executesql', 'execute_sql', 'data_model_output', 'datamodeloutput', 'emailaction', 'email_action', 'approval', 'remotefile', 'remote_file', 'flowrunner', 'flow_runner', 'barrier', 'sqldml', 'sql_dml', 'useraction', 'user_action'].includes(type || '')) {
            const propagate = async () => {
                if (type === 'join') {
                    const edges = getEdges();
                    const incomingEdges = edges.filter(e => e.target === selectedNode?.id);
                    const leftEdge = incomingEdges.find(e => e.targetHandle === 'left') || incomingEdges[0];
                    const rightEdge = incomingEdges.find(e => e.targetHandle === 'right') || incomingEdges[1];

                    const leftSchema: any[] = leftEdge ? await resolveSchema(leftEdge.source) : [];
                    const rightSchema: any[] = rightEdge ? await resolveSchema(rightEdge.source) : [];

                    setInputColumns([
                        ...leftSchema.map(c => ({ ...c, source: 'left', sourceId: leftEdge?.source })),
                        ...rightSchema.map(c => ({ ...c, source: 'right', sourceId: rightEdge?.source }))
                    ]);
                } else if (type === 'multijoin' || type === 'multi_join') {
                    const edges = getEdges();
                    const incomingEdges = edges.filter(e => e.target === selectedNode?.id);
                    const allCols: any[] = [];

                    for (const edge of incomingEdges) {
                        const schema = await resolveSchema(edge.source);
                        // Tag with Handle ID (e.g. input-0)
                        const handleId = edge.targetHandle || 'default';
                        schema.forEach(c => allCols.push({
                            ...c,
                            sourceId: edge.source,
                            sourceHandle: handleId,
                            // Ensure unique name for UI (optional, but helpful if collisions occur)
                            uiName: `${handleId}.${c.name}`
                        }));
                    }
                    setInputColumns(allCols);
                } else if (type === 'union') {
                    const edges = getEdges();
                    const incomingEdges = edges.filter(e => e.target === selectedNode?.id);
                    const allCols: any[] = [];
                    for (const edge of incomingEdges) {
                        const schema = await resolveSchema(edge.source);
                        schema.forEach(c => allCols.push({ ...c, sourceId: edge.source }));
                    }
                    setInputColumns(allCols);
                } else {
                    const edges = getEdges();
                    const incoming = edges.find(e => e.target === selectedNode?.id);
                    if (incoming) {
                        let schema = await resolveSchema(incoming.source);
                        // For Train Model: prefer full table schema from Source when available
                        const isMLTrain = ['trainModel', 'mlTrain', 'ML_TRAIN'].includes(selectedNode?.type || '');
                        if (isMLTrain && schema.length > 0) {
                            const nodes = getNodes();
                            const srcNode = nodes.find((n: any) => n.id === incoming.source);
                            const srcType = srcNode?.type?.toLowerCase();
                            if ((srcType === 'source' || srcType === 'datamodel' || srcType === 'data_model') && srcNode?.data) {
                                const dsId = srcNode.data.dataModelId || srcNode.data.datasetId || srcNode.data.dataSourceId;
                                const tbl = srcNode.data.table;
                                if (dsId && tbl) {
                                    try {
                                        const fullCols = await fetchSchema(dsId, tbl);
                                        if (fullCols && fullCols.length > schema.length) {
                                            schema = fullCols.map((c: any) => ({ name: c.name, type: c.dataType || c.type || c.data_type || 'string', sourceId: incoming.source }));
                                        }
                                    } catch { /* keep schema */ }
                                }
                            }
                        }
                        setInputColumns(schema);
                    } else {
                        // No incoming edge â€” check if inside a container
                        const pId = selectedNode.parentId || (selectedNode as any).parentNode;
                        if (pId) {
                            const pNode = getNodes().find(n => n.id === pId);
                            if (pNode && (pNode.type?.toLowerCase() === 'loop' || pNode.type?.toLowerCase() === 'container')) {
                                const schema = await resolveSchema(pId);
                                setInputColumns(schema);
                            } else {
                                setInputColumns([]);
                            }
                        } else {
                            // Truly no input
                            setInputColumns([]);
                        }
                    }
                }
            };
            propagate();
        }
    }, [selectedNode?.id, edges, nodes]);


    // Destination Table Fetch Effect
    useEffect(() => {
        if (selectedNode && selectedNode.type === 'destination' && selectedNode.data.connectionId) {
            fetchSchema(selectedNode.data.connectionId);
        }
    }, [selectedNode?.id, selectedNode?.data?.connectionId]);

    // Sync Defaults Effect
    useEffect(() => {
        if (selectedNode && ['clean', 'cleanse', 'formula', 'column'].includes(selectedNode.type || '') && inputColumns.length > 0) {
            const currentCols = selectedNode.data.columns || [];

            // If empty, initialize full list
            if (currentCols.length === 0) {
                const defaults = inputColumns.map(c => ({ name: c.name, newName: c.name, type: 'AUTO', keep: true }));
                updateData('columns', defaults);
            }
            else {
                // 1. Identify valid columns (Intersection of Current & Input)
                // We keep a column config if its source name exists in Input
                const validCols = currentCols.filter((cur: any) => inputColumns.some(inc => inc.name === cur.name));

                // 2. Identify Missing (New) Cols
                const missingCols = inputColumns.filter(inc => !currentCols.some((cur: any) => cur.name === inc.name));
                const newCols = missingCols.map(c => ({ name: c.name, newName: c.name, type: 'AUTO', keep: true }));

                // 3. Update if there are changes (Added or Removed)
                if (validCols.length !== currentCols.length || newCols.length > 0) {
                    updateData('columns', [...validCols, ...newCols]);
                }
            }
        }
        else if (selectedNode && selectedNode.type === 'destination' && inputColumns.length > 0) {
            const currentMappings = selectedNode.data.mappings || [];
            if (currentMappings.length === 0) {
                // Auto-Map 1:1 by Name (Best Effort)
                const defaults = inputColumns.map(c => ({ source: c.name, target: c.name }));
                updateData('mappings', defaults);
            }
            else {
                // Sync Logic: Remove stale sources, Add new inputs?
                // For destination, we might want to be conservative (don't break existing maps).
                // But if input schema changes, we should probably alert or add. 
                // Simple strategy: Just add new inputs as 1:1 if not present in usage
                // Filter out mappings where source no longer exists? 
                const validMappings = currentMappings.filter((m: any) => inputColumns.some(inc => inc.name === m.source));
                const mappedSources = new Set(validMappings.map((m: any) => m.source));

                const newMappings = inputColumns
                    .filter(inc => !mappedSources.has(inc.name))
                    .map(inc => ({ source: inc.name, target: inc.name }));

                if (validMappings.length !== currentMappings.length || newMappings.length > 0) {
                    updateData('mappings', [...validMappings, ...newMappings]);
                }
            }

            // Ensure Destination Type is set (Default to Table)
            if (!selectedNode.data.destinationType) {
                updateData('destinationType', 'Table');
            }
        }
        else if (selectedNode && selectedNode.type === 'join' && inputColumns.length > 0) {
            // Join specific sync with renaming
            const currentCols = selectedNode.data.columns || [];
            if (currentCols.length === 0 || inputColumns.length > currentCols.length) { // Simple check, ideally check content
                // Re-calculate defaults
                const newCols: any[] = [];
                const seenNames = new Set<string>();

                // If we want to preserve user choice, we must be careful.
                // But for now, we rebuild list if inputs change significantly? 
                // Better: Update MISSING only? No, for join we ideally want to refresh if inputs change to catch collisions.
                // Let's implement robust merge:

                inputColumns.forEach(inc => {
                    const existing = currentCols.find((c: any) => c.name === inc.name && c.source === inc.source);
                    if (existing) {
                        newCols.push(existing);
                        seenNames.add(existing.newName || existing.name);
                    } else {
                        // New or Initial
                        let finalName = inc.name;
                        // Initial Collision Check against ALREADY added
                        if (seenNames.has(finalName)) {
                            // Find unique name
                            let i = 1;
                            while (seenNames.has(`${finalName}_${i}`)) i++;
                            finalName = `${finalName}_${i}`;
                        }

                        seenNames.add(finalName);
                        newCols.push({
                            name: inc.name,
                            originalName: inc.name,
                            newName: finalName,
                            source: inc.source,
                            type: inc.type,
                            keep: true
                        });
                    }
                });

                if (JSON.stringify(newCols) !== JSON.stringify(currentCols)) {
                    updateData('columns', newCols);
                }
            }
        } else if (selectedNode && selectedNode.type === 'union' && inputColumns.length > 0) {
            const currentCols = selectedNode.data.columns || [];
            if (currentCols.length === 0) {
                const uniqueNames = Array.from(new Set(inputColumns.map(c => c.name)));
                const defaults = uniqueNames.map(name => {
                    const mappings: any = {};
                    inputColumns.filter(c => c.name === name).forEach(c => {
                        mappings[c.sourceId] = c.name;
                    });
                    return { name, type: 'string', mappings };
                });
                updateData('columns', defaults);
            }
        }
    }, [inputColumns, selectedNode?.id]);

    useEffect(() => {
        if (!selectedNode) return;

        if (selectedNode.type === 'source') {
            setDataSources([]);
        }

        if (selectedNode.type === 'executeSql' || selectedNode.type === 'EXECUTE_SQL' ||
            selectedNode.type === 'sqlDml' || selectedNode.type === 'SQL_DML' ||
            selectedNode.type === 'source' ||
            selectedNode.type === 'destination' || selectedNode.type === 'SINK' ||
            selectedNode.type === 'output' || selectedNode.type === 'OUTPUT' ||
            selectedNode.type === 'sink') {
            console.log("Fetching datasources for node type:", selectedNode.type);
            apiFetch('/datasources', null, { method: 'GET' })
                .then((data: any) => setRawConnections(Array.isArray(data) ? data : []))
                .catch(console.error);
        }
    }, [selectedNode?.type]);

    // Fetch tables for destination node when connection changes
    useEffect(() => {
        const connId = selectedNode?.data?.connectionId;
        const isOutputNode = selectedNode?.type === 'destination' || selectedNode?.type === 'SINK' ||
            selectedNode?.type === 'output' || selectedNode?.type === 'OUTPUT' ||
            selectedNode?.type === 'sink';
        if (!connId || !isOutputNode) {
            setDestTables([]);
            return;
        }
        setDestTablesLoading(true);
        fetchSchema(connId)
            .then((schema: any) => {
                if (Array.isArray(schema)) {
                    const names = schema.map((t: any) => t.name || t).filter(Boolean);
                    setDestTables(names);
                } else {
                    setDestTables([]);
                }
            })
            .catch(() => setDestTables([]))
            .finally(() => setDestTablesLoading(false));
    }, [selectedNode?.data?.connectionId, selectedNode?.type]);

    // Source Node schema fetching is now handled internally by SourceNodeConfig


    // Track Selection (Responsive)
    useEffect(() => {
        const selected = nodes.find(n => n.selected);
        setSelectedNode(selected || null);
    }, [nodes]);

    const updateData = (key: string | Record<string, any>, value?: any) => {
        if (!selectedNode) return;

        if (typeof key === 'object' && key !== null) {
            const updates = key;
            console.log('[PropertiesPanel] updateData (batch):', updates);
            setNodes((nds) => nds.map((node) => {
                if (node.id === selectedNode.id) {
                    const updated = { ...node, data: { ...node.data, ...updates } };
                    console.log('[PropertiesPanel] Updated node:', node.id, 'data:', updated.data);
                    return updated;
                }
                return node;
            }));
            onNodeUpdate?.(selectedNode.id, updates);
            setSelectedNode((prev) => prev ? { ...prev, data: { ...prev.data, ...updates } } : null);
            return;
        }

        // Single key update
        const stringKey = key as string;
        console.log('[PropertiesPanel] updateData:', stringKey, '=', value, 'for node:', selectedNode.id);
        setNodes((nds) => nds.map((node) => {
            if (node.id === selectedNode.id) {
                const updated = { ...node, data: { ...node.data, [stringKey]: value } };
                console.log('[PropertiesPanel] Updated node data contains:', Object.keys(updated.data), 'connectionId=', updated.data.connectionId);
                return updated;
            }
            return node;
        }));
        onNodeUpdate?.(selectedNode.id, { [stringKey]: value });
        setSelectedNode((prev) => prev ? { ...prev, data: { ...prev.data, [stringKey]: value } } : null);
    };


    const updateStyle = (key: string, value: any) => {
        if (!selectedNode) return;
        setNodes((nds) => nds.map((node) => {
            if (node.id === selectedNode.id) {
                return { ...node, style: { ...node.style, [key]: value } };
            }
            return node;
        }));
        setSelectedNode((prev) => prev ? { ...prev, style: { ...prev.style, [key]: value } } : null);
    };

    const updateCleanseConfig = (key: string, value: any) => {
        if (!selectedNode) return;
        const currentConfig = selectedNode.data.cleanse || {};
        const newConfig = { ...currentConfig, [key]: value };
        updateData('cleanse', newConfig);
    };

    const updateFormula = (index: number, field: string, value: any) => {
        if (!selectedNode) return;
        const formulas = [...(selectedNode.data.formulas || [])];
        if (!formulas[index]) return;
        formulas[index] = { ...formulas[index], [field]: value };
        updateData('formulas', formulas);
    };

    const addFormula = () => {
        if (!selectedNode) return;
        const formulas = [...(selectedNode.data.formulas || [])];
        formulas.push({ column: `NewColumn${formulas.length + 1}`, dataType: 'string', expression: '' });
        updateData('formulas', formulas);
        setActiveFormulaId(formulas.length);
    };

    const removeFormula = (index: number) => {
        if (!selectedNode) return;
        const formulas = [...(selectedNode.data.formulas || [])];
        formulas.splice(index, 1);
        updateData('formulas', formulas);
    };

    // Window Functions Helpers
    const updateWindowFunc = (index: number, field: string, value: any) => {
        if (!selectedNode) return;
        const funcs = [...(selectedNode.data.windowFunctions || [])];
        if (!funcs[index]) return;
        funcs[index] = { ...funcs[index], [field]: value };
        updateData('windowFunctions', funcs);
    };

    const addWindowFunc = () => {
        if (!selectedNode) return;
        const funcs = [...(selectedNode.data.windowFunctions || [])];
        funcs.push({ function: 'ROW_NUMBER', targetColumn: `Rank${funcs.length + 1}`, argumentColumn: '', offset: 1 });
        updateData('windowFunctions', funcs);
    };

    const removeWindowFunc = (index: number) => {
        if (!selectedNode) return;
        const funcs = [...(selectedNode.data.windowFunctions || [])];
        funcs.splice(index, 1);
        updateData('windowFunctions', funcs);
    };





    const [mdmModels, setMdmModels] = useState<any[]>([]);
    const [showDataModelEditor, setShowDataModelEditor] = useState(false);
    const [editingModelId, setEditingModelId] = useState<string | null>(null);

    useEffect(() => {
        if (selectedNode?.type === 'mdmIngest') {
            apiFetch('/mdm/models', null, { method: 'GET' })
                .then((data: any) => setMdmModels(Array.isArray(data) ? data : []))
                .catch(console.error);
        }
    }, [selectedNode?.type]);

    const handleEditModel = (modelId: string | null) => {
        setEditingModelId(modelId);
        setShowDataModelEditor(true);
    };

    const handleModelSaveSuccess = (model: any) => {
        if (selectedNode) {
            updateData('modelId', model.id);
            updateData('label', model.name); // Update node label to match model name
        }
        setShowDataModelEditor(false); // Optionally keep open? No, close is better UX.
    };


    const handleAutoMatch = async () => {
        if (!selectedNode || selectedNode.type !== 'join') return;
        setIsGenerating(true);
        const token = getToken();
        const nodes = getNodes();
        const edges = getEdges();

        // Identify Parents
        const parents = edges.filter(e => e.target === selectedNode.id).map(e => nodes.find(n => n.id === e.source)).filter(Boolean);
        if (parents.length < 2) {
            toast.error("Join node needs 2 inputs to auto-match.");
            setIsGenerating(false);
            return;
        }

        const leftNode = parents[0];
        const rightNode = parents[1];

        try {
            toast.info("Analyzing schemas with AI...");
            const leftSchema = await resolveSchema(leftNode?.id || '');
            const rightSchema = await resolveSchema(rightNode?.id || '');

            if (leftSchema.length === 0 || rightSchema.length === 0) {
                toast.warning("Could not resolve schema for one or more inputs. Ensure sources are configured.");
                setIsGenerating(false);
                return;
            }

            const data = await apiFetch('/ai/suggest-join', null, {
                method: 'POST',
                body: JSON.stringify({
                    LeftTableName: leftNode!.data.label || 'Left Table',
                    LeftColumns: leftSchema.map(c => c.name),
                    RightTableName: rightNode!.data.label || 'Right Table',
                    RightColumns: rightSchema.map(c => c.name)
                })
            });

            const suggestions = typeof data?.suggestion === 'string' ? JSON.parse(data.suggestion) : data?.suggestion;

            if (Array.isArray(suggestions) && suggestions.length > 0) {
                const newConds = suggestions.map((s: any) => ({
                    leftCol: s.leftCol,
                    operator: '=',
                    rightCol: s.rightCol
                }));
                updateData('conditions', newConds);
                toast.success(`AI found ${suggestions.length} matching keys!`);
            } else {
                toast.info("AI could not find obvious matching keys.");
            }
        } catch (e) {
            console.error(e);
            toast.error("Auto-match failed");
        } finally {
            setIsGenerating(false);
        }
    };

    const autoDetectTypes = async () => {
        const token = getToken();
        if (!token || !selectedNode) return;

        const nodes = getNodes();
        const edges = getEdges();

        // Trace back to source
        let currId = selectedNode.id;
        let sourceNode: Node | null = null;
        let errorMsg = "";

        for (let i = 0; i < 20; i++) {
            const incomingEdge = edges.find(e => e.target === currId);
            if (!incomingEdge) break;

            const parent = nodes.find(n => n.id === incomingEdge.source);
            if (!parent) break;

            // Stop at Source
            if (parent.type === 'source') {
                sourceNode = parent;
                break;
            }
            // Joins/Aggregates block direct table lineage usually (unless we implement smart schema mapping)
            // But 'types' detection API takes a Table Name, so we strictly need a real table source.
            if (['join', 'JOIN', 'aggregate', 'AGGREGATE', 'pivot', 'PIVOT', 'unpivot', 'UNPIVOT', 'union', 'UNION'].includes(parent.type)) {
                errorMsg = `Cannot detect types through ${parent.type} node. Connect directly to source or auto-type first.`;
                break;
            }

            currId = parent.id;
        }

        if (errorMsg) {
            toast.error(errorMsg);
            return;
        }

        if (!sourceNode || !sourceNode.data.dataSourceId || (!sourceNode.data.table && !sourceNode.data.customSql)) {
            toast.error("Could not trace back to a valid source table for type detection.");
            return;
        }

        // If custom SQL, we can't easily "Detect Types" via the table-schema API unless we run a limited query.
        // The /api/ai/detect-types endpoint expects a TableName in the current implementation.
        // Current fallback: Warn if custom SQL.
        if (sourceNode.data.isCustomSql) {
            toast.error("Type detection for Custom SQL sources is not yet supported. Please set types manually.");
            return;
        }

        setIsGenerating(true);
        try {
            const types = await apiFetch('/ai/detect-types', null, {
                method: 'POST',
                body: JSON.stringify({
                    dataSourceId: sourceNode.data.dataSourceId,
                    tableName: sourceNode.data.table
                })
            });

            if (types) {
                const currentConfig = selectedNode.data.columns || [];
                let newConfig = [...currentConfig];

                // If config empty, init from inputs
                if (newConfig.length === 0) {
                    newConfig = inputColumns.map(c => ({ name: c.name, newName: c.name, type: 'AUTO', keep: true }));
                }

                // Map detected types
                let matchCount = 0;
                newConfig = newConfig.map((c: any) => {
                    const detected = types[c.name];
                    if (detected) {
                        matchCount++;
                        return { ...c, type: detected };
                    }
                    return c;
                });

                updateData('columns', newConfig);
                toast.success(`Updated types for ${matchCount} columns.`);
            } else {
                toast.error('Type detection returned no results.');
            }
        } catch (e) {
            console.error(e);
            toast.error("Network error during detection.");
        } finally {
            setIsGenerating(false);
        }
    };







    if (!selectedNode) {
        return (
            <aside className="w-full h-full bg-white p-8 flex flex-col items-center justify-center text-center">
                <div className="bg-gray-50 p-4 rounded-full mb-4">
                    <Settings2 className="h-8 w-8 text-gray-300" />
                </div>
                <h3 className="text-sm font-medium text-gray-900">No Selection</h3>
            </aside>
        );
    }

    return (
        <div id="flow-properties-panel" className="flex flex-col h-full bg-white w-full transition-all duration-300">
            {/* Minimal Node Info Header */}
            <div className="px-5 py-3 border-b border-gray-50 flex items-center justify-between bg-white shrink-0">
                <div>
                    <span className="text-[10px] text-gray-400 block uppercase tracking-wider mb-0.5">Focus</span>
                    <span className="font-semibold text-sm text-gray-900 truncate block max-w-[200px]" title={selectedNode.data.label}>
                        {selectedNode.data.label || selectedNode.type}
                    </span>
                </div>
                <div className="flex flex-col items-end">
                    <span className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">Type</span>
                    <span className="text-xs font-medium text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
                        {(['emailTrigger', 'EMAIL_TRIGGER', 'EMAILTRIGGER', 'fetchemail'].includes(selectedNode.type || '') ? 'fetchemail' : selectedNode.type)}
                    </span>
                </div>
            </div>

            {/* Node Actions Toolbar */}
            <div className="px-5 py-2 border-b border-gray-100 flex gap-2 items-center bg-gray-50/50">
                {onRunToNode && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => selectedNode && onRunToNode(selectedNode.id)}
                        className="h-7 text-xs bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800 gap-1 ml-auto"
                    >
                        <Play className="h-3 w-3" /> Run to This Node
                    </Button>
                )}
            </div>

            {/* Info Panel (shown instead of config when toggled) */}
            {showInfoPanel && (
                <div className="p-4 overflow-y-auto flex-1 custom-scrollbar">
                    <NodeInfoPanel nodeType={selectedNode.type || ''} />
                </div>
            )}

            <div className={`p-5 space-y-6 overflow-y-auto flex-1 custom-scrollbar ${showInfoPanel ? 'hidden' : ''} relative`}>
                {/* Read-only overlay: blocks all interaction when flow is published */}
                {readOnly && (
                    <div className="absolute inset-0 z-20 cursor-not-allowed" title="View only â€” create a draft to edit node properties" />
                )}
                <div className="space-y-3">
                    <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Node Name</label>
                    <input
                        type="text"
                        className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg focus:border-purple-500 bg-gray-50 focus:bg-white transition-colors"
                        value={nodeLabelDraft}
                        onFocus={() => { isEditingLabelRef.current = true; }}
                        onChange={(e) => {
                            isEditingLabelRef.current = true;
                            setNodeLabelDraft(e.target.value);
                            updateData('label', e.target.value);
                        }}
                        onBlur={() => { isEditingLabelRef.current = false; updateData('label', nodeLabelDraft); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                    />
                </div>

                {/* MDM Ingest Props */}
                {selectedNode.type === 'mdmIngest' && (
                    <div className="space-y-6">
                        <div className="space-y-3">
                            <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Target Model</label>
                            <p className="text-[10px] text-gray-500">Select the Master Data Model to ingest into.</p>
                            <select
                                className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg focus:border-purple-500 bg-gray-50 focus:bg-white"
                                value={selectedNode.data.mdmModelId || ''}
                                onChange={(e) => updateData('mdmModelId', e.target.value)}
                            >
                                <option value="">Select MDM Model...</option>
                                {mdmModels.map(m => (
                                    <option key={m.id} value={m.id}>{m.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 text-[11px] text-blue-700">
                            <h4 className="font-semibold mb-1 flex items-center gap-1"><Info className="h-3 w-3" /> Auto-Mapping</h4>
                            <p>Columns from the input flow will be automatically mapped to the MDM Schema based on name. Ensure column names match.</p>
                        </div>
                    </div>
                )}



                {/* Salesforce Destination Props */}
                {selectedNode.type === 'salesforceDestination' && (
                    <div className="space-y-6">
                        {/* Connection Selector */}
                        <div className="space-y-3">
                            <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Salesforce Connection</label>
                            <Select
                                value={selectedNode.data.dataSourceId || ''}
                                onValueChange={(val) => {
                                    updateData('dataSourceId', val);
                                    // Reset object and mappings on connection change
                                    updateData('objectName', '');
                                    updateData('mappings', []);
                                }}
                            >
                                <SelectTrigger className="w-full text-sm">
                                    <SelectValue placeholder="Select Connection" />
                                </SelectTrigger>
                                <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                    {rawConnections.filter((c: any) => c.category === 'CRM' || c.provider === 'salesforce' || c.type === 17).map((c: any) => (
                                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Object Selector (Async) */}
                        <div className="space-y-3">
                            <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Object Name</label>
                            <div className="flex gap-2">
                                <div className="flex-1">
                                    <SalesforceObjectSelector
                                        dataSourceId={selectedNode.data.dataSourceId}
                                        value={selectedNode.data.objectName}
                                        onChange={(val: string) => {
                                            updateData('objectName', val);
                                            // Reset mappings on object change
                                            updateData('mappings', []);
                                        }}
                                    />
                                </div>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="shrink-0 h-9 w-9 bg-white"
                                    onClick={() => setPreviewModal({ isOpen: true, dataSourceId: selectedNode.data.dataSourceId, objectName: selectedNode.data.objectName })}
                                    disabled={!selectedNode.data.objectName}
                                    title="Preview Data"
                                >
                                    <Eye className="h-4 w-4 text-gray-500" />
                                </Button>
                            </div>
                            <p className="text-[10px] text-gray-500">The API Name of the Salesforce Object.</p>
                        </div>

                        {/* Operation Selector */}
                        <div className="space-y-3">
                            <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Operation</label>
                            <Select
                                value={selectedNode.data.operation || 'insert'}
                                onValueChange={(val) => updateData('operation', val)}
                            >
                                <SelectTrigger className="w-full text-sm">
                                    <SelectValue placeholder="Select Operation" />
                                </SelectTrigger>
                                <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                    <SelectItem value="insert">Insert (Write)</SelectItem>
                                    <SelectItem value="upsert">Upsert</SelectItem>
                                    <SelectItem value="delete">Delete</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Upsert External ID */}
                        {selectedNode.data.operation === 'upsert' && (
                            <div className="space-y-3">
                                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">External ID Field</label>
                                <SalesforceFieldSelector
                                    dataSourceId={selectedNode.data.dataSourceId}
                                    objectName={selectedNode.data.objectName}
                                    value={selectedNode.data.externalId}
                                    onChange={(val: string) => updateData('externalId', val)}
                                    placeholder="Select External ID Field..."
                                />
                                <p className="text-[10px] text-gray-500">Required for Upsert operations.</p>
                            </div>
                        )}

                        {/* Field Mappings */}
                        {selectedNode.data.objectName && (
                            <div className="space-y-3 pt-4 border-t border-gray-100">
                                <div className="flex items-center justify-between">
                                    <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Field Mapping</label>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 text-[10px]"
                                        onClick={() => {
                                            // Auto Map based on name match
                                            if (!selectedNode.data.mappings) return;
                                            // Ideally we need the list of SF fields here to map again
                                        }}
                                    >
                                        Auto-Map
                                    </Button>
                                </div>

                                <SalesforceMappingEditor
                                    dataSourceId={selectedNode.data.dataSourceId}
                                    objectName={selectedNode.data.objectName}
                                    inputColumns={inputColumns}
                                    mappings={selectedNode.data.mappings || []}
                                    onChange={(newMappings: any) => updateData('mappings', newMappings)}
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Source Node Props */}
                {(selectedNode.type === 'source' || selectedNode.type === 'SOURCE') && (
                    <div className="space-y-6">
                        <SourceNodeConfig node={selectedNode} onChangeData={updateData} />
                    </div>
                )}

                {/* Hybrid Source Node Props */}
                {(selectedNode.type === 'RELAY_SOURCE' || selectedNode.type === 'relay_source') && (
                    <RelaySourceConfig node={selectedNode} onChangeData={updateData} />
                )}

                {/* Hybrid File Handler â€” multi-source flat file ingest via hybrid agent */}
                {(selectedNode.type === 'HYBRID_FILE_HANDLER' || selectedNode.type === 'hybridFileHandler' || selectedNode.type === 'hybrid_file_handler') && (
                    <HybridFileHandlerConfig
                        node={selectedNode}
                        onChangeData={updateData}
                    />
                )}

                {/* Hybrid Sink Node Props */}
                {(selectedNode.type === 'RELAY_SINK' || selectedNode.type === 'relay_sink') && (
                    <RelaySinkConfig
                        node={selectedNode}
                        onChangeData={updateData}
                        inputColumns={inputColumns}
                        flowNodes={getNodes()}
                    />
                )}

                {/* File Source Node Props */}
                {(selectedNode.type === 'fileSource' || selectedNode.type === 'FILE_SOURCE' || selectedNode.type === 'file_source' || selectedNode.data?.connectionMode === 'file') && (
                    <FileSourceNodeConfig node={selectedNode} onChangeData={updateData} />
                )}

                {/* File Object Node Props (binary files for GenAI/ML) */}
                {(selectedNode.type === 'fileObject' || selectedNode.type === 'FILE_OBJECT') && (
                    <FileObjectNodeConfig node={selectedNode} onChangeData={updateData} />
                )}


                {(selectedNode.type === 'eventListenerEmail' || selectedNode.type === 'EVENT_LISTENER_EMAIL' || selectedNode.type === 'EVENTLISTENEREMAIL') && (
                    <EmailListenerTriggerConfig
                        data={selectedNode.data}
                        onChange={(k: any, v: any) => updateData(k, v)}
                    />
                )}

                {(selectedNode.type === 'emailTrigger' || selectedNode.type === 'EMAIL_TRIGGER' || selectedNode.type === 'EMAILTRIGGER' || selectedNode.type === 'fetchemail') && (
                    <EmailTriggerConfig
                        data={selectedNode.data}
                        onChange={(k: any, v: any) => updateData(k, v)}
                    />
                )}

                {(selectedNode.type === 'email_attribute_filter' || selectedNode.type === 'EMAIL_ATTRIBUTE_FILTER') && (
                    <EmailAttributeFilterConfig
                        data={selectedNode.data}
                        onChange={(k: any, v: any) => updateData(k, v)}
                    />
                )}

                {(selectedNode.type === 'emailPatternFilter' || selectedNode.type === 'EMAIL_PATTERN_FILTER') && (
                    <EmailPatternFilterConfig
                        data={selectedNode.data}
                        onChange={(k: any, v: any) => updateData(k, v)}
                    />
                )}

                {(selectedNode.type === 'attachmentFilter' || selectedNode.type === 'ATTACHMENT_FILTER') && (
                    <AttachmentFilterConfig
                        data={selectedNode.data}
                        onChange={(k: any, v: any) => updateData(k, v)}
                    />
                )}

                {(selectedNode.type === 'attachmentContentProcessor' || selectedNode.type === 'ATTACHMENT_CONTENT_PROCESSOR') && (
                    <AttachmentProcessorConfig
                        data={selectedNode.data}
                        onChange={(k: any, v: any) => updateData(k, v)}
                    />
                )}

                {/* EDI Source Node Props */}
                {
                    selectedNode.type === 'ediSource' && (
                        <div className="space-y-6">
                            {/* App Input Toggle */}
                            <div className="space-y-2">
                                <label className="flex items-center space-x-2 border p-2 rounded bg-indigo-50 border-indigo-100">
                                    <input
                                        type="checkbox"
                                        checked={selectedNode.data.isAppInput === true}
                                        onChange={(e) => updateData('isAppInput', e.target.checked)}
                                        className="rounded text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                                    />
                                    <div>
                                        <div className="text-xs font-bold text-indigo-900">Mark as App Input</div>
                                        <div className="text-[10px] text-indigo-600">User will upload file for this input.</div>
                                    </div>
                                </label>
                            </div>

                            <div className="space-y-3">
                                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">EDI File Path</label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg focus:border-purple-500 bg-gray-50 focus:bg-white transition-colors"
                                        value={selectedNode.data.filePath || ''}
                                        onChange={(e) => updateData('filePath', e.target.value)}
                                        placeholder="/path/to/835.txt"
                                    />
                                </div>
                                <p className="text-[10px] text-gray-400">Path to the X12 835 file.</p>
                            </div>
                        </div>
                    )
                }


                {/* Visual Node Props */}
                {selectedNode.type === 'visual' && <VisualConfig />}

                {/* API Call Node */}
                {(selectedNode.type === 'APICALL' || selectedNode.type === 'apiCall' || selectedNode.type === 'api_call' || selectedNode.type === 'API_CALL') && (
                    <ApiCallConfig
                        data={selectedNode.data}
                        onChange={(k: any, v: any) => updateData(k, v)}
                    />
                )}

                {/* Destination Node Props */}
                {(selectedNode.type === 'destination' || selectedNode.type === 'SINK' || selectedNode.type === 'output' || selectedNode.type === 'OUTPUT' || selectedNode.type === 'sink') && (() => {
                    const destType = selectedNode.data.destinationType || 'Table';
                    // DB types: PostgreSQL=0, MySQL=1, SQLServer=2, Oracle=3, Snowflake=4,
                    // BigQuery=5, Redshift=6, AzureSQL=7, Synapse=8, DuckDB=13
                    // File/Storage types to exclude: CSV=9, Excel=10, JSON=11, Parquet=12, AzureBlob=16, RestAPI=14, MCP=15, Salesforce=17
                    const DB_TYPES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 13];
                    const TYPE_NAMES: Record<number, string> = {
                        0: 'PostgreSQL', 1: 'MySQL', 2: 'SQL Server', 3: 'Oracle',
                        4: 'Snowflake', 5: 'BigQuery', 6: 'Redshift', 7: 'Azure SQL',
                        8: 'Synapse', 9: 'CSV', 10: 'Excel', 11: 'JSON', 12: 'Parquet',
                        13: 'DuckDB', 14: 'REST API', 15: 'MCP', 16: 'Azure Blob', 17: 'Salesforce'
                    };
                    const getTypeName = (c: any) => TYPE_NAMES[Number(c.type)] || `Type ${c.type}`;
                    const dbConnections = rawConnections.filter((c: any) => {
                        const t = Number(c.type);
                        return DB_TYPES.includes(t);
                    });
                    // If no DB connections found, fall back to showing all connections
                    const tableConnections = dbConnections.length > 0 ? dbConnections : rawConnections;
                    return (
                        <div className="space-y-5">
                            {/* Destination Type - Table Only */}
                            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3">
                                <p className="text-xs text-indigo-900 font-medium">ðŸ’¾ Database Table Output</p>
                                <p className="text-xs text-indigo-700 mt-1">Writes data to a database table via connection</p>
                            </div>

                            {/* â”€â”€ TABLE MODE â”€â”€ */}
                            {destType === 'Table' && (
                                <div className="space-y-4">
                                    {/* Connection */}
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                            Connection
                                            {rawConnections.length > 0 && (
                                                <span className="ml-2 text-[10px] font-normal text-gray-400 normal-case">
                                                    ({tableConnections.length} of {rawConnections.length} shown)
                                                </span>
                                            )}
                                        </label>
                                        <select
                                            className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 cursor-pointer"
                                            value={selectedNode.data.connectionId || ''}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                updateData({
                                                    connectionId: val,
                                                    tableName: '',
                                                    mappings: []
                                                });
                                            }}
                                        >
                                            <option value="">Select database connection...</option>
                                            {tableConnections.map((c: any) => (
                                                <option key={c.id} value={String(c.id)}>
                                                    {c.name} ({getTypeName(c)})
                                                </option>
                                            ))}
                                        </select>
                                        {rawConnections.length > 0 && dbConnections.length === 0 && (
                                            <p className="text-[10px] text-amber-600">
                                                âš  No DB connections found. Showing all. For table writes, use PostgreSQL/MySQL/etc.
                                            </p>
                                        )}
                                    </div>

                                    {/* Table Picker */}
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Target Table</label>
                                            <button
                                                className="text-[10px] text-indigo-500 hover:underline disabled:opacity-40"
                                                disabled={!selectedNode.data.connectionId || destTablesLoading}
                                                onClick={() => {
                                                    const connId = selectedNode.data.connectionId;
                                                    if (!connId) return;
                                                    // Clear cache and re-fetch
                                                    schemaCache.current[connId] = undefined as any;
                                                    setDestTablesLoading(true);
                                                    fetchSchema(connId)
                                                        .then((schema: any) => {
                                                            if (Array.isArray(schema)) {
                                                                setDestTables(schema.map((t: any) => t.name || t).filter(Boolean));
                                                            }
                                                        })
                                                        .catch(() => setDestTables([]))
                                                        .finally(() => setDestTablesLoading(false));
                                                }}
                                            >
                                                {destTablesLoading ? 'âŸ³ Loading...' : 'â†» Refresh'}
                                            </button>
                                        </div>
                                        {destTables.length > 0 ? (
                                            <Select
                                                value={selectedNode.data.tableName || ''}
                                                onValueChange={(val) => updateData('tableName', val)}
                                            >
                                                <SelectTrigger className="w-full text-sm">
                                                    <SelectValue placeholder="Select a table..." />
                                                </SelectTrigger>
                                                <SelectContent className="bg-white z-50 shadow-lg border-gray-200 max-h-60 overflow-y-auto">
                                                    {destTables.map((t: string) => (
                                                        <SelectItem key={t} value={t}>{t}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        ) : (
                                            <input
                                                type="text"
                                                className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg focus:border-indigo-500 bg-gray-50 focus:bg-white transition-colors"
                                                value={selectedNode.data.tableName || ''}
                                                onChange={(e) => updateData('tableName', e.target.value)}
                                                placeholder={selectedNode.data.connectionId
                                                    ? (destTablesLoading ? 'Loading tables...' : 'No tables found â€” type manually')
                                                    : 'Select a connection first'}
                                                disabled={!selectedNode.data.connectionId || destTablesLoading}
                                            />
                                        )}
                                        <p className="text-[10px] text-gray-400">The table must already exist in the target database.</p>
                                    </div>

                                    {/* Write Mode */}
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Write Mode</label>
                                        <Select
                                            value={selectedNode.data.writeMode || 'insert'}
                                            onValueChange={(val) => updateData('writeMode', val)}
                                        >
                                            <SelectTrigger className="w-full text-sm">
                                                <SelectValue placeholder="Select write mode" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                                <SelectItem value="insert">Insert (Append rows)</SelectItem>
                                                <SelectItem value="truncate_insert">Truncate + Insert (Replace all)</SelectItem>
                                                <SelectItem value="upsert">Upsert (Insert or Update)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        {/* Upsert Key â€” only shown when upsert mode is selected */}
                                        {(selectedNode.data.writeMode === 'upsert') && (
                                            <div className="mt-2 space-y-1">
                                                <label className="text-xs font-medium text-gray-600">Conflict Key Column(s)</label>
                                                <input
                                                    type="text"
                                                    className="w-full text-sm px-3 py-2 border border-indigo-200 rounded-lg focus:border-indigo-500 bg-indigo-50 focus:bg-white transition-colors"
                                                    value={(selectedNode.data.upsertKey || []).join(', ')}
                                                    onChange={(e) => {
                                                        const keys = e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean);
                                                        updateData('upsertKey', keys);
                                                    }}
                                                    placeholder="e.g. id, email"
                                                />
                                                <p className="text-[10px] text-gray-400">Comma-separated column name(s) used as the unique/primary key for ON CONFLICT.</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Column Mapping */}
                                    {selectedNode.data.connectionId && selectedNode.data.tableName && (
                                        <div className="space-y-2 pt-3 border-t border-gray-100">
                                            <div className="flex items-center justify-between">
                                                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Column Mapping</label>
                                                <button
                                                    className="text-[10px] text-indigo-600 hover:underline"
                                                    onClick={() => {
                                                        const defaults = inputColumns.map((c: any) => ({ source: c.name, target: c.name }));
                                                        updateData('mappings', defaults);
                                                    }}
                                                >
                                                    Auto-Map 1:1
                                                </button>
                                            </div>
                                            <TableMappingEditor
                                                dataSourceId={selectedNode.data.connectionId}
                                                tableName={selectedNode.data.tableName}
                                                inputColumns={inputColumns}
                                                mappings={selectedNode.data.mappings || []}
                                                onChange={(newMappings: any) => updateData('mappings', newMappings)}
                                                fetchSchema={fetchSchema}
                                            />
                                        </div>
                                    )}

                                    {/* Pre/Post SQL */}
                                    <div className="space-y-2 pt-3 border-t border-gray-100">
                                        <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Pre-Write SQL <span className="text-gray-400 font-normal">(optional)</span></label>
                                        <textarea
                                            className="w-full text-xs px-3 py-2 border border-gray-200 rounded-lg focus:border-indigo-500 bg-gray-50 focus:bg-white font-mono resize-none"
                                            rows={3}
                                            value={selectedNode.data.preSql || ''}
                                            onChange={(e) => updateData('preSql', e.target.value)}
                                            placeholder="e.g. TRUNCATE TABLE my_table;"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Post-Write SQL <span className="text-gray-400 font-normal">(optional)</span></label>
                                        <textarea
                                            className="w-full text-xs px-3 py-2 border border-gray-200 rounded-lg focus:border-indigo-500 bg-gray-50 focus:bg-white font-mono resize-none"
                                            rows={3}
                                            value={selectedNode.data.postSql || ''}
                                            onChange={(e) => updateData('postSql', e.target.value)}
                                            placeholder="e.g. ANALYZE my_table;"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Input Columns Preview */}
                            {inputColumns.length > 0 && (
                                <div className="space-y-2 pt-3 border-t border-gray-100">
                                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Input Columns ({inputColumns.length})</label>
                                    <div className="max-h-32 overflow-y-auto space-y-0.5">
                                        {inputColumns.map((col: any) => (
                                            <div key={col.name} className="flex items-center justify-between text-[11px] px-2 py-0.5 rounded hover:bg-gray-50">
                                                <span className="text-gray-700 font-medium truncate">{col.name}</span>
                                                <span className="text-gray-400 shrink-0 ml-2">{col.type}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })()}
                {/* AI Transform Node Props */}

                {/* Agentic Insight Node (Refactored) */}
                {(selectedNode.type === 'aiInsight' || selectedNode.type === 'AI_INSIGHT') && (
                    <AiInsightConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} />
                )}

                {/* Predict, Score, Train, Evaluate â€” rendered once in config section below to avoid duplication */}

                {/* Filter Node Props */}
                {
                    (selectedNode.type === 'filter' || selectedNode.type === 'FILTER') && (
                        <div className="space-y-6">
                            <div className="flex items-center justify-between">
                                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Filter Logic</label>
                                <div className="flex gap-2">
                                    <button className={`text-[10px] px-2 py-1 rounded ${!selectedNode.data.isManualCondition ? 'bg-blue-100 text-blue-700' : 'text-gray-500'}`} onClick={() => updateData('isManualCondition', false)}>Basic</button>
                                    <button className={`text-[10px] px-2 py-1 rounded ${selectedNode.data.isManualCondition ? 'bg-blue-100 text-blue-700' : 'text-gray-500'}`} onClick={() => updateData('isManualCondition', true)}>Custom</button>
                                </div>
                            </div>
                            {!selectedNode.data.isManualCondition ? (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <label className="text-[10px] font-semibold text-gray-700 uppercase">Combine with</label>
                                        <div className="flex gap-1 bg-gray-100 p-0.5 rounded">
                                            <button className={`text-[10px] px-3 py-1 rounded transition-all ${selectedNode.data.logic !== 'OR' ? 'bg-white shadow-sm text-indigo-700 font-medium' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => updateData('logic', 'AND')}>AND</button>
                                            <button className={`text-[10px] px-3 py-1 rounded transition-all ${selectedNode.data.logic === 'OR' ? 'bg-white shadow-sm text-indigo-700 font-medium' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => updateData('logic', 'OR')}>OR</button>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        {(selectedNode.data.conditions || []).map((cond: any, idx: number) => (
                                            <div key={idx} className="bg-gray-50 p-3 rounded-lg border border-gray-200 flex flex-col gap-2 relative group">
                                                <button onClick={() => {
                                                    const newConditions = [...(selectedNode.data.conditions || [])];
                                                    newConditions.splice(idx, 1);
                                                    updateData('conditions', newConditions);
                                                }} className="absolute top-2 right-2 text-gray-400 hover:text-red-500 transition-opacity opacity-0 group-hover:opacity-100 p-1" title="Remove condition">
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                                <div className="grid grid-cols-2 gap-2 pr-6">
                                                    <div className="space-y-1">
                                                        <label className="text-[10px] font-medium text-gray-500">Column</label>
                                                        <Select value={cond.column || ''} onValueChange={(val) => {
                                                            const newConditions = [...(selectedNode.data.conditions || [])];
                                                            newConditions[idx].column = val;
                                                            updateData('conditions', newConditions);
                                                        }}>
                                                            <SelectTrigger className="w-full text-xs h-7 bg-white"><SelectValue placeholder="Select Column..." /></SelectTrigger>
                                                            <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                                                {inputColumns.map((c: any) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                    <div className="space-y-1">
                                                        <label className="text-[10px] font-medium text-gray-500">Operator</label>
                                                        <Select value={cond.operator || '='} onValueChange={(val) => {
                                                            const newConditions = [...(selectedNode.data.conditions || [])];
                                                            newConditions[idx].operator = val;
                                                            updateData('conditions', newConditions);
                                                        }}>
                                                            <SelectTrigger className="w-full text-xs h-7 bg-white"><SelectValue placeholder="Operator..." /></SelectTrigger>
                                                            <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                                                <SelectItem value="=">Equals</SelectItem>
                                                                <SelectItem value="<>">Not Equals</SelectItem>
                                                                <SelectItem value=">">&gt;</SelectItem>
                                                                <SelectItem value="<">&lt;</SelectItem>
                                                                <SelectItem value=">=">&gt;=</SelectItem>
                                                                <SelectItem value="<=">&lt;=</SelectItem>
                                                                <SelectItem value="contains">Contains</SelectItem>
                                                                <SelectItem value="REGEX">Matches Regex</SelectItem>
                                                                <SelectItem value="IS NULL">Is Null</SelectItem>
                                                                <SelectItem value="IS NOT NULL">Is Not Null</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>
                                                {!['IS NULL', 'IS NOT NULL'].includes(cond.operator) && (
                                                    <div className="space-y-1">
                                                        <label className="text-[10px] font-medium text-gray-500">Value</label>
                                                        <input type="text" className="w-full text-xs border border-gray-200 rounded p-1.5 focus:border-indigo-500 outline-none" value={cond.value || ''} onChange={e => {
                                                            const newConditions = [...(selectedNode.data.conditions || [])];
                                                            newConditions[idx].value = e.target.value;
                                                            updateData('conditions', newConditions);
                                                        }} />
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    <Button size="sm" variant="outline" className="w-full text-xs py-1 h-7 border-dashed border-gray-300 text-gray-600 hover:text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50" onClick={() => {
                                        const newConditions = [...(selectedNode.data.conditions || []), { column: '', operator: '=', value: '' }];
                                        updateData('conditions', newConditions);
                                    }}>
                                        <Plus className="h-3 w-3 mr-1" /> Add Condition
                                    </Button>

                                    {/* Legacy migration logic hidden silently if single condition exists */}
                                    {selectedNode.data.filterColumn && !(selectedNode.data.conditions && selectedNode.data.conditions.length > 0) && (
                                        <div className="bg-amber-50 border border-amber-200 p-2 rounded text-[10px] text-amber-800 flex items-start gap-2">
                                            <Info className="h-3 w-3 mt-0.5 shrink-0" />
                                            <div>
                                                <span>Legacy filter detected. </span>
                                                <button className="underline font-medium hover:text-amber-900" onClick={() => {
                                                    updateData({
                                                        conditions: [{
                                                            column: selectedNode.data.filterColumn,
                                                            operator: selectedNode.data.filterOp || '=',
                                                            value: selectedNode.data.filterValue || ''
                                                        }],
                                                        filterColumn: undefined,
                                                        filterOp: undefined,
                                                        filterValue: undefined,
                                                        condition: undefined
                                                    });
                                                }}>Migrate to new format</button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div>
                                    {renderAiInput('Filter')}
                                    <SqlEditor className="h-64 border" value={selectedNode.data.condition || ''} onChange={v => updateData('condition', v)} columns={inputColumns} />
                                </div>
                            )}
                        </div>
                    )
                }



                {/* Smart Impute Node Props */}
                {
                    (selectedNode.type === 'smartImpute' || selectedNode.type === 'SMART_IMPUTE') && (
                        <SmartImputeConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} />
                    )
                }





                {/* Clean / Data Prep Node Props */}
                {
                    (selectedNode.type === 'clean' || selectedNode.type === 'cleanse' || selectedNode.type === 'CLEAN') && (
                        <div className="space-y-6">
                            {/* 1. Null Data Handler */}
                            <div className="space-y-3">
                                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Null Data Handling</label>
                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 space-y-3">
                                    <label className="flex items-start gap-2 cursor-pointer">
                                        <input type="checkbox" className="mt-0.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                            checked={selectedNode.data.cleanse?.removeNullRows || false}
                                            onChange={(e) => updateCleanseConfig('removeNullRows', e.target.checked)}
                                        />
                                        <div>
                                            <span className="text-xs text-gray-700 font-medium block">Remove Null Rows</span>
                                            <span className="text-[10px] text-gray-500">Remove rows where ALL columns are null.</span>
                                        </div>
                                    </label>
                                    <label className="flex items-start gap-2 cursor-pointer">
                                        <input type="checkbox" className="mt-0.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                            checked={selectedNode.data.cleanse?.removeNullCols || false}
                                            onChange={(e) => updateCleanseConfig('removeNullCols', e.target.checked)}
                                        />
                                        <div>
                                            <span className="text-xs text-gray-700 font-medium block">Remove Null Columns</span>
                                            <span className="text-[10px] text-gray-500">Remove columns where ALL values are null.</span>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {/* 2. Select Columns to Cleanse */}
                            <div className="space-y-3">
                                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Select Columns to Cleanse</label>
                                <div className="border border-gray-200 rounded-lg overflow-hidden text-[11px]">
                                    <div className="bg-gray-50 border-b p-2 font-medium text-gray-500 flex items-center justify-between">
                                        <span>Available Columns</span>
                                        <div className="flex bg-white border rounded px-1">
                                            <Search className="h-3 w-3 text-gray-400 m-1" />
                                            <input className="w-20 outline-none text-[10px]" placeholder="Search..." />
                                        </div>
                                    </div>
                                    <div className="max-h-[150px] overflow-y-auto p-1 bg-white space-y-1">
                                        <label className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded cursor-pointer border-b border-gray-50">
                                            <input type="checkbox" className="rounded border-gray-300 text-purple-600"

                                                checked={!selectedNode.data.cleanse?.targetColumns || selectedNode.data.cleanse?.targetColumns.length === 0 || (selectedNode.data.cleanse?.targetColumns.length === 1 && selectedNode.data.cleanse?.targetColumns[0] === '__NONE__' ? false : selectedNode.data.cleanse?.targetColumns.length === inputColumns.length)}
                                                onChange={(e) => {
                                                    if (e.target.checked) updateCleanseConfig('targetColumns', []); // Empty means ALL
                                                    else updateCleanseConfig('targetColumns', ['__NONE__']); // Sentinel: no columns selected
                                                }}
                                            />
                                            <span className="font-semibold text-purple-700">(Select All)</span>
                                        </label>
                                        {inputColumns.map(col => {
                                            const tc = selectedNode.data.cleanse?.targetColumns;
                                            const isAllSelected = !tc || tc.length === 0;
                                            const isNoneSelected = tc?.length === 1 && tc[0] === '__NONE__';
                                            const isExplicitList = tc && tc.length > 0 && !isNoneSelected;
                                            const isChecked = isAllSelected ? true : isNoneSelected ? false : tc.includes(col.name);
                                            return (
                                                <label key={col.name} className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded cursor-pointer">
                                                    <input type="checkbox" className="rounded border-gray-300 text-purple-600"
                                                        checked={isChecked}
                                                        onChange={(e) => {
                                                            let newList: string[];
                                                            if (isAllSelected) {
                                                                // Currently All â€” uncheck one â†’ explicit list of all EXCEPT this one
                                                                if (!e.target.checked) {
                                                                    newList = inputColumns.map(c => c.name).filter(n => n !== col.name);
                                                                } else {
                                                                    newList = []; // Still All
                                                                }
                                                            } else if (isNoneSelected) {
                                                                // Currently None â€” check one â†’ explicit list with just this one
                                                                if (e.target.checked) {
                                                                    newList = [col.name];
                                                                } else {
                                                                    newList = ['__NONE__']; // Still None
                                                                }
                                                            } else {
                                                                // Explicit list
                                                                if (e.target.checked) {
                                                                    newList = [...(tc || []), col.name];
                                                                    // If all columns now selected, collapse back to [] (ALL)
                                                                    if (newList.length >= inputColumns.length) newList = [];
                                                                } else {
                                                                    newList = (tc || []).filter((n: string) => n !== col.name);
                                                                    // If none left, use sentinel
                                                                    if (newList.length === 0) newList = ['__NONE__'];
                                                                }
                                                            }
                                                            updateCleanseConfig('targetColumns', newList);
                                                        }}
                                                    />
                                                    <span className="truncate flex-1">{col.name}</span>
                                                    <span className="text-gray-400 text-[9px] uppercase">{col.type}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* 3. Replace Nulls */}
                            <div className="space-y-3">
                                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Replace Nulls</label>
                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 grid grid-cols-2 gap-4">
                                    <label className="flex items-center gap-2 cursor-pointer" title="Replace Null with Empty String">
                                        <input type="checkbox" className="rounded border-gray-300 text-purple-600"
                                            checked={selectedNode.data.cleanse?.replaceNullWithBlank || false} onChange={(e) => updateCleanseConfig('replaceNullWithBlank', e.target.checked)} />
                                        <span className="text-[11px] text-gray-600">Null to Blank</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer" title="Replace Null with 0">
                                        <input type="checkbox" className="rounded border-gray-300 text-purple-600"
                                            checked={selectedNode.data.cleanse?.replaceNullWithZero || false} onChange={(e) => updateCleanseConfig('replaceNullWithZero', e.target.checked)} />
                                        <span className="text-[11px] text-gray-600">Null to 0</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer" title="Replace Blank with Null">
                                        <input type="checkbox" className="rounded border-gray-300 text-purple-600"
                                            checked={selectedNode.data.cleanse?.replaceBlankWithNull || false} onChange={(e) => updateCleanseConfig('replaceBlankWithNull', e.target.checked)} />
                                        <span className="text-[11px] text-gray-600">Blank to Null</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer" title="Replace 0 with Null">
                                        <input type="checkbox" className="rounded border-gray-300 text-purple-600"
                                            checked={selectedNode.data.cleanse?.replaceZeroWithNull || false} onChange={(e) => updateCleanseConfig('replaceZeroWithNull', e.target.checked)} />
                                        <span className="text-[11px] text-gray-600">0 to Null</span>
                                    </label>
                                </div>
                            </div>

                            {/* 4. Remove Characters */}
                            <div className="space-y-3">
                                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Remove Characters</label>
                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 space-y-3">
                                    {/* Whitespace Group */}
                                    <div className="space-y-2 pb-2 border-b border-gray-200">
                                        <h5 className="text-[10px] font-bold text-gray-500 uppercase">Whitespace</h5>
                                        <div className="grid grid-cols-2 gap-2">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="checkbox" className="rounded border-gray-300 text-purple-600" checked={selectedNode.data.cleanse?.removeLeadingTrailingWhitespace ?? true} onChange={(e) => updateCleanseConfig('removeLeadingTrailingWhitespace', e.target.checked)} />
                                                <span className="text-[11px]">Leading/Trailing</span>
                                            </label>
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="checkbox" className="rounded border-gray-300 text-purple-600" checked={selectedNode.data.cleanse?.removeTabsBreaks || false} onChange={(e) => updateCleanseConfig('removeTabsBreaks', e.target.checked)} />
                                                <span className="text-[11px]">Tabs/Line Breaks</span>
                                            </label>
                                            <label className="flex items-center gap-2 cursor-pointer col-span-2">
                                                <input type="checkbox" className="rounded border-gray-300 text-purple-600" checked={selectedNode.data.cleanse?.removeAllWhitespace || false} onChange={(e) => updateCleanseConfig('removeAllWhitespace', e.target.checked)} />
                                                <span className="text-[11px]">All Whitespace</span>
                                            </label>
                                        </div>
                                    </div>

                                    {/* Special Group */}
                                    <div className="space-y-2 pb-2 border-b border-gray-200">
                                        <h5 className="text-[10px] font-bold text-gray-500 uppercase">Special</h5>
                                        <div className="grid grid-cols-2 gap-2">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="checkbox" className="rounded border-gray-300 text-purple-600" checked={selectedNode.data.cleanse?.removeHtml || false} onChange={(e) => updateCleanseConfig('removeHtml', e.target.checked)} />
                                                <span className="text-[11px]">HTML Tags</span>
                                            </label>
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="checkbox" className="rounded border-gray-300 text-purple-600" checked={selectedNode.data.cleanse?.removeInvisible || false} onChange={(e) => updateCleanseConfig('removeInvisible', e.target.checked)} />
                                                <span className="text-[11px]">Invisible Chars</span>
                                            </label>
                                        </div>
                                    </div>

                                    {/* Filter Group */}
                                    <div className="space-y-2">
                                        <h5 className="text-[10px] font-bold text-gray-500 uppercase">Filter</h5>

                                        <div className="flex flex-col gap-1">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="checkbox" className="rounded border-gray-300 text-purple-600" checked={selectedNode.data.cleanse?.removeLetters || false} onChange={(e) => updateCleanseConfig('removeLetters', e.target.checked)} />
                                                <span className="text-[11px]">Letters</span>
                                            </label>
                                            {selectedNode.data.cleanse?.removeLetters && (
                                                <input className="text-[10px] border border-gray-200 rounded px-2 py-1 w-full" placeholder="Except: (e.g. ABC)"
                                                    value={selectedNode.data.cleanse?.removeLettersExcept || ''} onChange={(e) => updateCleanseConfig('removeLettersExcept', e.target.value)} />
                                            )}
                                        </div>

                                        <div className="flex flex-col gap-1">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="checkbox" className="rounded border-gray-300 text-purple-600" checked={selectedNode.data.cleanse?.removeNumbers || false} onChange={(e) => updateCleanseConfig('removeNumbers', e.target.checked)} />
                                                <span className="text-[11px]">Numbers</span>
                                            </label>
                                            {selectedNode.data.cleanse?.removeNumbers && (
                                                <input className="text-[10px] border border-gray-200 rounded px-2 py-1 w-full" placeholder="Except: (e.g. 123)"
                                                    value={selectedNode.data.cleanse?.removeNumbersExcept || ''} onChange={(e) => updateCleanseConfig('removeNumbersExcept', e.target.value)} />
                                            )}
                                        </div>

                                        <div className="flex flex-col gap-1">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="checkbox" className="rounded border-gray-300 text-purple-600" checked={selectedNode.data.cleanse?.removePunctuation || false} onChange={(e) => updateCleanseConfig('removePunctuation', e.target.checked)} />
                                                <span className="text-[11px]">Punctuation</span>
                                            </label>
                                            {selectedNode.data.cleanse?.removePunctuation && (
                                                <input className="text-[10px] border border-gray-200 rounded px-2 py-1 w-full" placeholder="Except: (e.g. .@)"
                                                    value={selectedNode.data.cleanse?.removePunctuationExcept || ''} onChange={(e) => updateCleanseConfig('removePunctuationExcept', e.target.value)} />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 5. Standardization & Fill Missing */}
                            <div className="space-y-3">
                                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Advanced Prep</label>
                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 space-y-3">


                                    {/* Null Filling */}
                                    <div className="space-y-2">
                                        <h5 className="text-[10px] font-bold text-gray-500 uppercase">Fill Missing Values</h5>
                                        <div className="flex flex-col gap-1">
                                            <label className="text-[10px] text-gray-500">Replace NULL with:</label>
                                            <input
                                                type="text"
                                                className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded focus:border-purple-500 bg-white placeholder:text-gray-300"
                                                placeholder="e.g. Unknown, 0, N/A"
                                                value={selectedNode.data.cleanse?.replaceNullWithCustom || ''}
                                                onChange={(e) => updateCleanseConfig('replaceNullWithCustom', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 5. Modify Case */}
                            <div className="space-y-3">
                                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Modify Case</label>
                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                                    <select className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg focus:border-purple-500 bg-white"
                                        value={selectedNode.data.cleanse?.modifyCase || 'none'}
                                        onChange={(e) => updateCleanseConfig('modifyCase', e.target.value)}
                                    >
                                        <option value="none">No Change</option>
                                        <option value="upper">Upper Case</option>
                                        <option value="lower">Lower Case</option>
                                        <option value="title">Title Case</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )
                }

                {/* Formula Node Props */}
                {
                    (selectedNode.type === 'formula' || selectedNode.type === 'FORMULA') && (
                        <div className="space-y-6">
                            <div className="flex items-center justify-between">
                                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Formulas</label>
                                <div className="flex gap-2">
                                    <button onClick={() => setShowFuncHelp(!showFuncHelp)} className="text-gray-400 hover:text-blue-500"><HelpCircle className="h-4 w-4" /></button>
                                    <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1" onClick={addFormula}><Plus className="h-3 w-3" /> Add</Button>
                                </div>
                            </div>

                            {/* Function Reference Panel */}
                            {showFuncHelp && (
                                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-[10px] space-y-2 mb-2">
                                    <h4 className="font-bold text-blue-800">Function Library (DuckDB)</h4>
                                    <div className="space-y-1 max-h-40 overflow-y-auto">
                                        {FUNCTION_CATS.map(cat => (
                                            <div key={cat.name}>
                                                <div className="font-semibold text-blue-700">{cat.name}</div>
                                                <div className="text-gray-600 pl-2">{cat.funcs.join(', ')}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="space-y-4">
                                {(selectedNode.data.formulas || []).map((f: any, i: number) => (
                                    <div key={i} className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                                        <div className="flex items-center gap-2 p-2 border-b bg-gray-100">
                                            <input
                                                className="flex-1 min-w-0 bg-white text-sm font-medium border border-transparent rounded px-1.5 py-0.5 focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-200 placeholder:text-gray-400 hover:border-gray-300 transition-colors"
                                                placeholder="Output Column"
                                                value={f.column}
                                                onChange={(e) => updateFormula(i, 'column', e.target.value)}
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onFocus={(e) => e.target.select()}
                                            />
                                            <select
                                                className="w-20 shrink-0 text-[10px] bg-transparent border-none text-gray-500 focus:ring-0 text-right"
                                                value={f.dataType || 'string'}
                                                onChange={(e) => updateFormula(i, 'dataType', e.target.value)}
                                            >
                                                <option value="string">String</option>
                                                <option value="double">Number</option>
                                                <option value="date">Date</option>
                                                <option value="boolean">Boolean</option>
                                            </select>
                                            <button onClick={() => removeFormula(i)} className="shrink-0 p-1.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-500 transition-colors" title="Remove Column"><Trash2 className="h-4 w-4" /></button>
                                        </div>
                                        <div className="px-2 pt-2">
                                            {renderAiInput('Formula', i)}
                                        </div>
                                        <div className="h-32 relative">
                                            <SqlEditor
                                                value={f.expression}
                                                onChange={(val) => updateFormula(i, 'expression', val)}
                                                columns={inputColumns}
                                                placeholder="e.g. Quantity * Price"
                                                className="h-full border-none rounded-none bg-white"
                                            />
                                        </div>
                                    </div>
                                ))}
                                {(selectedNode.data.formulas || []).length === 0 && (
                                    <div className="text-center p-8 border-2 border-dashed border-gray-200 rounded-lg">
                                        <p className="text-xs text-gray-500">No active formulas.</p>
                                        <Button size="sm" variant="ghost" className="mt-2 text-purple-600" onClick={addFormula}>+ Add Derived Column</Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                }







                {/* AutoType Node Props */}
                {
                    (selectedNode.type === 'autoType' || selectedNode.type === 'AUTO_TYPE') && (
                        <AutoTypeConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} />
                    )
                }

                {/* Union Node Props */}
                {
                    (selectedNode.type === 'union' || selectedNode.type === 'UNION') && (
                        <UnionConfig data={selectedNode.data} onChange={updateData} />
                    )
                }

                {/* Deduplicate Node Props */}
                {
                    (selectedNode.type === 'deduplicate' || selectedNode.type === 'DEDUPLICATE') && (
                        <DeduplicateConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} />
                    )
                }

                {/* Date Node Props */}
                {
                    selectedNode.type === 'date' && (
                        <DateConfig
                            node={selectedNode}
                            onChangeData={updateData}
                            inputColumns={inputColumns}
                        />
                    )
                }

                {/* Container Node Props */}
                {
                    selectedNode.type === 'container' && (
                        <div className="space-y-6">
                            <div className="p-3 bg-gray-50 text-gray-700 text-[10px] rounded border border-gray-100 flex gap-2">
                                <Box className="h-4 w-4 flex-shrink-0" />
                                <div>
                                    A generic container to group nodes visually. Nodes inside move with it.
                                </div>
                            </div>

                            <div className="space-y-3">
                                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Dimensions</label>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="text-[10px] text-gray-500">Width (px)</label>
                                        <input
                                            type="number"
                                            className="w-full text-xs p-2 border rounded mt-1"
                                            value={parseInt(selectedNode.style?.width as any) || 300}
                                            onChange={(e) => updateStyle('width', parseInt(e.target.value))}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[10px] text-gray-500">Height (px)</label>
                                        <input
                                            type="number"
                                            className="w-full text-xs p-2 border rounded mt-1"
                                            value={parseInt(selectedNode.style?.height as any) || 300}
                                            onChange={(e) => updateStyle('height', parseInt(e.target.value))}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Appearance</label>
                                <div>
                                    <label className="text-[10px] text-gray-500">Background Color</label>
                                    <div className="flex gap-2 mt-1">
                                        {['#f8fafc', '#f0f9ff', '#f0fdf4', '#fef2f2', '#fff7ed'].map(color => (
                                            <button
                                                key={color}
                                                className={`w-6 h-6 rounded border ${selectedNode.data.backgroundColor === color ? 'ring-2 ring-purple-500' : 'border-gray-200'}`}
                                                style={{ backgroundColor: color }}
                                                onClick={() => updateData('backgroundColor', color)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                }

                {/* Window Node Props */}
                {
                    (selectedNode.type === 'window' || selectedNode.type === 'WINDOW') && (
                        <WindowNodeConfig
                            data={selectedNode.data}
                            onChange={updateData}
                            inputColumns={inputColumns}
                        />
                    )
                }

                {/* Pareto Node Props */}
                {
                    (selectedNode.type === 'pareto' || selectedNode.type === 'PARETO') && (
                        <ParetoConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} />
                    )
                }

                {/* Sort Node Props */}
                {
                    (selectedNode.type === 'sort' || selectedNode.type === 'SORT') && (
                        <SortNodeConfig
                            data={selectedNode.data}
                            onChange={updateData}
                            inputColumns={inputColumns}
                        />
                    )
                }

                {/* Split Node Props */}
                {
                    (selectedNode.type === 'split' || selectedNode.type === 'SPLIT' || selectedNode.type === 'conditional_split' || selectedNode.type === 'CONDITIONAL_SPLIT' || selectedNode.type === 'COND_SPLIT' || selectedNode.type === 'conditionalSplit') && (
                        <ConditionalSplitConfig
                            data={selectedNode.data}
                            onChange={updateData}
                            inputColumns={inputColumns}
                            nodeId={selectedNode.id}
                            onRemoveConditionEdges={(conditionId: string) => {
                                setEdges(eds => eds.filter(
                                    e => !(e.source === selectedNode.id && e.sourceHandle === `condition-${conditionId}`)
                                ));
                            }}
                        />
                    )
                }

                {/* Geo Node Props */}
                {
                    selectedNode.type === 'geo' && (
                        <GeoConfig
                            node={selectedNode}
                            onChangeData={updateData}
                            inputColumns={inputColumns}
                        />
                    )
                }

                {/* Currency Node Props */}
                {
                    selectedNode.type === 'currency' && (
                        <CurrencyConfig
                            node={selectedNode}
                            onChangeData={updateData}
                            inputColumns={inputColumns}
                            getNodes={getNodes}
                            getEdges={getEdges}
                        />
                    )
                }

                {
                    (selectedNode.type === 'universalApi' || selectedNode.type === 'API_CALL' || selectedNode.type === 'UNIVERSAL_API' || selectedNode.type === 'APICALL') && (
                        <ApiRequestPanel
                            data={selectedNode.data || {}
                            }
                            onChange={(newData: any) => {
                                // Bulk update keys from ApiRequestPanel
                                // We need to trigger updateData for each key OR implement bulk update.
                                // Since updateData handles one key, let's iterate.
                                Object.entries(newData).forEach(([k, v]) => updateData(k, v));
                            }}
                        />
                    )
                }



                {/* Aggregate Node Props */}
                {
                    (selectedNode.type === 'aggregate' || selectedNode.type === 'AGGREGATE') && (
                        <AggregateNodeConfig
                            data={selectedNode.data}
                            onChange={updateData}
                            inputColumns={inputColumns}
                        />
                    )
                }

                {/* Execute SQL Node Props */}
                {
                    (selectedNode.type === 'executeSql' || selectedNode.type === 'EXECUTE_SQL') && (
                        <ExecuteSqlConfig selectedNode={selectedNode} updateData={updateData} flowVariables={flowVariables} />
                    )
                }

                {/* Join Configuration */}
                {
                    (selectedNode.type === 'join' || selectedNode.type === 'JOIN') && (
                        <div className="space-y-4">
                            <div className="p-4 bg-purple-50 border border-purple-100 rounded-xl text-center space-y-3">
                                <div className="mx-auto w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                    <GitMerge className="h-6 w-6 text-purple-600" />
                                </div>
                                <div>
                                    <h4 className="font-semibold text-purple-900">Configure Join</h4>
                                    <p className="text-xs text-purple-600/80 mt-1">
                                        {selectedNode.data.conditions?.length
                                            ? (selectedNode.data.conditions.length === 1
                                                ? `1 condition: ${selectedNode.data.conditions[0].leftCol} = ${selectedNode.data.conditions[0].rightCol}`
                                                : selectedNode.data.conditions.length <= 3
                                                    ? `${selectedNode.data.conditions.length} conditions: ${selectedNode.data.conditions.map((c: any) => `${c.leftCol}=${c.rightCol}`).join(', ')}`
                                                    : `${selectedNode.data.conditions.length} conditions defined`
                                            )
                                            : 'No conditions defined'
                                        }
                                    </p>
                                    <div className="mt-2 text-[10px] text-gray-500 font-mono bg-white inline-block px-2 py-1 rounded border">
                                        {(selectedNode.data.conditions?.length || 0) === 0 ? 'CROSS' : (selectedNode.data.joinType || 'INNER')} JOIN
                                    </div>
                                </div>
                                <Button
                                    className="w-full bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-200"
                                    onClick={() => setIsJoinConfigOpen(true)}
                                >
                                    <Settings2 className="h-4 w-4 mr-2" />
                                    Open Configuration
                                </Button>
                            </div>
                            <div className="text-[10px] text-gray-400 text-center">
                                Use the advanced editor to map columns and set fuzzy matching rules.
                            </div>
                        </div>
                    )
                }

                {/* â”€â”€ Save as ETL Table Node â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
                {selectedNode.type === 'DATA_MODEL_OUTPUT' && (
                    <ETLModelOutputConfig data={selectedNode.data} updateData={updateData} />
                )}


                {/* Train Model Node Props */}
                {(selectedNode.type === 'trainModel' || selectedNode.type === 'mlTrain' || selectedNode.type === 'ML_TRAIN') && (
                    <>
                        <TrainModelConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} />
                        {onShowLeaderboard && selectedNode.data?.executionStatus === 'Success' && (
                            <div className="pt-2 border-t border-gray-100">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-8 gap-2 text-indigo-600 border-indigo-200 hover:bg-indigo-50 w-full"
                                    onClick={() => onShowLeaderboard(selectedNode.id)}
                                >
                                    <Trophy className="h-4 w-4" /> View Leaderboard
                                </Button>
                            </div>
                        )}
                    </>
                )}

                {/* Score / Predict - handled above via ScorePredictConfig */}



                {/* Data Model Node Props */}
                {
                    (selectedNode.type === 'dataModel' || selectedNode.type === 'DATA_MODEL') && (
                        <div className="space-y-6">
                            <h3 className="font-semibold text-gray-900 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                                Data Model Configuration
                            </h3>
                            <DataModelConfig
                                node={selectedNode}
                                onChangeData={updateData}
                                onEditModel={handleEditModel}
                            />
                        </div>
                    )
                }


                {/* Email Action Node Props */}
                {
                    (selectedNode.type === 'emailAction' || selectedNode.type === 'EMAIL_ACTION' || selectedNode.type === 'EMAILACTION') && (
                        <div className="space-y-6">
                            <h3 className="font-semibold text-gray-900 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                                Email Configuration
                            </h3>
                            <EmailActionConfig
                                data={selectedNode.data}
                                onChange={updateData}
                                inputColumns={inputColumns}
                                incomingNodes={edges.filter((e: any) => e.target === selectedNode.id).map((e: any) => {
                                    const src: any = nodes.find(n => n.id === e.source);
                                    return { id: e.source, label: src?.data?.label || src?.type || e.source };
                                })}
                            />
                        </div>
                    )
                }

                {/* Approval Node Props - Auto/Conditional/Human-in-Loop */}
                {(selectedNode.type === 'approval' || selectedNode.type === 'APPROVAL') && (
                    <ApprovalConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} />
                )}

                {/* Remote File Node Props */}
                {
                    (selectedNode.type === 'remoteFile' || selectedNode.type === 'REMOTE_FILE') && (
                        <div className="space-y-6">
                            <RemoteStorageConfig
                                node={selectedNode}
                                onChangeData={updateData}
                            />
                        </div>
                    )
                }

                {/* HubSpot CRM Node */}
                {
                    (selectedNode.type === 'hubspot' || selectedNode.type === 'HUBSPOT') && (
                        <div className="space-y-6">
                            <HubSpotNodeConfig
                                node={selectedNode}
                                onChangeData={updateData}
                            />
                        </div>
                    )
                }

                {/* Legacy Flow Nodes Ported */}
                {(selectedNode.type === 'flowRunner' || selectedNode.type === 'FLOW_RUNNER' || selectedNode.type === 'FLOWRUNNER') && <FlowRunnerConfig data={selectedNode.data} onChange={updateData} />}
                {(selectedNode.type === 'barrier' || selectedNode.type === 'BARRIER') && <BarrierConfig data={selectedNode.data} onChange={updateData} />}
                {(selectedNode.type === 'sqlDml' || selectedNode.type === 'SQL_DML') && <SqlDmlConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} rawConnections={rawConnections} datasetTables={dataSources} />}
                {(selectedNode.type === 'user_action' || selectedNode.type === 'USER_ACTION' || selectedNode.type === 'USERACTION') && <UserActionConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} rawConnections={rawConnections} datasetTables={dataSources} />}
                {(selectedNode.type === 'aiAgent' || selectedNode.type === 'AI_AGENT') && <AIAgentConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} />}
                {(selectedNode.type === 'aiTransform' || selectedNode.type === 'AI_TRANSFORM') && <SmartTransformConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} />}
                {(selectedNode.type === 'llmNode' || selectedNode.type === 'LLM_NODE') && <LLMPromptConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} />}
                {(selectedNode.type === 'evaluateModel' || selectedNode.type === 'ML_EVALUATE') && <EvaluateModelConfigNode data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} />}
                {(selectedNode.type === 'trainedModel' || selectedNode.type === 'ML_TRAINED_MODEL') && <TrainedModelConfig data={selectedNode.data} onChange={updateData} />}
                {(selectedNode.type === 'scoreModel' || selectedNode.type === 'ML_SCORE') && <ScorePredictConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} mode="score" />}
                {(selectedNode.type === 'mlPredict' || selectedNode.type === 'predict' || selectedNode.type === 'ML_PREDICT') && <ScorePredictConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} mode="predict" />}


                {/* New Ported Nodes */}
                {(selectedNode.type === 'schedule' || selectedNode.type === 'SCHEDULE') && <ScheduleNodeConfig data={selectedNode.data} onUpdate={(k: string, v: any) => updateData(k, v)} />}
                {(selectedNode.type === 'webhook' || selectedNode.type === 'WEBHOOK_TRIGGER' || selectedNode.type === 'WEBHOOK') && <WebhookPanel data={selectedNode.data} onChange={updateData} flowId={flowId || 'new'} />}
                {(selectedNode.type === 'teams' || selectedNode.type === 'TEAMS_NOTIFY' || selectedNode.type === 'TEAMS') && <TeamsConfig data={selectedNode.data} onChange={updateData} />}
                {(selectedNode.type === 'generateDocument' || selectedNode.type === 'GENERATE_DOCUMENT' || selectedNode.type === 'GENERATEDOCUMENT') && <GenerateDocumentConfig data={selectedNode.data} onChange={updateData} />}
                {(selectedNode.type === 'forEach' || selectedNode.type === 'FOR_EACH' || selectedNode.type === 'FOREACH') && <ForEachConfig data={selectedNode.data} onChange={updateData} />}
                {(selectedNode.type === 'pivot' || selectedNode.type === 'PIVOT') && <PivotNodeConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} flowId={flowId || 'new'} nodeId={selectedNode.id} />}
                {(selectedNode.type === 'unpivot' || selectedNode.type === 'UNPIVOT') && <UnpivotNodeConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} />}
                {(selectedNode.type === 'column' || selectedNode.type === 'COLUMN') && <ColumnNodeConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} />}
                {(selectedNode.type === 'multiJoin' || selectedNode.type === 'MULTI_JOIN' || selectedNode.type === 'MULTIJOIN') && <MultiJoinNodeConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} upstreamNodes={getMultiJoinInputs()} />}

                {/* Workflow Automation Configs */}
                {(selectedNode.type === 'fileListener' || selectedNode.type === 'FILE_LISTENER') && <FileListenerConfig data={selectedNode.data} onChange={updateData} />}
                {(selectedNode.type === 'documentParser' || selectedNode.type === 'DOCUMENT_PARSER') && <DocumentParserConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} />}
                {(selectedNode.type === 'ediSource' || selectedNode.type === 'EDI_PARSE') && <EdiParserConfig data={selectedNode.data} onChange={updateData} />}
                {(selectedNode.type === 'conditionalRouter' || selectedNode.type === 'CONDITIONAL_ROUTER') && <ConditionalRouterConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} />}
                {(selectedNode.type?.toLowerCase() === 'datamapper' || selectedNode.type?.toLowerCase() === 'data_mapper') && <DataMapperConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} />}
                {(selectedNode.type === 'documentGenerator' || selectedNode.type === 'DOCUMENT_GENERATOR') && <DocumentGeneratorConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} />}
                {(selectedNode.type === 'duplicateDetector' || selectedNode.type === 'DUPLICATE_DETECTOR') && <DuplicateDetectorConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} flowId={flowId} nodeId={selectedNode?.id} />}
                {(selectedNode.type === 'pythonScript' || selectedNode.type === 'PYTHON_SCRIPT') && <PythonScriptConfig data={selectedNode.data} onChange={updateData} flowId={flowId || 'new'} />}
                {(selectedNode.type === 'loop' || selectedNode.type === 'LOOP') && <LoopConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} />}
                {(selectedNode.type === 'forEach' || selectedNode.type === 'FOR_EACH' || selectedNode.type === 'FOREACH' || selectedNode.type === 'for_each') && <ForEachConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} />}
                {(selectedNode.type === 'contentMerger' || selectedNode.type === 'CONTENT_MERGER') && <ContentMergerConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} />}
                {(selectedNode.type === 'aiSummarizer' || selectedNode.type === 'AI_SUMMARIZER') && <AISummarizerConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} />}
                {(selectedNode.type === 'databaseLookup' || selectedNode.type === 'DATABASE_LOOKUP') && <DatabaseLookupConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} />}
                {(selectedNode.type === 'templateRenderer' || selectedNode.type === 'TEMPLATE_RENDERER') && <TemplateRendererConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} />}
                {(selectedNode.type === 'fileOutput' || selectedNode.type === 'FILE_OUTPUT') && <FileOutputConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} />}
                {(selectedNode.type === 'setVariable' || selectedNode.type === 'SET_VARIABLE') && <SetVariableConfig data={selectedNode.data} onChange={updateData} inputColumns={inputColumns} flowVariables={flowVariables} />}
                {(selectedNode.type === 'getVariable' || selectedNode.type === 'GET_VARIABLE') && <GetVariableConfig data={selectedNode.data} onChange={updateData} flowVariables={flowVariables} />}
                {(selectedNode.type === 'USER_INPUT' || selectedNode.type === 'user_input' || selectedNode.type === 'userInput') && (
                    <UserInputNodePanel data={selectedNode.data} onChange={updateData} />
                )}

                {/* Data Profile Node Props */}
                {
                    (selectedNode.type === 'dataProfile' || selectedNode.type === 'DATA_PROFILE') && (
                        <div className="space-y-6">
                            <div className="p-3 bg-blue-50 text-blue-700 text-[10px] rounded border border-blue-100 flex gap-2">
                                <BarChart className="h-4 w-4 flex-shrink-0" />
                                <div>
                                    Analyzes dataset quality, statistics, and distributions. Execution is required to view results.
                                </div>
                            </div>

                            <div className="space-y-3">
                                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Analysis</label>
                                <Button
                                    className="w-full h-8 text-xs bg-blue-600 hover:bg-blue-700 gap-2"
                                    onClick={handleViewProfile}
                                    disabled={profileLoading}
                                >
                                    {profileLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />}
                                    {profileLoading ? 'Loading...' : 'View Data Profile'}
                                </Button>
                                <p className="text-[9px] text-gray-400 text-center">
                                    Click to inspect statistics, nulls, and histograms.
                                </p>
                            </div>
                        </div>
                    )
                }

                {/* Fetch Document Node Props (Verified) */}
                {
                    (selectedNode.type === 'fetchDocument' || selectedNode.type === 'FETCH_DOCUMENT') && (
                        <FetchDocumentConfig data={selectedNode.data} onChange={updateData} />
                    )
                }

                {/* Signal Start Props */}
                {
                    (selectedNode.type === 'signalStart' ||
                        selectedNode.type === 'SIGNAL_START' ||
                        selectedNode.type === 'SIGNALSTART') && (
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <Label>Signal Type</Label>
                                <Input
                                    value={selectedNode.data.signalType || ''}
                                    onChange={(e) => updateData('signalType', e.target.value)}
                                    placeholder="e.g. ticket_created"
                                />
                                <p className="text-[10px] text-gray-400">
                                    Set to <strong>ticket_created</strong> to trigger this flow when a new support ticket is raised.
                                    The ticket fields (<code>ticket_id</code>, <code>priority</code>, <code>category</code>, <code>subject</code>, <code>tenant_id</code>) become columns available to all downstream nodes.
                                </p>
                            </div>
                            <div className="p-3 bg-yellow-50 text-yellow-700 text-[10px] rounded border border-yellow-100 flex gap-2">
                                <Zap className="h-4 w-4 flex-shrink-0" />
                                <div>
                                    After saving, set <strong>workflow_flow_id</strong> on the tenant's support config to point to this flow ID so new tickets trigger it automatically.
                                </div>
                            </div>
                        </div>
                    )
                }

                {/* Schema-Driven Auto-Form (Phase 5) */}
                {
                    flowSchema && flowSchema[selectedNode.type] &&
                    !['date', 'DATE', 'container', 'CONTAINER', 'window', 'WINDOW', 'pareto', 'PARETO', 'geo', 'GEO',
                        'currency', 'CURRENCY', 'universalApi', 'API_CALL', 'UNIVERSAL_API',
                        'aggregate', 'AGGREGATE', 'executeSql', 'EXECUTE_SQL', 'conditionalSplit', 'CONDITIONAL_SPLIT',
                        'pivot', 'PIVOT', 'unpivot', 'UNPIVOT',
                        'trainModel', 'ML_TRAIN', 'trainedModel', 'ML_TRAINED_MODEL', 'scoreModel', 'ML_SCORE',
                        'destination', 'SINK', 'output', 'OUTPUT', 'emailAction', 'EMAIL_ACTION', 'approval', 'APPROVAL',
                        'remoteFile', 'REMOTE_FILE', 'dataProfile', 'DATA_PROFILE',
                        'fetchDocument', 'FETCH_DOCUMENT', 'signalStart', 'SIGNAL_START',
                        'dataModel', 'DATA_MODEL', 'join', 'JOIN', 'union', 'UNION',
                        'flowRunner', 'FLOW_RUNNER', 'barrier', 'BARRIER', 'sqlDml', 'SQL_DML',
                        'user_action', 'USER_ACTION', 'aiAgent', 'AI_AGENT',
                        'schedule', 'SCHEDULE', 'webhook', 'WEBHOOK_TRIGGER', 'teams', 'TEAMS_NOTIFY',
                        'generateDocument', 'GENERATE_DOCUMENT', 'forEach', 'FOR_EACH',
                        'aiTransform', 'AI_TRANSFORM',
                        'evaluateModel', 'ML_EVALUATE',
                        'scoreModel', 'ML_SCORE',
                        'ediSource', 'EDI_PARSE',
                        'column', 'COLUMN',
                        'multiJoin', 'MULTI_JOIN',
                        'fileListener', 'FILE_LISTENER', 'documentParser', 'DOCUMENT_PARSER',
                        'conditionalRouter', 'CONDITIONAL_ROUTER', 'dataMapper', 'DATA_MAPPER',
                        'documentGenerator', 'DOCUMENT_GENERATOR', 'duplicateDetector', 'DUPLICATE_DETECTOR',
                        'pythonScript', 'PYTHON_SCRIPT', 'loop', 'LOOP', 'contentMerger', 'CONTENT_MERGER',
                        'aiSummarizer', 'AI_SUMMARIZER', 'databaseLookup', 'DATABASE_LOOKUP',
                        'templateRenderer', 'TEMPLATE_RENDERER', 'fileOutput', 'FILE_OUTPUT',
                        'setVariable', 'SET_VARIABLE', 'getVariable', 'GET_VARIABLE',
                        // HubSpot has its own config panel
                        'hubspot', 'HUBSPOT',
                    ].includes(selectedNode.type) && (

                        <div className="space-y-6 animate-in fade-in">
                            <div className="p-2 bg-purple-50 border border-purple-100 rounded text-[10px] text-purple-700 mb-2 flex items-center gap-2">
                                <Sparkles className="h-3 w-3" />
                                <span>Auto-Generated Config ({selectedNode.type})</span>
                            </div>
                            <SchemaForm
                                schema={flowSchema[selectedNode.type].schema}
                                definitions={flowSchema[selectedNode.type].schema.definitions || flowSchema[selectedNode.type].schema.$defs}
                                data={selectedNode.data || {}}
                                onChange={(newData: any) => {
                                    // Bulk update is safer
                                    Object.entries(newData).forEach(([k, v]) => updateData(k, v));
                                }}
                            />
                        </div>
                    )
                }

                {/* Render Profile Report Modal */}
                {
                    showProfile && profileData && (
                        <DataProfileReport
                            open={showProfile}
                            onOpenChange={setShowProfile}
                            data={profileData}
                        />
                    )
                }

            </div >
            <DataPreviewModal
                isOpen={previewModal.isOpen}
                onClose={() => setPreviewModal({ ...previewModal, isOpen: false })}
                dataSourceId={previewModal.dataSourceId}
                objectName={previewModal.objectName}
            />
            <JoinConfigModal
                isOpen={isJoinConfigOpen}
                onClose={() => setIsJoinConfigOpen(false)}
                nodeData={selectedNode?.data || {}}
                inputColumns={inputColumns}
                onUpdate={(k: string, v: any) => updateData(k, v)}
                leftSourceId={edges.find((e: any) => e.target === selectedNode?.id && e.targetHandle === 'left')?.source || edges.filter((e: any) => e.target === selectedNode?.id)[0]?.source}
                rightSourceId={edges.find((e: any) => e.target === selectedNode?.id && e.targetHandle === 'right')?.source || edges.filter((e: any) => e.target === selectedNode?.id)[1]?.source}
                getNodes={getNodes}
            />
            <MappingReviewModal
                isOpen={isReviewOpen}
                onClose={() => setIsReviewOpen(false)}
                mappings={selectedNode?.data?.mappings || []}
                onUpdate={(newMappings) => {
                    if (selectedNode) {
                        setNodes((nds: any[]) => nds.map((n: any) => n.id === selectedNode.id ? { ...n, data: { ...n.data, mappings: newMappings } } : n));
                        // Update local state is tricky because selectedNode is a snapshot, but React Flow updates are declarative.
                        // We need to ensure next render picks it up.
                        const updatedNode = { ...selectedNode, data: { ...selectedNode.data, mappings: newMappings } };
                        setSelectedNode(updatedNode);
                    }
                }}
            />
            <DataModelEditorModal
                open={showDataModelEditor}
                onClose={() => setShowDataModelEditor(false)}
                modelId={editingModelId}
                onSaveSuccess={handleModelSaveSuccess}
            />
        </div>
    );
}

export default PropertiesPanel;
